// src/fetcher.ts
var ApiError = class extends Error {
  constructor(status, message, body = void 0) {
    super(message);
    this.status = status;
    this.body = body;
  }
  status;
  body;
};
var LONG_RUNNING_URL = /\/(?:pdf|csv|export)(?:$|[/?])/i;
var DEFAULT_TIMEOUT_MS = 15e3;
var LONG_RUNNING_TIMEOUT_MS = 6e4;
function resolveTimeoutMs(url, override) {
  if (typeof override === "number" && override > 0) return override;
  return LONG_RUNNING_URL.test(url) ? LONG_RUNNING_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
}
function combineSignals(caller, timeout) {
  if (!caller) return timeout;
  const anyFn = AbortSignal.any;
  if (typeof anyFn === "function") return anyFn([caller, timeout]);
  const controller = new AbortController();
  if (caller.aborted || timeout.aborted) {
    controller.abort();
  } else {
    const onAbort = () => controller.abort();
    caller.addEventListener("abort", onAbort, { once: true });
    timeout.addEventListener("abort", onAbort, { once: true });
  }
  return controller.signal;
}
async function customFetch(url, options = {}) {
  const { timeoutMs, signal: callerSignal, ...init } = options;
  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), resolveTimeoutMs(url, timeoutMs));
  let res;
  try {
    res = await fetch(url, {
      credentials: "same-origin",
      ...init,
      signal: combineSignals(callerSignal, timeoutController.signal),
      headers: {
        ...init.body ? { "Content-Type": "application/json" } : {},
        ...init.headers
      }
    });
  } catch (err) {
    if (timeoutController.signal.aborted) {
      throw new ApiError(408, "Zeit\xFCberschreitung \u2014 bitte erneut versuchen.");
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => void 0);
    const message = typeof body === "object" && body !== null && "message" in body ? String(body.message) : `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  if (res.status === 204) return void 0;
  return await res.json();
}

// src/labels.ts
var DOC_TYPE_LABELS = {
  QUOTE: "Angebot",
  ORDER_CONF: "Auftragsbest\xE4tigung",
  DELIVERY_NOTE: "Lieferschein",
  INVOICE: "Rechnung",
  CREDIT_NOTE: "Gutschrift",
  WORK_REPORT: "Arbeitsnachweis"
};
function invoiceTitle(kind, mitAbzug) {
  if (kind === "FINAL") return mitAbzug ? "Schlussrechnung" : "Rechnung";
  return INVOICE_KIND_LABELS[kind ?? "STANDARD"] ?? "Rechnung";
}
var INVOICE_KIND_LABELS = {
  STANDARD: "Rechnung",
  PARTIAL: "Abschlagsrechnung",
  // Kundentest 2026-08: echte Teilleistung aus ausgewählten Auftragspositionen —
  // abzugrenzen vom Abschlag (Vorauszahlung ohne Leistungsbezug).
  INTERIM: "Teilrechnung",
  FINAL: "Schlussrechnung"
};
var DUNNING_LEVEL_LABELS = {
  REMINDER: "Zahlungserinnerung",
  LEVEL1: "1. Mahnung",
  LEVEL2: "2. Mahnung"
};
var TAX_CODE_LABELS = {
  DE_19: "19 %",
  DE_7: "7 %",
  RC_13B: "0 % (\xA7 13b)"
};
var ITEM_KIND_LABELS = {
  LABOR: "Lohn",
  MATERIAL: "Material",
  TRAVEL: "Anfahrt",
  EQUIPMENT: "Ger\xE4t",
  OTHER: "Sonstiges"
};
var HOLD_REASON_LABELS = {
  MATERIAL_FEHLT: "Material fehlt",
  MIETER_NICHT_ERREICHBAR: "Mieter nicht erreichbar",
  SONSTIGES: "Sonstiges"
};

// src/leerwohnung.ts
var LEERWOHNUNG_FELDER = {
  eingangstuer: ["pzLinks", "pzRechts", "spion", "rosette", "wechselgarnitur", "dichtung"],
  zimmertueren: ["bbLinks", "bbRechts", "bbGarnituren", "dichtungen", "lueftungsgitter"],
  fenster: [
    "fensterfluegel",
    "balkontuer",
    "dichtung",
    "fenstergriff",
    "fenstergriffAbschliessbar"
  ],
  rollladen: [
    "gurt",
    "aufschraubMini",
    "aufschraubMaxi",
    "einlasswickler105",
    "einlasswickler135",
    "einlasswickler165",
    "einlasswickler185",
    "blende105",
    "blende135",
    "blende165",
    "blende185"
  ]
};
var LEERWOHNUNG_GRENZEN = {
  /** Stückzahl je Element (0 = nichts erfasst). */
  maxStueck: 999,
  /** Freitext je Block. */
  maxSonstiges: 500,
  /** Freitext über die ganze Aufnahme. */
  maxBemerkung: 2e3,
  /** Raum-/Türbezeichnung einer neuen Tür. */
  maxBezeichnung: 120,
  /** Türmaße in Millimetern (ganzzahlig). */
  maxMassMm: 9999,
  /** Neue Türen je Aufnahme. */
  maxNeueTueren: 50
};

// src/placeholders.ts
var PLACEHOLDER_CATALOG = [
  "firma.name",
  "firma.inhaber",
  "firma.telefon",
  "firma.mobil",
  "firma.email",
  "firma.iban",
  "kunde.name",
  "kunde.nummer",
  "kunde.ansprechpartner",
  "kunde.anrede",
  "beleg.art",
  "beleg.nummer",
  "beleg.datum",
  "beleg.betreff",
  "beleg.netto",
  "beleg.ust",
  "beleg.brutto",
  "beleg.zahlbetrag",
  "zahlung.ziel_tage",
  "zahlung.faellig_am",
  "zahlung.skonto_prozent",
  "zahlung.skonto_bis",
  "auftrag.nummer",
  "auftrag.titel",
  "auftrag.projektnummer",
  // Faruq 2026-09-05: Mieterdirekt-Block als editierbarer Kopftext — Objektzeile
  // „Straße / Stockwerk / Lage" und Mietvertragsnummer der Einheit.
  "auftrag.objekt",
  "auftrag.mietvertrag",
  "leistung.von",
  "leistung.bis",
  "mahnung.stufe",
  "mahnung.gebuehr",
  "mahnung.offen",
  "benutzer.name",
  "benutzer.email"
];
var PLACEHOLDER_GROUPS = PLACEHOLDER_CATALOG.reduce((groups, key) => {
  const [group, field] = key.split(".");
  (groups[group] ??= []).push(field);
  return groups;
}, {});
var CATALOG_SET = new Set(PLACEHOLDER_CATALOG);
var PLACEHOLDER_RE = /\{([a-zA-Z]+)\.([a-zA-Z_]+)\}/g;
function resolvePlaceholders(template, context) {
  const unknown = [];
  const seen = /* @__PURE__ */ new Set();
  const resolveLine = (line) => {
    let bekannte = 0;
    let mitWert = 0;
    const text = line.replace(PLACEHOLDER_RE, (full, group, field) => {
      const key = `${group}.${field}`;
      if (!CATALOG_SET.has(key)) {
        if (!seen.has(key)) {
          seen.add(key);
          unknown.push(key);
        }
        return full;
      }
      bekannte++;
      const value = context[group]?.[field];
      if (value == null || String(value) === "") return "";
      mitWert++;
      return String(value);
    });
    return { text, leerAufgeloest: bekannte > 0 && mitWert === 0 };
  };
  const out = [];
  for (const rawLine of template.split("\n")) {
    const hadContent = rawLine.trim() !== "";
    const { text, leerAufgeloest } = resolveLine(rawLine);
    const resolved = text.replace(/\s+$/, "");
    if (hadContent && resolved.trim() === "") continue;
    if (hadContent && leerAufgeloest && /:$/.test(resolved)) continue;
    if (resolved.trim() === "" && (out.length === 0 || out[out.length - 1] === "")) continue;
    out.push(resolved.trim() === "" ? "" : resolved);
  }
  while (out.length > 0 && out[out.length - 1] === "") out.pop();
  return { text: out.join("\n"), unknown };
}
function resolvePlaceholdersSingleLine(template, context) {
  const { text, unknown } = resolvePlaceholders(template, context);
  return { text: text.replace(/\s*\n\s*/g, " ").trim(), unknown };
}

// src/client/endpoints.ts
import {
  useMutation,
  useQuery
} from "@tanstack/react-query";
var getHealthControllerCheckUrl = () => {
  return `/api/health`;
};
var healthControllerCheck = async (options) => {
  return customFetch(
    getHealthControllerCheckUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getHealthControllerCheckQueryKey = () => {
  return [
    `/api/health`
  ];
};
var getHealthControllerCheckQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getHealthControllerCheckQueryKey();
  const queryFn = ({ signal }) => healthControllerCheck({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useHealthControllerCheck(options, queryClient) {
  const queryOptions = getHealthControllerCheckQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAuthControllerLoginUrl = () => {
  return `/api/auth/login`;
};
var authControllerLogin = async (loginDto, options) => {
  return customFetch(
    getAuthControllerLoginUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        loginDto
      )
    }
  );
};
var getAuthControllerLoginMutationOptions = (options) => {
  const mutationKey = ["authControllerLogin"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return authControllerLogin(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAuthControllerLogin = (options, queryClient) => {
  const mutationOptions = getAuthControllerLoginMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAuthControllerLogoutUrl = () => {
  return `/api/auth/logout`;
};
var authControllerLogout = async (options) => {
  return customFetch(
    getAuthControllerLogoutUrl(),
    {
      ...options,
      method: "POST"
    }
  );
};
var getAuthControllerLogoutMutationOptions = (options) => {
  const mutationKey = ["authControllerLogout"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = () => {
    return authControllerLogout(requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAuthControllerLogout = (options, queryClient) => {
  const mutationOptions = getAuthControllerLogoutMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAuthControllerMeUrl = () => {
  return `/api/auth/me`;
};
var authControllerMe = async (options) => {
  return customFetch(
    getAuthControllerMeUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAuthControllerMeQueryKey = () => {
  return [
    `/api/auth/me`
  ];
};
var getAuthControllerMeQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAuthControllerMeQueryKey();
  const queryFn = ({ signal }) => authControllerMe({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAuthControllerMe(options, queryClient) {
  const queryOptions = getAuthControllerMeQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAuthControllerUpdateMeUrl = () => {
  return `/api/auth/me`;
};
var authControllerUpdateMe = async (updateProfileDto, options) => {
  return customFetch(
    getAuthControllerUpdateMeUrl(),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        updateProfileDto
      )
    }
  );
};
var getAuthControllerUpdateMeMutationOptions = (options) => {
  const mutationKey = ["authControllerUpdateMe"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return authControllerUpdateMe(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAuthControllerUpdateMe = (options, queryClient) => {
  const mutationOptions = getAuthControllerUpdateMeMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAuthControllerChangePasswordUrl = () => {
  return `/api/auth/change-password`;
};
var authControllerChangePassword = async (changePasswordDto, options) => {
  return customFetch(
    getAuthControllerChangePasswordUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        changePasswordDto
      )
    }
  );
};
var getAuthControllerChangePasswordMutationOptions = (options) => {
  const mutationKey = ["authControllerChangePassword"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return authControllerChangePassword(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAuthControllerChangePassword = (options, queryClient) => {
  const mutationOptions = getAuthControllerChangePasswordMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAuthControllerSetupTotpUrl = () => {
  return `/api/auth/totp/setup`;
};
var authControllerSetupTotp = async (totpSetupDto, options) => {
  return customFetch(
    getAuthControllerSetupTotpUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        totpSetupDto
      )
    }
  );
};
var getAuthControllerSetupTotpMutationOptions = (options) => {
  const mutationKey = ["authControllerSetupTotp"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return authControllerSetupTotp(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAuthControllerSetupTotp = (options, queryClient) => {
  const mutationOptions = getAuthControllerSetupTotpMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAuthControllerEnableTotpUrl = () => {
  return `/api/auth/totp/enable`;
};
var authControllerEnableTotp = async (totpEnableDto, options) => {
  return customFetch(
    getAuthControllerEnableTotpUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        totpEnableDto
      )
    }
  );
};
var getAuthControllerEnableTotpMutationOptions = (options) => {
  const mutationKey = ["authControllerEnableTotp"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return authControllerEnableTotp(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAuthControllerEnableTotp = (options, queryClient) => {
  const mutationOptions = getAuthControllerEnableTotpMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAuthControllerDisableTotpUrl = () => {
  return `/api/auth/totp/disable`;
};
var authControllerDisableTotp = async (totpDisableDto, options) => {
  return customFetch(
    getAuthControllerDisableTotpUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        totpDisableDto
      )
    }
  );
};
var getAuthControllerDisableTotpMutationOptions = (options) => {
  const mutationKey = ["authControllerDisableTotp"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return authControllerDisableTotp(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAuthControllerDisableTotp = (options, queryClient) => {
  const mutationOptions = getAuthControllerDisableTotpMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCustomersControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/customers?${stringifiedParams}` : `/api/customers`;
};
var customersControllerList = async (params, options) => {
  return customFetch(
    getCustomersControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getCustomersControllerListQueryKey = (params) => {
  return [
    `/api/customers`,
    ...params ? [params] : []
  ];
};
var getCustomersControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getCustomersControllerListQueryKey(params);
  const queryFn = ({ signal }) => customersControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useCustomersControllerList(params, options, queryClient) {
  const queryOptions = getCustomersControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getCustomersControllerCreateUrl = () => {
  return `/api/customers`;
};
var customersControllerCreate = async (customerCreateDto, options) => {
  return customFetch(
    getCustomersControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        customerCreateDto
      )
    }
  );
};
var getCustomersControllerCreateMutationOptions = (options) => {
  const mutationKey = ["customersControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return customersControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCustomersControllerCreate = (options, queryClient) => {
  const mutationOptions = getCustomersControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCustomersControllerCreateFromUnitUrl = (unitId) => {
  return `/api/customers/from-unit/${unitId}`;
};
var customersControllerCreateFromUnit = async (unitId, options) => {
  return customFetch(
    getCustomersControllerCreateFromUnitUrl(unitId),
    {
      ...options,
      method: "POST"
    }
  );
};
var getCustomersControllerCreateFromUnitMutationOptions = (options) => {
  const mutationKey = ["customersControllerCreateFromUnit"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { unitId } = props ?? {};
    return customersControllerCreateFromUnit(unitId, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCustomersControllerCreateFromUnit = (options, queryClient) => {
  const mutationOptions = getCustomersControllerCreateFromUnitMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCustomersControllerFileUrl = (id, params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/customers/${id}?${stringifiedParams}` : `/api/customers/${id}`;
};
var customersControllerFile = async (id, params, options) => {
  return customFetch(
    getCustomersControllerFileUrl(id, params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getCustomersControllerFileQueryKey = (id, params) => {
  return [
    `/api/customers/${id}`,
    ...params ? [params] : []
  ];
};
var getCustomersControllerFileQueryOptions = (id, params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getCustomersControllerFileQueryKey(id, params);
  const queryFn = ({ signal }) => customersControllerFile(id, params, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useCustomersControllerFile(id, params, options, queryClient) {
  const queryOptions = getCustomersControllerFileQueryOptions(id, params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getCustomersControllerUpdateUrl = (id) => {
  return `/api/customers/${id}`;
};
var customersControllerUpdate = async (id, customerUpdateDto, options) => {
  return customFetch(
    getCustomersControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        customerUpdateDto
      )
    }
  );
};
var getCustomersControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["customersControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return customersControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCustomersControllerUpdate = (options, queryClient) => {
  const mutationOptions = getCustomersControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMasterDataDeleteControllerCustomerUrl = (id) => {
  return `/api/customers/${id}`;
};
var masterDataDeleteControllerCustomer = async (id, options) => {
  return customFetch(
    getMasterDataDeleteControllerCustomerUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMasterDataDeleteControllerCustomerMutationOptions = (options) => {
  const mutationKey = ["masterDataDeleteControllerCustomer"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return masterDataDeleteControllerCustomer(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMasterDataDeleteControllerCustomer = (options, queryClient) => {
  const mutationOptions = getMasterDataDeleteControllerCustomerMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCustomersControllerUnitsUrl = (id, params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/customers/${id}/units?${stringifiedParams}` : `/api/customers/${id}/units`;
};
var customersControllerUnits = async (id, params, options) => {
  return customFetch(
    getCustomersControllerUnitsUrl(id, params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getCustomersControllerUnitsQueryKey = (id, params) => {
  return [
    `/api/customers/${id}/units`,
    ...params ? [params] : []
  ];
};
var getCustomersControllerUnitsQueryOptions = (id, params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getCustomersControllerUnitsQueryKey(id, params);
  const queryFn = ({ signal }) => customersControllerUnits(id, params, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useCustomersControllerUnits(id, params, options, queryClient) {
  const queryOptions = getCustomersControllerUnitsQueryOptions(id, params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getCustomersControllerCreatePropertyUrl = (id) => {
  return `/api/customers/${id}/properties`;
};
var customersControllerCreateProperty = async (id, propertyCreateDto, options) => {
  return customFetch(
    getCustomersControllerCreatePropertyUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        propertyCreateDto
      )
    }
  );
};
var getCustomersControllerCreatePropertyMutationOptions = (options) => {
  const mutationKey = ["customersControllerCreateProperty"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return customersControllerCreateProperty(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCustomersControllerCreateProperty = (options, queryClient) => {
  const mutationOptions = getCustomersControllerCreatePropertyMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCustomersControllerCreatePropertiesBulkUrl = (id) => {
  return `/api/customers/${id}/properties/bulk`;
};
var customersControllerCreatePropertiesBulk = async (id, propertiesBulkDto, options) => {
  return customFetch(
    getCustomersControllerCreatePropertiesBulkUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        propertiesBulkDto
      )
    }
  );
};
var getCustomersControllerCreatePropertiesBulkMutationOptions = (options) => {
  const mutationKey = ["customersControllerCreatePropertiesBulk"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return customersControllerCreatePropertiesBulk(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCustomersControllerCreatePropertiesBulk = (options, queryClient) => {
  const mutationOptions = getCustomersControllerCreatePropertiesBulkMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCustomersControllerUpdatePropertyUrl = (id) => {
  return `/api/properties/${id}`;
};
var customersControllerUpdateProperty = async (id, propertyUpdateDto, options) => {
  return customFetch(
    getCustomersControllerUpdatePropertyUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        propertyUpdateDto
      )
    }
  );
};
var getCustomersControllerUpdatePropertyMutationOptions = (options) => {
  const mutationKey = ["customersControllerUpdateProperty"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return customersControllerUpdateProperty(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCustomersControllerUpdateProperty = (options, queryClient) => {
  const mutationOptions = getCustomersControllerUpdatePropertyMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMasterDataDeleteControllerPropertyUrl = (id) => {
  return `/api/properties/${id}`;
};
var masterDataDeleteControllerProperty = async (id, options) => {
  return customFetch(
    getMasterDataDeleteControllerPropertyUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMasterDataDeleteControllerPropertyMutationOptions = (options) => {
  const mutationKey = ["masterDataDeleteControllerProperty"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return masterDataDeleteControllerProperty(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMasterDataDeleteControllerProperty = (options, queryClient) => {
  const mutationOptions = getMasterDataDeleteControllerPropertyMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCustomersControllerCreateUnitUrl = (id) => {
  return `/api/properties/${id}/units`;
};
var customersControllerCreateUnit = async (id, unitCreateDto, options) => {
  return customFetch(
    getCustomersControllerCreateUnitUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        unitCreateDto
      )
    }
  );
};
var getCustomersControllerCreateUnitMutationOptions = (options) => {
  const mutationKey = ["customersControllerCreateUnit"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return customersControllerCreateUnit(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCustomersControllerCreateUnit = (options, queryClient) => {
  const mutationOptions = getCustomersControllerCreateUnitMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCustomersControllerUpdateUnitUrl = (id) => {
  return `/api/units/${id}`;
};
var customersControllerUpdateUnit = async (id, unitUpdateDto, options) => {
  return customFetch(
    getCustomersControllerUpdateUnitUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        unitUpdateDto
      )
    }
  );
};
var getCustomersControllerUpdateUnitMutationOptions = (options) => {
  const mutationKey = ["customersControllerUpdateUnit"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return customersControllerUpdateUnit(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCustomersControllerUpdateUnit = (options, queryClient) => {
  const mutationOptions = getCustomersControllerUpdateUnitMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMasterDataDeleteControllerUnitUrl = (id) => {
  return `/api/units/${id}`;
};
var masterDataDeleteControllerUnit = async (id, options) => {
  return customFetch(
    getMasterDataDeleteControllerUnitUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMasterDataDeleteControllerUnitMutationOptions = (options) => {
  const mutationKey = ["masterDataDeleteControllerUnit"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return masterDataDeleteControllerUnit(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMasterDataDeleteControllerUnit = (options, queryClient) => {
  const mutationOptions = getMasterDataDeleteControllerUnitMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCustomersControllerCreateContactUrl = () => {
  return `/api/contacts`;
};
var customersControllerCreateContact = async (contactCreateDto, options) => {
  return customFetch(
    getCustomersControllerCreateContactUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        contactCreateDto
      )
    }
  );
};
var getCustomersControllerCreateContactMutationOptions = (options) => {
  const mutationKey = ["customersControllerCreateContact"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return customersControllerCreateContact(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCustomersControllerCreateContact = (options, queryClient) => {
  const mutationOptions = getCustomersControllerCreateContactMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCustomersControllerUpdateContactUrl = (id) => {
  return `/api/contacts/${id}`;
};
var customersControllerUpdateContact = async (id, contactUpdateDto, options) => {
  return customFetch(
    getCustomersControllerUpdateContactUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        contactUpdateDto
      )
    }
  );
};
var getCustomersControllerUpdateContactMutationOptions = (options) => {
  const mutationKey = ["customersControllerUpdateContact"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return customersControllerUpdateContact(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCustomersControllerUpdateContact = (options, queryClient) => {
  const mutationOptions = getCustomersControllerUpdateContactMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMasterDataDeleteControllerContactUrl = (id) => {
  return `/api/contacts/${id}`;
};
var masterDataDeleteControllerContact = async (id, options) => {
  return customFetch(
    getMasterDataDeleteControllerContactUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMasterDataDeleteControllerContactMutationOptions = (options) => {
  const mutationKey = ["masterDataDeleteControllerContact"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return masterDataDeleteControllerContact(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMasterDataDeleteControllerContact = (options, queryClient) => {
  const mutationOptions = getMasterDataDeleteControllerContactMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getArticlesControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/articles?${stringifiedParams}` : `/api/articles`;
};
var articlesControllerList = async (params, options) => {
  return customFetch(
    getArticlesControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getArticlesControllerListQueryKey = (params) => {
  return [
    `/api/articles`,
    ...params ? [params] : []
  ];
};
var getArticlesControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getArticlesControllerListQueryKey(params);
  const queryFn = ({ signal }) => articlesControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useArticlesControllerList(params, options, queryClient) {
  const queryOptions = getArticlesControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getArticlesControllerCreateUrl = () => {
  return `/api/articles`;
};
var articlesControllerCreate = async (articleCreateDto, options) => {
  return customFetch(
    getArticlesControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        articleCreateDto
      )
    }
  );
};
var getArticlesControllerCreateMutationOptions = (options) => {
  const mutationKey = ["articlesControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return articlesControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useArticlesControllerCreate = (options, queryClient) => {
  const mutationOptions = getArticlesControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getArticlesControllerUpdateUrl = (id) => {
  return `/api/articles/${id}`;
};
var articlesControllerUpdate = async (id, articleUpdateDto, options) => {
  return customFetch(
    getArticlesControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        articleUpdateDto
      )
    }
  );
};
var getArticlesControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["articlesControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return articlesControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useArticlesControllerUpdate = (options, queryClient) => {
  const mutationOptions = getArticlesControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMasterDataDeleteControllerArticleUrl = (id) => {
  return `/api/articles/${id}`;
};
var masterDataDeleteControllerArticle = async (id, options) => {
  return customFetch(
    getMasterDataDeleteControllerArticleUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMasterDataDeleteControllerArticleMutationOptions = (options) => {
  const mutationKey = ["masterDataDeleteControllerArticle"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return masterDataDeleteControllerArticle(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMasterDataDeleteControllerArticle = (options, queryClient) => {
  const mutationOptions = getMasterDataDeleteControllerArticleMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPriceListsControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/price-lists?${stringifiedParams}` : `/api/price-lists`;
};
var priceListsControllerList = async (params, options) => {
  return customFetch(
    getPriceListsControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getPriceListsControllerListQueryKey = (params) => {
  return [
    `/api/price-lists`,
    ...params ? [params] : []
  ];
};
var getPriceListsControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getPriceListsControllerListQueryKey(params);
  const queryFn = ({ signal }) => priceListsControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function usePriceListsControllerList(params, options, queryClient) {
  const queryOptions = getPriceListsControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getPriceListsControllerCreateUrl = () => {
  return `/api/price-lists`;
};
var priceListsControllerCreate = async (priceListCreateDto, options) => {
  return customFetch(
    getPriceListsControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        priceListCreateDto
      )
    }
  );
};
var getPriceListsControllerCreateMutationOptions = (options) => {
  const mutationKey = ["priceListsControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return priceListsControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePriceListsControllerCreate = (options, queryClient) => {
  const mutationOptions = getPriceListsControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPriceListsControllerDetailUrl = (id) => {
  return `/api/price-lists/${id}`;
};
var priceListsControllerDetail = async (id, options) => {
  return customFetch(
    getPriceListsControllerDetailUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getPriceListsControllerDetailQueryKey = (id) => {
  return [
    `/api/price-lists/${id}`
  ];
};
var getPriceListsControllerDetailQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getPriceListsControllerDetailQueryKey(id);
  const queryFn = ({ signal }) => priceListsControllerDetail(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function usePriceListsControllerDetail(id, options, queryClient) {
  const queryOptions = getPriceListsControllerDetailQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getPriceListsControllerUpdateUrl = (id) => {
  return `/api/price-lists/${id}`;
};
var priceListsControllerUpdate = async (id, priceListUpdateDto, options) => {
  return customFetch(
    getPriceListsControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        priceListUpdateDto
      )
    }
  );
};
var getPriceListsControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["priceListsControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return priceListsControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePriceListsControllerUpdate = (options, queryClient) => {
  const mutationOptions = getPriceListsControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMasterDataDeleteControllerPriceListUrl = (id) => {
  return `/api/price-lists/${id}`;
};
var masterDataDeleteControllerPriceList = async (id, options) => {
  return customFetch(
    getMasterDataDeleteControllerPriceListUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMasterDataDeleteControllerPriceListMutationOptions = (options) => {
  const mutationKey = ["masterDataDeleteControllerPriceList"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return masterDataDeleteControllerPriceList(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMasterDataDeleteControllerPriceList = (options, queryClient) => {
  const mutationOptions = getMasterDataDeleteControllerPriceListMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPriceListsControllerCreateItemUrl = (id) => {
  return `/api/price-lists/${id}/items`;
};
var priceListsControllerCreateItem = async (id, priceListItemCreateDto, options) => {
  return customFetch(
    getPriceListsControllerCreateItemUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        priceListItemCreateDto
      )
    }
  );
};
var getPriceListsControllerCreateItemMutationOptions = (options) => {
  const mutationKey = ["priceListsControllerCreateItem"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return priceListsControllerCreateItem(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePriceListsControllerCreateItem = (options, queryClient) => {
  const mutationOptions = getPriceListsControllerCreateItemMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPriceListsControllerUpdateItemUrl = (id) => {
  return `/api/price-list-items/${id}`;
};
var priceListsControllerUpdateItem = async (id, priceListItemUpdateDto, options) => {
  return customFetch(
    getPriceListsControllerUpdateItemUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        priceListItemUpdateDto
      )
    }
  );
};
var getPriceListsControllerUpdateItemMutationOptions = (options) => {
  const mutationKey = ["priceListsControllerUpdateItem"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return priceListsControllerUpdateItem(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePriceListsControllerUpdateItem = (options, queryClient) => {
  const mutationOptions = getPriceListsControllerUpdateItemMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPriceListsControllerDeleteItemUrl = (id) => {
  return `/api/price-list-items/${id}`;
};
var priceListsControllerDeleteItem = async (id, options) => {
  return customFetch(
    getPriceListsControllerDeleteItemUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getPriceListsControllerDeleteItemMutationOptions = (options) => {
  const mutationKey = ["priceListsControllerDeleteItem"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return priceListsControllerDeleteItem(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePriceListsControllerDeleteItem = (options, queryClient) => {
  const mutationOptions = getPriceListsControllerDeleteItemMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPriceListsControllerImportCsvUrl = (id) => {
  return `/api/price-lists/${id}/import`;
};
var priceListsControllerImportCsv = async (id, epaImportDto, options) => {
  return customFetch(
    getPriceListsControllerImportCsvUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        epaImportDto
      )
    }
  );
};
var getPriceListsControllerImportCsvMutationOptions = (options) => {
  const mutationKey = ["priceListsControllerImportCsv"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return priceListsControllerImportCsv(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePriceListsControllerImportCsv = (options, queryClient) => {
  const mutationOptions = getPriceListsControllerImportCsvMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMaintenanceContractsControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/maintenance-contracts?${stringifiedParams}` : `/api/maintenance-contracts`;
};
var maintenanceContractsControllerList = async (params, options) => {
  return customFetch(
    getMaintenanceContractsControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getMaintenanceContractsControllerListQueryKey = (params) => {
  return [
    `/api/maintenance-contracts`,
    ...params ? [params] : []
  ];
};
var getMaintenanceContractsControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getMaintenanceContractsControllerListQueryKey(params);
  const queryFn = ({ signal }) => maintenanceContractsControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useMaintenanceContractsControllerList(params, options, queryClient) {
  const queryOptions = getMaintenanceContractsControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMaintenanceContractsControllerCreateUrl = () => {
  return `/api/maintenance-contracts`;
};
var maintenanceContractsControllerCreate = async (maintenanceContractCreateDto, options) => {
  return customFetch(
    getMaintenanceContractsControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        maintenanceContractCreateDto
      )
    }
  );
};
var getMaintenanceContractsControllerCreateMutationOptions = (options) => {
  const mutationKey = ["maintenanceContractsControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return maintenanceContractsControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMaintenanceContractsControllerCreate = (options, queryClient) => {
  const mutationOptions = getMaintenanceContractsControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMaintenanceContractsControllerGetUrl = (id) => {
  return `/api/maintenance-contracts/${id}`;
};
var maintenanceContractsControllerGet = async (id, options) => {
  return customFetch(
    getMaintenanceContractsControllerGetUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getMaintenanceContractsControllerGetQueryKey = (id) => {
  return [
    `/api/maintenance-contracts/${id}`
  ];
};
var getMaintenanceContractsControllerGetQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getMaintenanceContractsControllerGetQueryKey(id);
  const queryFn = ({ signal }) => maintenanceContractsControllerGet(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useMaintenanceContractsControllerGet(id, options, queryClient) {
  const queryOptions = getMaintenanceContractsControllerGetQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMaintenanceContractsControllerUpdateUrl = (id) => {
  return `/api/maintenance-contracts/${id}`;
};
var maintenanceContractsControllerUpdate = async (id, maintenanceContractUpdateDto, options) => {
  return customFetch(
    getMaintenanceContractsControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        maintenanceContractUpdateDto
      )
    }
  );
};
var getMaintenanceContractsControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["maintenanceContractsControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return maintenanceContractsControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMaintenanceContractsControllerUpdate = (options, queryClient) => {
  const mutationOptions = getMaintenanceContractsControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getBusinessAreasControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/business-areas?${stringifiedParams}` : `/api/business-areas`;
};
var businessAreasControllerList = async (params, options) => {
  return customFetch(
    getBusinessAreasControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getBusinessAreasControllerListQueryKey = (params) => {
  return [
    `/api/business-areas`,
    ...params ? [params] : []
  ];
};
var getBusinessAreasControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getBusinessAreasControllerListQueryKey(params);
  const queryFn = ({ signal }) => businessAreasControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useBusinessAreasControllerList(params, options, queryClient) {
  const queryOptions = getBusinessAreasControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getBusinessAreasControllerCreateUrl = () => {
  return `/api/business-areas`;
};
var businessAreasControllerCreate = async (businessAreaCreateDto, options) => {
  return customFetch(
    getBusinessAreasControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        businessAreaCreateDto
      )
    }
  );
};
var getBusinessAreasControllerCreateMutationOptions = (options) => {
  const mutationKey = ["businessAreasControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return businessAreasControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useBusinessAreasControllerCreate = (options, queryClient) => {
  const mutationOptions = getBusinessAreasControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getBusinessAreasControllerUpdateUrl = (id) => {
  return `/api/business-areas/${id}`;
};
var businessAreasControllerUpdate = async (id, businessAreaUpdateDto, options) => {
  return customFetch(
    getBusinessAreasControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        businessAreaUpdateDto
      )
    }
  );
};
var getBusinessAreasControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["businessAreasControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return businessAreasControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useBusinessAreasControllerUpdate = (options, queryClient) => {
  const mutationOptions = getBusinessAreasControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMasterDataDeleteControllerBusinessAreaUrl = (id) => {
  return `/api/business-areas/${id}`;
};
var masterDataDeleteControllerBusinessArea = async (id, options) => {
  return customFetch(
    getMasterDataDeleteControllerBusinessAreaUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMasterDataDeleteControllerBusinessAreaMutationOptions = (options) => {
  const mutationKey = ["masterDataDeleteControllerBusinessArea"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return masterDataDeleteControllerBusinessArea(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMasterDataDeleteControllerBusinessArea = (options, queryClient) => {
  const mutationOptions = getMasterDataDeleteControllerBusinessAreaMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getSearchControllerRunUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/search?${stringifiedParams}` : `/api/search`;
};
var searchControllerRun = async (params, options) => {
  return customFetch(
    getSearchControllerRunUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getSearchControllerRunQueryKey = (params) => {
  return [
    `/api/search`,
    ...params ? [params] : []
  ];
};
var getSearchControllerRunQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getSearchControllerRunQueryKey(params);
  const queryFn = ({ signal }) => searchControllerRun(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useSearchControllerRun(params, options, queryClient) {
  const queryOptions = getSearchControllerRunQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/documents?${stringifiedParams}` : `/api/documents`;
};
var documentsControllerList = async (params, options) => {
  return customFetch(
    getDocumentsControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDocumentsControllerListQueryKey = (params) => {
  return [
    `/api/documents`,
    ...params ? [params] : []
  ];
};
var getDocumentsControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDocumentsControllerListQueryKey(params);
  const queryFn = ({ signal }) => documentsControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useDocumentsControllerList(params, options, queryClient) {
  const queryOptions = getDocumentsControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerCreateUrl = () => {
  return `/api/documents`;
};
var documentsControllerCreate = async (documentCreateDto, options) => {
  return customFetch(
    getDocumentsControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        documentCreateDto
      )
    }
  );
};
var getDocumentsControllerCreateMutationOptions = (options) => {
  const mutationKey = ["documentsControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return documentsControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerCreate = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerDetailUrl = (id) => {
  return `/api/documents/${id}`;
};
var documentsControllerDetail = async (id, options) => {
  return customFetch(
    getDocumentsControllerDetailUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDocumentsControllerDetailQueryKey = (id) => {
  return [
    `/api/documents/${id}`
  ];
};
var getDocumentsControllerDetailQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDocumentsControllerDetailQueryKey(id);
  const queryFn = ({ signal }) => documentsControllerDetail(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useDocumentsControllerDetail(id, options, queryClient) {
  const queryOptions = getDocumentsControllerDetailQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerUpdateUrl = (id) => {
  return `/api/documents/${id}`;
};
var documentsControllerUpdate = async (id, documentUpdateDto, options) => {
  return customFetch(
    getDocumentsControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        documentUpdateDto
      )
    }
  );
};
var getDocumentsControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["documentsControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return documentsControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerUpdate = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerRemoveUrl = (id) => {
  return `/api/documents/${id}`;
};
var documentsControllerRemove = async (id, options) => {
  return customFetch(
    getDocumentsControllerRemoveUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getDocumentsControllerRemoveMutationOptions = (options) => {
  const mutationKey = ["documentsControllerRemove"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return documentsControllerRemove(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerRemove = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerRemoveMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerAddAttachmentUrl = (id, lineId) => {
  return `/api/documents/${id}/lines/${lineId}/attachments`;
};
var documentsControllerAddAttachment = async (id, lineId, lineAttachmentCreateDto, options) => {
  return customFetch(
    getDocumentsControllerAddAttachmentUrl(id, lineId),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        lineAttachmentCreateDto
      )
    }
  );
};
var getDocumentsControllerAddAttachmentMutationOptions = (options) => {
  const mutationKey = ["documentsControllerAddAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, lineId, data } = props ?? {};
    return documentsControllerAddAttachment(id, lineId, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerAddAttachment = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerAddAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerUpdateAttachmentUrl = (id, lineId, attachmentId) => {
  return `/api/documents/${id}/lines/${lineId}/attachments/${attachmentId}`;
};
var documentsControllerUpdateAttachment = async (id, lineId, attachmentId, lineAttachmentUpdateDto, options) => {
  return customFetch(
    getDocumentsControllerUpdateAttachmentUrl(id, lineId, attachmentId),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        lineAttachmentUpdateDto
      )
    }
  );
};
var getDocumentsControllerUpdateAttachmentMutationOptions = (options) => {
  const mutationKey = ["documentsControllerUpdateAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, lineId, attachmentId, data } = props ?? {};
    return documentsControllerUpdateAttachment(id, lineId, attachmentId, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerUpdateAttachment = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerUpdateAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerRemoveAttachmentUrl = (id, lineId, attachmentId) => {
  return `/api/documents/${id}/lines/${lineId}/attachments/${attachmentId}`;
};
var documentsControllerRemoveAttachment = async (id, lineId, attachmentId, options) => {
  return customFetch(
    getDocumentsControllerRemoveAttachmentUrl(id, lineId, attachmentId),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getDocumentsControllerRemoveAttachmentMutationOptions = (options) => {
  const mutationKey = ["documentsControllerRemoveAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, lineId, attachmentId } = props ?? {};
    return documentsControllerRemoveAttachment(id, lineId, attachmentId, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerRemoveAttachment = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerRemoveAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerReopenUrl = (id) => {
  return `/api/documents/${id}/reopen`;
};
var documentsControllerReopen = async (id, options) => {
  return customFetch(
    getDocumentsControllerReopenUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getDocumentsControllerReopenMutationOptions = (options) => {
  const mutationKey = ["documentsControllerReopen"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return documentsControllerReopen(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerReopen = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerReopenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerMergeWorkReportsUrl = (id) => {
  return `/api/documents/${id}/work-report-merge`;
};
var documentsControllerMergeWorkReports = async (id, workReportMergeDto, options) => {
  return customFetch(
    getDocumentsControllerMergeWorkReportsUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        workReportMergeDto
      )
    }
  );
};
var getDocumentsControllerMergeWorkReportsMutationOptions = (options) => {
  const mutationKey = ["documentsControllerMergeWorkReports"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return documentsControllerMergeWorkReports(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerMergeWorkReports = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerMergeWorkReportsMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerFinalizeUrl = (id) => {
  return `/api/documents/${id}/finalize`;
};
var documentsControllerFinalize = async (id, options) => {
  return customFetch(
    getDocumentsControllerFinalizeUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getDocumentsControllerFinalizeMutationOptions = (options) => {
  const mutationKey = ["documentsControllerFinalize"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return documentsControllerFinalize(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerFinalize = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerFinalizeMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerCancelUrl = (id) => {
  return `/api/documents/${id}/cancel`;
};
var documentsControllerCancel = async (id, options) => {
  return customFetch(
    getDocumentsControllerCancelUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getDocumentsControllerCancelMutationOptions = (options) => {
  const mutationKey = ["documentsControllerCancel"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return documentsControllerCancel(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerCancel = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerCancelMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerCorrectUrl = (id) => {
  return `/api/documents/${id}/correct`;
};
var documentsControllerCorrect = async (id, options) => {
  return customFetch(
    getDocumentsControllerCorrectUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getDocumentsControllerCorrectMutationOptions = (options) => {
  const mutationKey = ["documentsControllerCorrect"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return documentsControllerCorrect(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerCorrect = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerCorrectMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerWrFotoUrl = (id) => {
  return `/api/documents/${id}/wr-foto`;
};
var documentsControllerWrFoto = async (id, wrFotoUploadDto, options) => {
  return customFetch(
    getDocumentsControllerWrFotoUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        wrFotoUploadDto
      )
    }
  );
};
var getDocumentsControllerWrFotoMutationOptions = (options) => {
  const mutationKey = ["documentsControllerWrFoto"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return documentsControllerWrFoto(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerWrFoto = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerWrFotoMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerWrFotoBildUrl = (id, objectId) => {
  return `/api/documents/${id}/wr-foto/${objectId}`;
};
var documentsControllerWrFotoBild = async (id, objectId, options) => {
  return customFetch(
    getDocumentsControllerWrFotoBildUrl(id, objectId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDocumentsControllerWrFotoBildQueryKey = (id, objectId) => {
  return [
    `/api/documents/${id}/wr-foto/${objectId}`
  ];
};
var getDocumentsControllerWrFotoBildQueryOptions = (id, objectId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDocumentsControllerWrFotoBildQueryKey(id, objectId);
  const queryFn = ({ signal }) => documentsControllerWrFotoBild(id, objectId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!(id && objectId), ...queryOptions };
};
function useDocumentsControllerWrFotoBild(id, objectId, options, queryClient) {
  const queryOptions = getDocumentsControllerWrFotoBildQueryOptions(id, objectId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerPreviewHtmlUrl = (id) => {
  return `/api/documents/${id}/preview`;
};
var documentsControllerPreviewHtml = async (id, options) => {
  return customFetch(
    getDocumentsControllerPreviewHtmlUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDocumentsControllerPreviewHtmlQueryKey = (id) => {
  return [
    `/api/documents/${id}/preview`
  ];
};
var getDocumentsControllerPreviewHtmlQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDocumentsControllerPreviewHtmlQueryKey(id);
  const queryFn = ({ signal }) => documentsControllerPreviewHtml(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useDocumentsControllerPreviewHtml(id, options, queryClient) {
  const queryOptions = getDocumentsControllerPreviewHtmlQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerPreviewPdfUrl = (id) => {
  return `/api/documents/${id}/preview.pdf`;
};
var documentsControllerPreviewPdf = async (id, options) => {
  return customFetch(
    getDocumentsControllerPreviewPdfUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDocumentsControllerPreviewPdfQueryKey = (id) => {
  return [
    `/api/documents/${id}/preview.pdf`
  ];
};
var getDocumentsControllerPreviewPdfQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDocumentsControllerPreviewPdfQueryKey(id);
  const queryFn = ({ signal }) => documentsControllerPreviewPdf(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useDocumentsControllerPreviewPdf(id, options, queryClient) {
  const queryOptions = getDocumentsControllerPreviewPdfQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerPreviewPagesUrl = (id) => {
  return `/api/documents/${id}/preview-pages`;
};
var documentsControllerPreviewPages = async (id, options) => {
  return customFetch(
    getDocumentsControllerPreviewPagesUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDocumentsControllerPreviewPagesQueryKey = (id) => {
  return [
    `/api/documents/${id}/preview-pages`
  ];
};
var getDocumentsControllerPreviewPagesQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDocumentsControllerPreviewPagesQueryKey(id);
  const queryFn = ({ signal }) => documentsControllerPreviewPages(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useDocumentsControllerPreviewPages(id, options, queryClient) {
  const queryOptions = getDocumentsControllerPreviewPagesQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerPdfUrl = (id) => {
  return `/api/documents/${id}/pdf`;
};
var documentsControllerPdf = async (id, options) => {
  return customFetch(
    getDocumentsControllerPdfUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDocumentsControllerPdfQueryKey = (id) => {
  return [
    `/api/documents/${id}/pdf`
  ];
};
var getDocumentsControllerPdfQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDocumentsControllerPdfQueryKey(id);
  const queryFn = ({ signal }) => documentsControllerPdf(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useDocumentsControllerPdf(id, options, queryClient) {
  const queryOptions = getDocumentsControllerPdfQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerXmlUrl = (id) => {
  return `/api/documents/${id}/xml`;
};
var documentsControllerXml = async (id, options) => {
  return customFetch(
    getDocumentsControllerXmlUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDocumentsControllerXmlQueryKey = (id) => {
  return [
    `/api/documents/${id}/xml`
  ];
};
var getDocumentsControllerXmlQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDocumentsControllerXmlQueryKey(id);
  const queryFn = ({ signal }) => documentsControllerXml(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useDocumentsControllerXml(id, options, queryClient) {
  const queryOptions = getDocumentsControllerXmlQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerDeriveUrl = (id) => {
  return `/api/documents/${id}/derive`;
};
var documentsControllerDerive = async (id, documentDeriveDto, options) => {
  return customFetch(
    getDocumentsControllerDeriveUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        documentDeriveDto
      )
    }
  );
};
var getDocumentsControllerDeriveMutationOptions = (options) => {
  const mutationKey = ["documentsControllerDerive"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return documentsControllerDerive(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerDerive = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerDeriveMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerDuplicateUrl = (id) => {
  return `/api/documents/${id}/duplicate`;
};
var documentsControllerDuplicate = async (id, documentDuplicateDto, options) => {
  return customFetch(
    getDocumentsControllerDuplicateUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        documentDuplicateDto
      )
    }
  );
};
var getDocumentsControllerDuplicateMutationOptions = (options) => {
  const mutationKey = ["documentsControllerDuplicate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return documentsControllerDuplicate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerDuplicate = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerDuplicateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerMailDraftUrl = (id) => {
  return `/api/documents/${id}/mail-draft`;
};
var documentsControllerMailDraft = async (id, options) => {
  return customFetch(
    getDocumentsControllerMailDraftUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDocumentsControllerMailDraftQueryKey = (id) => {
  return [
    `/api/documents/${id}/mail-draft`
  ];
};
var getDocumentsControllerMailDraftQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDocumentsControllerMailDraftQueryKey(id);
  const queryFn = ({ signal }) => documentsControllerMailDraft(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useDocumentsControllerMailDraft(id, options, queryClient) {
  const queryOptions = getDocumentsControllerMailDraftQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerSendUrl = (id) => {
  return `/api/documents/${id}/send`;
};
var documentsControllerSend = async (id, documentSendDto, options) => {
  return customFetch(
    getDocumentsControllerSendUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        documentSendDto
      )
    }
  );
};
var getDocumentsControllerSendMutationOptions = (options) => {
  const mutationKey = ["documentsControllerSend"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return documentsControllerSend(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerSend = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerSendMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerDatevUploadUrl = (id) => {
  return `/api/documents/${id}/datev-upload`;
};
var documentsControllerDatevUpload = async (id, options) => {
  return customFetch(
    getDocumentsControllerDatevUploadUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getDocumentsControllerDatevUploadMutationOptions = (options) => {
  const mutationKey = ["documentsControllerDatevUpload"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return documentsControllerDatevUpload(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerDatevUpload = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerDatevUploadMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDocumentsControllerEmailsUrl = (id) => {
  return `/api/documents/${id}/emails`;
};
var documentsControllerEmails = async (id, options) => {
  return customFetch(
    getDocumentsControllerEmailsUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDocumentsControllerEmailsQueryKey = (id) => {
  return [
    `/api/documents/${id}/emails`
  ];
};
var getDocumentsControllerEmailsQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDocumentsControllerEmailsQueryKey(id);
  const queryFn = ({ signal }) => documentsControllerEmails(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useDocumentsControllerEmails(id, options, queryClient) {
  const queryOptions = getDocumentsControllerEmailsQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDocumentsControllerMarkSubmittedUrl = (id) => {
  return `/api/documents/${id}/mark-submitted`;
};
var documentsControllerMarkSubmitted = async (id, options) => {
  return customFetch(
    getDocumentsControllerMarkSubmittedUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getDocumentsControllerMarkSubmittedMutationOptions = (options) => {
  const mutationKey = ["documentsControllerMarkSubmitted"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return documentsControllerMarkSubmitted(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDocumentsControllerMarkSubmitted = (options, queryClient) => {
  const mutationOptions = getDocumentsControllerMarkSubmittedMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTextTemplatesControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/text-templates?${stringifiedParams}` : `/api/text-templates`;
};
var textTemplatesControllerList = async (params, options) => {
  return customFetch(
    getTextTemplatesControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTextTemplatesControllerListQueryKey = (params) => {
  return [
    `/api/text-templates`,
    ...params ? [params] : []
  ];
};
var getTextTemplatesControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTextTemplatesControllerListQueryKey(params);
  const queryFn = ({ signal }) => textTemplatesControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTextTemplatesControllerList(params, options, queryClient) {
  const queryOptions = getTextTemplatesControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTextTemplatesControllerCreateUrl = () => {
  return `/api/text-templates`;
};
var textTemplatesControllerCreate = async (textTemplateCreateDto, options) => {
  return customFetch(
    getTextTemplatesControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        textTemplateCreateDto
      )
    }
  );
};
var getTextTemplatesControllerCreateMutationOptions = (options) => {
  const mutationKey = ["textTemplatesControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return textTemplatesControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTextTemplatesControllerCreate = (options, queryClient) => {
  const mutationOptions = getTextTemplatesControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTextTemplatesControllerUpdateUrl = (id) => {
  return `/api/text-templates/${id}`;
};
var textTemplatesControllerUpdate = async (id, textTemplateUpdateDto, options) => {
  return customFetch(
    getTextTemplatesControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        textTemplateUpdateDto
      )
    }
  );
};
var getTextTemplatesControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["textTemplatesControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return textTemplatesControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTextTemplatesControllerUpdate = (options, queryClient) => {
  const mutationOptions = getTextTemplatesControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTextTemplatesControllerArchiveUrl = (id) => {
  return `/api/text-templates/${id}`;
};
var textTemplatesControllerArchive = async (id, options) => {
  return customFetch(
    getTextTemplatesControllerArchiveUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getTextTemplatesControllerArchiveMutationOptions = (options) => {
  const mutationKey = ["textTemplatesControllerArchive"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return textTemplatesControllerArchive(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTextTemplatesControllerArchive = (options, queryClient) => {
  const mutationOptions = getTextTemplatesControllerArchiveMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPaymentsControllerListUrl = (id) => {
  return `/api/documents/${id}/payments`;
};
var paymentsControllerList = async (id, options) => {
  return customFetch(
    getPaymentsControllerListUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getPaymentsControllerListQueryKey = (id) => {
  return [
    `/api/documents/${id}/payments`
  ];
};
var getPaymentsControllerListQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getPaymentsControllerListQueryKey(id);
  const queryFn = ({ signal }) => paymentsControllerList(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function usePaymentsControllerList(id, options, queryClient) {
  const queryOptions = getPaymentsControllerListQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getPaymentsControllerCreateUrl = (id) => {
  return `/api/documents/${id}/payments`;
};
var paymentsControllerCreate = async (id, paymentCreateDto, options) => {
  return customFetch(
    getPaymentsControllerCreateUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        paymentCreateDto
      )
    }
  );
};
var getPaymentsControllerCreateMutationOptions = (options) => {
  const mutationKey = ["paymentsControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return paymentsControllerCreate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePaymentsControllerCreate = (options, queryClient) => {
  const mutationOptions = getPaymentsControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPaymentsControllerRemoveUrl = (paymentId) => {
  return `/api/payments/${paymentId}`;
};
var paymentsControllerRemove = async (paymentId, options) => {
  return customFetch(
    getPaymentsControllerRemoveUrl(paymentId),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getPaymentsControllerRemoveMutationOptions = (options) => {
  const mutationKey = ["paymentsControllerRemove"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { paymentId } = props ?? {};
    return paymentsControllerRemove(paymentId, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePaymentsControllerRemove = (options, queryClient) => {
  const mutationOptions = getPaymentsControllerRemoveMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPaymentsControllerOpenItemsUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/open-items?${stringifiedParams}` : `/api/open-items`;
};
var paymentsControllerOpenItems = async (params, options) => {
  return customFetch(
    getPaymentsControllerOpenItemsUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getPaymentsControllerOpenItemsQueryKey = (params) => {
  return [
    `/api/open-items`,
    ...params ? [params] : []
  ];
};
var getPaymentsControllerOpenItemsQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getPaymentsControllerOpenItemsQueryKey(params);
  const queryFn = ({ signal }) => paymentsControllerOpenItems(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function usePaymentsControllerOpenItems(params, options, queryClient) {
  const queryOptions = getPaymentsControllerOpenItemsQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getPaymentsControllerOpenItemsCsvUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/open-items/csv?${stringifiedParams}` : `/api/open-items/csv`;
};
var paymentsControllerOpenItemsCsv = async (params, options) => {
  return customFetch(
    getPaymentsControllerOpenItemsCsvUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getPaymentsControllerOpenItemsCsvQueryKey = (params) => {
  return [
    `/api/open-items/csv`,
    ...params ? [params] : []
  ];
};
var getPaymentsControllerOpenItemsCsvQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getPaymentsControllerOpenItemsCsvQueryKey(params);
  const queryFn = ({ signal }) => paymentsControllerOpenItemsCsv(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function usePaymentsControllerOpenItemsCsv(params, options, queryClient) {
  const queryOptions = getPaymentsControllerOpenItemsCsvQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getBankImportControllerPruefenUrl = () => {
  return `/api/payments/bank/pruefen`;
};
var bankImportControllerPruefen = async (bankImportDto, options) => {
  return customFetch(
    getBankImportControllerPruefenUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        bankImportDto
      )
    }
  );
};
var getBankImportControllerPruefenMutationOptions = (options) => {
  const mutationKey = ["bankImportControllerPruefen"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return bankImportControllerPruefen(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useBankImportControllerPruefen = (options, queryClient) => {
  const mutationOptions = getBankImportControllerPruefenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getBankImportControllerEinlesenUrl = () => {
  return `/api/payments/bank/einlesen`;
};
var bankImportControllerEinlesen = async (bankImportDto, options) => {
  return customFetch(
    getBankImportControllerEinlesenUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        bankImportDto
      )
    }
  );
};
var getBankImportControllerEinlesenMutationOptions = (options) => {
  const mutationKey = ["bankImportControllerEinlesen"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return bankImportControllerEinlesen(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useBankImportControllerEinlesen = (options, queryClient) => {
  const mutationOptions = getBankImportControllerEinlesenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getBankImportControllerListeUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/payments/bank?${stringifiedParams}` : `/api/payments/bank`;
};
var bankImportControllerListe = async (params, options) => {
  return customFetch(
    getBankImportControllerListeUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getBankImportControllerListeQueryKey = (params) => {
  return [
    `/api/payments/bank`,
    ...params ? [params] : []
  ];
};
var getBankImportControllerListeQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getBankImportControllerListeQueryKey(params);
  const queryFn = ({ signal }) => bankImportControllerListe(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useBankImportControllerListe(params, options, queryClient) {
  const queryOptions = getBankImportControllerListeQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getBankImportControllerRechnungenUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/payments/bank/rechnungen?${stringifiedParams}` : `/api/payments/bank/rechnungen`;
};
var bankImportControllerRechnungen = async (params, options) => {
  return customFetch(
    getBankImportControllerRechnungenUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getBankImportControllerRechnungenQueryKey = (params) => {
  return [
    `/api/payments/bank/rechnungen`,
    ...params ? [params] : []
  ];
};
var getBankImportControllerRechnungenQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getBankImportControllerRechnungenQueryKey(params);
  const queryFn = ({ signal }) => bankImportControllerRechnungen(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useBankImportControllerRechnungen(params, options, queryClient) {
  const queryOptions = getBankImportControllerRechnungenQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getBankImportControllerVorschlaegeUrl = (id) => {
  return `/api/payments/bank/${id}/vorschlaege`;
};
var bankImportControllerVorschlaege = async (id, options) => {
  return customFetch(
    getBankImportControllerVorschlaegeUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getBankImportControllerVorschlaegeQueryKey = (id) => {
  return [
    `/api/payments/bank/${id}/vorschlaege`
  ];
};
var getBankImportControllerVorschlaegeQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getBankImportControllerVorschlaegeQueryKey(id);
  const queryFn = ({ signal }) => bankImportControllerVorschlaege(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useBankImportControllerVorschlaege(id, options, queryClient) {
  const queryOptions = getBankImportControllerVorschlaegeQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getBankImportControllerZuordnenUrl = (id) => {
  return `/api/payments/bank/${id}/zuordnen`;
};
var bankImportControllerZuordnen = async (id, bankZuordnenDto, options) => {
  return customFetch(
    getBankImportControllerZuordnenUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        bankZuordnenDto
      )
    }
  );
};
var getBankImportControllerZuordnenMutationOptions = (options) => {
  const mutationKey = ["bankImportControllerZuordnen"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return bankImportControllerZuordnen(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useBankImportControllerZuordnen = (options, queryClient) => {
  const mutationOptions = getBankImportControllerZuordnenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getBankImportControllerZuruecksetzenUrl = (id) => {
  return `/api/payments/bank/${id}/zuruecksetzen`;
};
var bankImportControllerZuruecksetzen = async (id, options) => {
  return customFetch(
    getBankImportControllerZuruecksetzenUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getBankImportControllerZuruecksetzenMutationOptions = (options) => {
  const mutationKey = ["bankImportControllerZuruecksetzen"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return bankImportControllerZuruecksetzen(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useBankImportControllerZuruecksetzen = (options, queryClient) => {
  const mutationOptions = getBankImportControllerZuruecksetzenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getBankImportControllerIgnorierenUrl = (id) => {
  return `/api/payments/bank/${id}/ignorieren`;
};
var bankImportControllerIgnorieren = async (id, bankIgnorierenDto, options) => {
  return customFetch(
    getBankImportControllerIgnorierenUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        bankIgnorierenDto
      )
    }
  );
};
var getBankImportControllerIgnorierenMutationOptions = (options) => {
  const mutationKey = ["bankImportControllerIgnorieren"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return bankImportControllerIgnorieren(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useBankImportControllerIgnorieren = (options, queryClient) => {
  const mutationOptions = getBankImportControllerIgnorierenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerNavCountersUrl = () => {
  return `/api/orders/nav-counters`;
};
var ordersControllerNavCounters = async (options) => {
  return customFetch(
    getOrdersControllerNavCountersUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getOrdersControllerNavCountersQueryKey = () => {
  return [
    `/api/orders/nav-counters`
  ];
};
var getOrdersControllerNavCountersQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getOrdersControllerNavCountersQueryKey();
  const queryFn = ({ signal }) => ordersControllerNavCounters({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useOrdersControllerNavCounters(options, queryClient) {
  const queryOptions = getOrdersControllerNavCountersQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getOrdersControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/orders?${stringifiedParams}` : `/api/orders`;
};
var ordersControllerList = async (params, options) => {
  return customFetch(
    getOrdersControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getOrdersControllerListQueryKey = (params) => {
  return [
    `/api/orders`,
    ...params ? [params] : []
  ];
};
var getOrdersControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getOrdersControllerListQueryKey(params);
  const queryFn = ({ signal }) => ordersControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useOrdersControllerList(params, options, queryClient) {
  const queryOptions = getOrdersControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getOrdersControllerCreateUrl = () => {
  return `/api/orders`;
};
var ordersControllerCreate = async (orderCreateDto, options) => {
  return customFetch(
    getOrdersControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderCreateDto
      )
    }
  );
};
var getOrdersControllerCreateMutationOptions = (options) => {
  const mutationKey = ["ordersControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return ordersControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerCreate = (options, queryClient) => {
  const mutationOptions = getOrdersControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerLeerwohnungUrl = (id) => {
  return `/api/orders/${id}/leerwohnung`;
};
var ordersControllerLeerwohnung = async (id, options) => {
  return customFetch(
    getOrdersControllerLeerwohnungUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getOrdersControllerLeerwohnungQueryKey = (id) => {
  return [
    `/api/orders/${id}/leerwohnung`
  ];
};
var getOrdersControllerLeerwohnungQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getOrdersControllerLeerwohnungQueryKey(id);
  const queryFn = ({ signal }) => ordersControllerLeerwohnung(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useOrdersControllerLeerwohnung(id, options, queryClient) {
  const queryOptions = getOrdersControllerLeerwohnungQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getOrdersControllerLeerwohnungLoeschenUrl = (id, eventId) => {
  return `/api/orders/${id}/leerwohnung/${eventId}`;
};
var ordersControllerLeerwohnungLoeschen = async (id, eventId, options) => {
  return customFetch(
    getOrdersControllerLeerwohnungLoeschenUrl(id, eventId),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getOrdersControllerLeerwohnungLoeschenMutationOptions = (options) => {
  const mutationKey = ["ordersControllerLeerwohnungLoeschen"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, eventId } = props ?? {};
    return ordersControllerLeerwohnungLoeschen(id, eventId, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerLeerwohnungLoeschen = (options, queryClient) => {
  const mutationOptions = getOrdersControllerLeerwohnungLoeschenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerUploadUrl = (id, objectId) => {
  return `/api/orders/${id}/uploads/${objectId}`;
};
var ordersControllerUpload = async (id, objectId, options) => {
  return customFetch(
    getOrdersControllerUploadUrl(id, objectId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getOrdersControllerUploadQueryKey = (id, objectId) => {
  return [
    `/api/orders/${id}/uploads/${objectId}`
  ];
};
var getOrdersControllerUploadQueryOptions = (id, objectId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getOrdersControllerUploadQueryKey(id, objectId);
  const queryFn = ({ signal }) => ordersControllerUpload(id, objectId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!(id && objectId), ...queryOptions };
};
function useOrdersControllerUpload(id, objectId, options, queryClient) {
  const queryOptions = getOrdersControllerUploadQueryOptions(id, objectId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getOrdersControllerFromQuoteUrl = (quoteId) => {
  return `/api/orders/from-quote/${quoteId}`;
};
var ordersControllerFromQuote = async (quoteId, options) => {
  return customFetch(
    getOrdersControllerFromQuoteUrl(quoteId),
    {
      ...options,
      method: "POST"
    }
  );
};
var getOrdersControllerFromQuoteMutationOptions = (options) => {
  const mutationKey = ["ordersControllerFromQuote"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { quoteId } = props ?? {};
    return ordersControllerFromQuote(quoteId, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerFromQuote = (options, queryClient) => {
  const mutationOptions = getOrdersControllerFromQuoteMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerNachtragUrl = (id) => {
  return `/api/orders/${id}/nachtrag`;
};
var ordersControllerNachtrag = async (id, options) => {
  return customFetch(
    getOrdersControllerNachtragUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getOrdersControllerNachtragMutationOptions = (options) => {
  const mutationKey = ["ordersControllerNachtrag"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return ordersControllerNachtrag(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerNachtrag = (options, queryClient) => {
  const mutationOptions = getOrdersControllerNachtragMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerDetailUrl = (id) => {
  return `/api/orders/${id}`;
};
var ordersControllerDetail = async (id, options) => {
  return customFetch(
    getOrdersControllerDetailUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getOrdersControllerDetailQueryKey = (id) => {
  return [
    `/api/orders/${id}`
  ];
};
var getOrdersControllerDetailQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getOrdersControllerDetailQueryKey(id);
  const queryFn = ({ signal }) => ordersControllerDetail(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useOrdersControllerDetail(id, options, queryClient) {
  const queryOptions = getOrdersControllerDetailQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getOrdersControllerUpdateUrl = (id) => {
  return `/api/orders/${id}`;
};
var ordersControllerUpdate = async (id, orderUpdateDto, options) => {
  return customFetch(
    getOrdersControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderUpdateDto
      )
    }
  );
};
var getOrdersControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["ordersControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return ordersControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerUpdate = (options, queryClient) => {
  const mutationOptions = getOrdersControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerRemoveUrl = (id) => {
  return `/api/orders/${id}`;
};
var ordersControllerRemove = async (id, options) => {
  return customFetch(
    getOrdersControllerRemoveUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getOrdersControllerRemoveMutationOptions = (options) => {
  const mutationKey = ["ordersControllerRemove"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return ordersControllerRemove(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerRemove = (options, queryClient) => {
  const mutationOptions = getOrdersControllerRemoveMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerAppDatenUrl = (id) => {
  return `/api/orders/${id}/app-daten`;
};
var ordersControllerAppDaten = async (id, options) => {
  return customFetch(
    getOrdersControllerAppDatenUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getOrdersControllerAppDatenQueryKey = (id) => {
  return [
    `/api/orders/${id}/app-daten`
  ];
};
var getOrdersControllerAppDatenQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getOrdersControllerAppDatenQueryKey(id);
  const queryFn = ({ signal }) => ordersControllerAppDaten(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useOrdersControllerAppDaten(id, options, queryClient) {
  const queryOptions = getOrdersControllerAppDatenQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getOrdersControllerVorgangUrl = (id) => {
  return `/api/orders/${id}/vorgang`;
};
var ordersControllerVorgang = async (id, options) => {
  return customFetch(
    getOrdersControllerVorgangUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getOrdersControllerVorgangQueryKey = (id) => {
  return [
    `/api/orders/${id}/vorgang`
  ];
};
var getOrdersControllerVorgangQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getOrdersControllerVorgangQueryKey(id);
  const queryFn = ({ signal }) => ordersControllerVorgang(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useOrdersControllerVorgang(id, options, queryClient) {
  const queryOptions = getOrdersControllerVorgangQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getOrdersControllerLoeschVorschauUrl = (id) => {
  return `/api/orders/${id}/loesch-vorschau`;
};
var ordersControllerLoeschVorschau = async (id, options) => {
  return customFetch(
    getOrdersControllerLoeschVorschauUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getOrdersControllerLoeschVorschauQueryKey = (id) => {
  return [
    `/api/orders/${id}/loesch-vorschau`
  ];
};
var getOrdersControllerLoeschVorschauQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getOrdersControllerLoeschVorschauQueryKey(id);
  const queryFn = ({ signal }) => ordersControllerLoeschVorschau(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useOrdersControllerLoeschVorschau(id, options, queryClient) {
  const queryOptions = getOrdersControllerLoeschVorschauQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getOrdersControllerLoeschenUrl = (id) => {
  return `/api/orders/${id}/loeschen`;
};
var ordersControllerLoeschen = async (id, orderLoeschenDto, options) => {
  return customFetch(
    getOrdersControllerLoeschenUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderLoeschenDto
      )
    }
  );
};
var getOrdersControllerLoeschenMutationOptions = (options) => {
  const mutationKey = ["ordersControllerLoeschen"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return ordersControllerLoeschen(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerLoeschen = (options, queryClient) => {
  const mutationOptions = getOrdersControllerLoeschenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerAddAttachmentUrl = (id) => {
  return `/api/orders/${id}/attachments`;
};
var ordersControllerAddAttachment = async (id, orderAttachmentCreateDto, options) => {
  return customFetch(
    getOrdersControllerAddAttachmentUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderAttachmentCreateDto
      )
    }
  );
};
var getOrdersControllerAddAttachmentMutationOptions = (options) => {
  const mutationKey = ["ordersControllerAddAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return ordersControllerAddAttachment(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerAddAttachment = (options, queryClient) => {
  const mutationOptions = getOrdersControllerAddAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerUpdateAttachmentUrl = (id, attachmentId) => {
  return `/api/orders/${id}/attachments/${attachmentId}`;
};
var ordersControllerUpdateAttachment = async (id, attachmentId, orderAttachmentUpdateDto, options) => {
  return customFetch(
    getOrdersControllerUpdateAttachmentUrl(id, attachmentId),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderAttachmentUpdateDto
      )
    }
  );
};
var getOrdersControllerUpdateAttachmentMutationOptions = (options) => {
  const mutationKey = ["ordersControllerUpdateAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, attachmentId, data } = props ?? {};
    return ordersControllerUpdateAttachment(id, attachmentId, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerUpdateAttachment = (options, queryClient) => {
  const mutationOptions = getOrdersControllerUpdateAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerRemoveAttachmentUrl = (id, attachmentId) => {
  return `/api/orders/${id}/attachments/${attachmentId}`;
};
var ordersControllerRemoveAttachment = async (id, attachmentId, options) => {
  return customFetch(
    getOrdersControllerRemoveAttachmentUrl(id, attachmentId),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getOrdersControllerRemoveAttachmentMutationOptions = (options) => {
  const mutationKey = ["ordersControllerRemoveAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, attachmentId } = props ?? {};
    return ordersControllerRemoveAttachment(id, attachmentId, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerRemoveAttachment = (options, queryClient) => {
  const mutationOptions = getOrdersControllerRemoveAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerAttachmentFileUrl = (id, attachmentId) => {
  return `/api/orders/${id}/attachments/${attachmentId}/datei`;
};
var ordersControllerAttachmentFile = async (id, attachmentId, options) => {
  return customFetch(
    getOrdersControllerAttachmentFileUrl(id, attachmentId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getOrdersControllerAttachmentFileQueryKey = (id, attachmentId) => {
  return [
    `/api/orders/${id}/attachments/${attachmentId}/datei`
  ];
};
var getOrdersControllerAttachmentFileQueryOptions = (id, attachmentId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getOrdersControllerAttachmentFileQueryKey(id, attachmentId);
  const queryFn = ({ signal }) => ordersControllerAttachmentFile(id, attachmentId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!(id && attachmentId), ...queryOptions };
};
function useOrdersControllerAttachmentFile(id, attachmentId, options, queryClient) {
  const queryOptions = getOrdersControllerAttachmentFileQueryOptions(id, attachmentId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getOrdersControllerStornoUrl = (id) => {
  return `/api/orders/${id}/storno`;
};
var ordersControllerStorno = async (id, orderStornoDto, options) => {
  return customFetch(
    getOrdersControllerStornoUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderStornoDto
      )
    }
  );
};
var getOrdersControllerStornoMutationOptions = (options) => {
  const mutationKey = ["ordersControllerStorno"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return ordersControllerStorno(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerStorno = (options, queryClient) => {
  const mutationOptions = getOrdersControllerStornoMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerSetHoldUrl = (id) => {
  return `/api/orders/${id}/hold`;
};
var ordersControllerSetHold = async (id, holdSetDto, options) => {
  return customFetch(
    getOrdersControllerSetHoldUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        holdSetDto
      )
    }
  );
};
var getOrdersControllerSetHoldMutationOptions = (options) => {
  const mutationKey = ["ordersControllerSetHold"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return ordersControllerSetHold(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerSetHold = (options, queryClient) => {
  const mutationOptions = getOrdersControllerSetHoldMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerResolveHoldUrl = (id) => {
  return `/api/orders/${id}/hold/resolve`;
};
var ordersControllerResolveHold = async (id, options) => {
  return customFetch(
    getOrdersControllerResolveHoldUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getOrdersControllerResolveHoldMutationOptions = (options) => {
  const mutationKey = ["ordersControllerResolveHold"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return ordersControllerResolveHold(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerResolveHold = (options, queryClient) => {
  const mutationOptions = getOrdersControllerResolveHoldMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getOrdersControllerPreviewHoldMailUrl = (id) => {
  return `/api/orders/${id}/hold/notify-customer/preview`;
};
var ordersControllerPreviewHoldMail = async (id, options) => {
  return customFetch(
    getOrdersControllerPreviewHoldMailUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getOrdersControllerPreviewHoldMailQueryKey = (id) => {
  return [
    `/api/orders/${id}/hold/notify-customer/preview`
  ];
};
var getOrdersControllerPreviewHoldMailQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getOrdersControllerPreviewHoldMailQueryKey(id);
  const queryFn = ({ signal }) => ordersControllerPreviewHoldMail(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useOrdersControllerPreviewHoldMail(id, options, queryClient) {
  const queryOptions = getOrdersControllerPreviewHoldMailQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getOrdersControllerSendHoldMailUrl = (id) => {
  return `/api/orders/${id}/hold/notify-customer`;
};
var ordersControllerSendHoldMail = async (id, options) => {
  return customFetch(
    getOrdersControllerSendHoldMailUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getOrdersControllerSendHoldMailMutationOptions = (options) => {
  const mutationKey = ["ordersControllerSendHoldMail"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return ordersControllerSendHoldMail(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useOrdersControllerSendHoldMail = (options, queryClient) => {
  const mutationOptions = getOrdersControllerSendHoldMailMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMaterialControllerOverviewUrl = () => {
  return `/api/material/overview`;
};
var materialControllerOverview = async (options) => {
  return customFetch(
    getMaterialControllerOverviewUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getMaterialControllerOverviewQueryKey = () => {
  return [
    `/api/material/overview`
  ];
};
var getMaterialControllerOverviewQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getMaterialControllerOverviewQueryKey();
  const queryFn = ({ signal }) => materialControllerOverview({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useMaterialControllerOverview(options, queryClient) {
  const queryOptions = getMaterialControllerOverviewQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMaterialControllerAppReceiptsUrl = () => {
  return `/api/material/app-receipts`;
};
var materialControllerAppReceipts = async (options) => {
  return customFetch(
    getMaterialControllerAppReceiptsUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getMaterialControllerAppReceiptsQueryKey = () => {
  return [
    `/api/material/app-receipts`
  ];
};
var getMaterialControllerAppReceiptsQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getMaterialControllerAppReceiptsQueryKey();
  const queryFn = ({ signal }) => materialControllerAppReceipts({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useMaterialControllerAppReceipts(options, queryClient) {
  const queryOptions = getMaterialControllerAppReceiptsQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMaterialControllerCreateUrl = (orderId) => {
  return `/api/material/orders/${orderId}`;
};
var materialControllerCreate = async (orderId, materialOrderCreateDto, options) => {
  return customFetch(
    getMaterialControllerCreateUrl(orderId),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        materialOrderCreateDto
      )
    }
  );
};
var getMaterialControllerCreateMutationOptions = (options) => {
  const mutationKey = ["materialControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { orderId, data } = props ?? {};
    return materialControllerCreate(orderId, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMaterialControllerCreate = (options, queryClient) => {
  const mutationOptions = getMaterialControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMaterialControllerMarkDeliveredUrl = (id) => {
  return `/api/material/${id}/delivered`;
};
var materialControllerMarkDelivered = async (id, options) => {
  return customFetch(
    getMaterialControllerMarkDeliveredUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getMaterialControllerMarkDeliveredMutationOptions = (options) => {
  const mutationKey = ["materialControllerMarkDelivered"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return materialControllerMarkDelivered(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMaterialControllerMarkDelivered = (options, queryClient) => {
  const mutationOptions = getMaterialControllerMarkDeliveredMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMaterialControllerRemoveUrl = (id) => {
  return `/api/material/${id}`;
};
var materialControllerRemove = async (id, options) => {
  return customFetch(
    getMaterialControllerRemoveUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMaterialControllerRemoveMutationOptions = (options) => {
  const mutationKey = ["materialControllerRemove"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return materialControllerRemove(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMaterialControllerRemove = (options, queryClient) => {
  const mutationOptions = getMaterialControllerRemoveMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMaterialControllerAddAttachmentUrl = (id) => {
  return `/api/material/${id}/attachments`;
};
var materialControllerAddAttachment = async (id, materialAttachmentCreateDto, options) => {
  return customFetch(
    getMaterialControllerAddAttachmentUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        materialAttachmentCreateDto
      )
    }
  );
};
var getMaterialControllerAddAttachmentMutationOptions = (options) => {
  const mutationKey = ["materialControllerAddAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return materialControllerAddAttachment(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMaterialControllerAddAttachment = (options, queryClient) => {
  const mutationOptions = getMaterialControllerAddAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMaterialControllerDownloadUrl = (attachmentId) => {
  return `/api/material/attachments/${attachmentId}`;
};
var materialControllerDownload = async (attachmentId, options) => {
  return customFetch(
    getMaterialControllerDownloadUrl(attachmentId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getMaterialControllerDownloadQueryKey = (attachmentId) => {
  return [
    `/api/material/attachments/${attachmentId}`
  ];
};
var getMaterialControllerDownloadQueryOptions = (attachmentId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getMaterialControllerDownloadQueryKey(attachmentId);
  const queryFn = ({ signal }) => materialControllerDownload(attachmentId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!attachmentId, ...queryOptions };
};
function useMaterialControllerDownload(attachmentId, options, queryClient) {
  const queryOptions = getMaterialControllerDownloadQueryOptions(attachmentId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMaterialControllerDeleteAttachmentUrl = (attachmentId) => {
  return `/api/material/attachments/${attachmentId}`;
};
var materialControllerDeleteAttachment = async (attachmentId, options) => {
  return customFetch(
    getMaterialControllerDeleteAttachmentUrl(attachmentId),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMaterialControllerDeleteAttachmentMutationOptions = (options) => {
  const mutationKey = ["materialControllerDeleteAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { attachmentId } = props ?? {};
    return materialControllerDeleteAttachment(attachmentId, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMaterialControllerDeleteAttachment = (options, queryClient) => {
  const mutationOptions = getMaterialControllerDeleteAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getBriefingControllerSendTestUrl = () => {
  return `/api/briefing/test`;
};
var briefingControllerSendTest = async (options) => {
  return customFetch(
    getBriefingControllerSendTestUrl(),
    {
      ...options,
      method: "POST"
    }
  );
};
var getBriefingControllerSendTestMutationOptions = (options) => {
  const mutationKey = ["briefingControllerSendTest"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = () => {
    return briefingControllerSendTest(requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useBriefingControllerSendTest = (options, queryClient) => {
  const mutationOptions = getBriefingControllerSendTestMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getBriefingControllerPreviewUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/briefing/preview?${stringifiedParams}` : `/api/briefing/preview`;
};
var briefingControllerPreview = async (params, options) => {
  return customFetch(
    getBriefingControllerPreviewUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getBriefingControllerPreviewQueryKey = (params) => {
  return [
    `/api/briefing/preview`,
    ...params ? [params] : []
  ];
};
var getBriefingControllerPreviewQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getBriefingControllerPreviewQueryKey(params);
  const queryFn = ({ signal }) => briefingControllerPreview(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useBriefingControllerPreview(params, options, queryClient) {
  const queryOptions = getBriefingControllerPreviewQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getWorkReportControllerReleaseUrl = (id) => {
  return `/api/documents/${id}/work-report-release`;
};
var workReportControllerRelease = async (id, options) => {
  return customFetch(
    getWorkReportControllerReleaseUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getWorkReportControllerReleaseMutationOptions = (options) => {
  const mutationKey = ["workReportControllerRelease"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return workReportControllerRelease(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useWorkReportControllerRelease = (options, queryClient) => {
  const mutationOptions = getWorkReportControllerReleaseMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPublicSignatureControllerPageUrl = (token) => {
  return `/api/public/sign/${token}`;
};
var publicSignatureControllerPage = async (token, options) => {
  return customFetch(
    getPublicSignatureControllerPageUrl(token),
    {
      ...options,
      method: "GET"
    }
  );
};
var getPublicSignatureControllerPageQueryKey = (token) => {
  return [
    `/api/public/sign/${token}`
  ];
};
var getPublicSignatureControllerPageQueryOptions = (token, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getPublicSignatureControllerPageQueryKey(token);
  const queryFn = ({ signal }) => publicSignatureControllerPage(token, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!token, ...queryOptions };
};
function usePublicSignatureControllerPage(token, options, queryClient) {
  const queryOptions = getPublicSignatureControllerPageQueryOptions(token, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getPublicSignatureControllerSubmitUrl = (token) => {
  return `/api/public/sign/${token}`;
};
var publicSignatureControllerSubmit = async (token, signPublicDto, options) => {
  return customFetch(
    getPublicSignatureControllerSubmitUrl(token),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        signPublicDto
      )
    }
  );
};
var getPublicSignatureControllerSubmitMutationOptions = (options) => {
  const mutationKey = ["publicSignatureControllerSubmit"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { token, data } = props ?? {};
    return publicSignatureControllerSubmit(token, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePublicSignatureControllerSubmit = (options, queryClient) => {
  const mutationOptions = getPublicSignatureControllerSubmitMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPublicSignatureControllerPdfUrl = (token) => {
  return `/api/public/sign/${token}/pdf`;
};
var publicSignatureControllerPdf = async (token, options) => {
  return customFetch(
    getPublicSignatureControllerPdfUrl(token),
    {
      ...options,
      method: "GET"
    }
  );
};
var getPublicSignatureControllerPdfQueryKey = (token) => {
  return [
    `/api/public/sign/${token}/pdf`
  ];
};
var getPublicSignatureControllerPdfQueryOptions = (token, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getPublicSignatureControllerPdfQueryKey(token);
  const queryFn = ({ signal }) => publicSignatureControllerPdf(token, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!token, ...queryOptions };
};
function usePublicSignatureControllerPdf(token, options, queryClient) {
  const queryOptions = getPublicSignatureControllerPdfQueryOptions(token, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getSignatureLinkControllerObligationUrl = (documentId) => {
  return `/api/documents/${documentId}/signature-obligation`;
};
var signatureLinkControllerObligation = async (documentId, options) => {
  return customFetch(
    getSignatureLinkControllerObligationUrl(documentId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getSignatureLinkControllerObligationQueryKey = (documentId) => {
  return [
    `/api/documents/${documentId}/signature-obligation`
  ];
};
var getSignatureLinkControllerObligationQueryOptions = (documentId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getSignatureLinkControllerObligationQueryKey(documentId);
  const queryFn = ({ signal }) => signatureLinkControllerObligation(documentId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!documentId, ...queryOptions };
};
function useSignatureLinkControllerObligation(documentId, options, queryClient) {
  const queryOptions = getSignatureLinkControllerObligationQueryOptions(documentId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getSignatureLinkControllerRequestSignatureUrl = (documentId) => {
  return `/api/documents/${documentId}/request-signature`;
};
var signatureLinkControllerRequestSignature = async (documentId, options) => {
  return customFetch(
    getSignatureLinkControllerRequestSignatureUrl(documentId),
    {
      ...options,
      method: "POST"
    }
  );
};
var getSignatureLinkControllerRequestSignatureMutationOptions = (options) => {
  const mutationKey = ["signatureLinkControllerRequestSignature"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { documentId } = props ?? {};
    return signatureLinkControllerRequestSignature(documentId, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useSignatureLinkControllerRequestSignature = (options, queryClient) => {
  const mutationOptions = getSignatureLinkControllerRequestSignatureMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getSignatureLinkControllerRevokeUrl = (id) => {
  return `/api/signature-requests/${id}/revoke`;
};
var signatureLinkControllerRevoke = async (id, options) => {
  return customFetch(
    getSignatureLinkControllerRevokeUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getSignatureLinkControllerRevokeMutationOptions = (options) => {
  const mutationKey = ["signatureLinkControllerRevoke"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return signatureLinkControllerRevoke(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useSignatureLinkControllerRevoke = (options, queryClient) => {
  const mutationOptions = getSignatureLinkControllerRevokeMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getSignatureLinkControllerListUrl = (orderId) => {
  return `/api/orders/${orderId}/signature-requests`;
};
var signatureLinkControllerList = async (orderId, options) => {
  return customFetch(
    getSignatureLinkControllerListUrl(orderId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getSignatureLinkControllerListQueryKey = (orderId) => {
  return [
    `/api/orders/${orderId}/signature-requests`
  ];
};
var getSignatureLinkControllerListQueryOptions = (orderId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getSignatureLinkControllerListQueryKey(orderId);
  const queryFn = ({ signal }) => signatureLinkControllerList(orderId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!orderId, ...queryOptions };
};
function useSignatureLinkControllerList(orderId, options, queryClient) {
  const queryOptions = getSignatureLinkControllerListQueryOptions(orderId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAppReleaseControllerVersionUrl = () => {
  return `/api/app/version`;
};
var appReleaseControllerVersion = async (options) => {
  return customFetch(
    getAppReleaseControllerVersionUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAppReleaseControllerVersionQueryKey = () => {
  return [
    `/api/app/version`
  ];
};
var getAppReleaseControllerVersionQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAppReleaseControllerVersionQueryKey();
  const queryFn = ({ signal }) => appReleaseControllerVersion({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAppReleaseControllerVersion(options, queryClient) {
  const queryOptions = getAppReleaseControllerVersionQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getRolesControllerListUrl = () => {
  return `/api/roles`;
};
var rolesControllerList = async (options) => {
  return customFetch(
    getRolesControllerListUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getRolesControllerListQueryKey = () => {
  return [
    `/api/roles`
  ];
};
var getRolesControllerListQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getRolesControllerListQueryKey();
  const queryFn = ({ signal }) => rolesControllerList({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useRolesControllerList(options, queryClient) {
  const queryOptions = getRolesControllerListQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getRolesControllerCreateUrl = () => {
  return `/api/roles`;
};
var rolesControllerCreate = async (roleCreateDto, options) => {
  return customFetch(
    getRolesControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        roleCreateDto
      )
    }
  );
};
var getRolesControllerCreateMutationOptions = (options) => {
  const mutationKey = ["rolesControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return rolesControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useRolesControllerCreate = (options, queryClient) => {
  const mutationOptions = getRolesControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getRolesControllerUpdateUrl = (id) => {
  return `/api/roles/${id}`;
};
var rolesControllerUpdate = async (id, roleUpdateDto, options) => {
  return customFetch(
    getRolesControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        roleUpdateDto
      )
    }
  );
};
var getRolesControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["rolesControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return rolesControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useRolesControllerUpdate = (options, queryClient) => {
  const mutationOptions = getRolesControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getRolesControllerRemoveUrl = (id) => {
  return `/api/roles/${id}`;
};
var rolesControllerRemove = async (id, options) => {
  return customFetch(
    getRolesControllerRemoveUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getRolesControllerRemoveMutationOptions = (options) => {
  const mutationKey = ["rolesControllerRemove"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return rolesControllerRemove(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useRolesControllerRemove = (options, queryClient) => {
  const mutationOptions = getRolesControllerRemoveMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getSettingsControllerGetUrl = () => {
  return `/api/settings`;
};
var settingsControllerGet = async (options) => {
  return customFetch(
    getSettingsControllerGetUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getSettingsControllerGetQueryKey = () => {
  return [
    `/api/settings`
  ];
};
var getSettingsControllerGetQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getSettingsControllerGetQueryKey();
  const queryFn = ({ signal }) => settingsControllerGet({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useSettingsControllerGet(options, queryClient) {
  const queryOptions = getSettingsControllerGetQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getSettingsControllerUpdateUrl = () => {
  return `/api/settings`;
};
var settingsControllerUpdate = async (settingsUpdateDto, options) => {
  return customFetch(
    getSettingsControllerUpdateUrl(),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        settingsUpdateDto
      )
    }
  );
};
var getSettingsControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["settingsControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return settingsControllerUpdate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useSettingsControllerUpdate = (options, queryClient) => {
  const mutationOptions = getSettingsControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getSettingsControllerSetNextNumbersUrl = () => {
  return `/api/settings/next-numbers`;
};
var settingsControllerSetNextNumbers = async (nextNumbersDto, options) => {
  return customFetch(
    getSettingsControllerSetNextNumbersUrl(),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        nextNumbersDto
      )
    }
  );
};
var getSettingsControllerSetNextNumbersMutationOptions = (options) => {
  const mutationKey = ["settingsControllerSetNextNumbers"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return settingsControllerSetNextNumbers(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useSettingsControllerSetNextNumbers = (options, queryClient) => {
  const mutationOptions = getSettingsControllerSetNextNumbersMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getSettingsControllerUploadLogoUrl = (slot) => {
  return `/api/settings/logo/${slot}`;
};
var settingsControllerUploadLogo = async (slot, logoUploadDto, options) => {
  return customFetch(
    getSettingsControllerUploadLogoUrl(slot),
    {
      ...options,
      method: "PUT",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        logoUploadDto
      )
    }
  );
};
var getSettingsControllerUploadLogoMutationOptions = (options) => {
  const mutationKey = ["settingsControllerUploadLogo"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { slot, data } = props ?? {};
    return settingsControllerUploadLogo(slot, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useSettingsControllerUploadLogo = (options, queryClient) => {
  const mutationOptions = getSettingsControllerUploadLogoMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getSettingsControllerRemoveLogoUrl = (slot) => {
  return `/api/settings/logo/${slot}`;
};
var settingsControllerRemoveLogo = async (slot, options) => {
  return customFetch(
    getSettingsControllerRemoveLogoUrl(slot),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getSettingsControllerRemoveLogoMutationOptions = (options) => {
  const mutationKey = ["settingsControllerRemoveLogo"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { slot } = props ?? {};
    return settingsControllerRemoveLogo(slot, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useSettingsControllerRemoveLogo = (options, queryClient) => {
  const mutationOptions = getSettingsControllerRemoveLogoMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getSettingsLogoControllerLogoUrl = (slot) => {
  return `/api/settings/logo/${slot}`;
};
var settingsLogoControllerLogo = async (slot, options) => {
  return customFetch(
    getSettingsLogoControllerLogoUrl(slot),
    {
      ...options,
      method: "GET"
    }
  );
};
var getSettingsLogoControllerLogoQueryKey = (slot) => {
  return [
    `/api/settings/logo/${slot}`
  ];
};
var getSettingsLogoControllerLogoQueryOptions = (slot, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getSettingsLogoControllerLogoQueryKey(slot);
  const queryFn = ({ signal }) => settingsLogoControllerLogo(slot, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!slot, ...queryOptions };
};
function useSettingsLogoControllerLogo(slot, options, queryClient) {
  const queryOptions = getSettingsLogoControllerLogoQueryOptions(slot, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDashboardControllerSummaryUrl = () => {
  return `/api/dashboard`;
};
var dashboardControllerSummary = async (options) => {
  return customFetch(
    getDashboardControllerSummaryUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDashboardControllerSummaryQueryKey = () => {
  return [
    `/api/dashboard`
  ];
};
var getDashboardControllerSummaryQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDashboardControllerSummaryQueryKey();
  const queryFn = ({ signal }) => dashboardControllerSummary({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useDashboardControllerSummary(options, queryClient) {
  const queryOptions = getDashboardControllerSummaryQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDashboardControllerHausmeisterWartelisteUrl = () => {
  return `/api/dashboard/hausmeister-warteliste`;
};
var dashboardControllerHausmeisterWarteliste = async (options) => {
  return customFetch(
    getDashboardControllerHausmeisterWartelisteUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDashboardControllerHausmeisterWartelisteQueryKey = () => {
  return [
    `/api/dashboard/hausmeister-warteliste`
  ];
};
var getDashboardControllerHausmeisterWartelisteQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDashboardControllerHausmeisterWartelisteQueryKey();
  const queryFn = ({ signal }) => dashboardControllerHausmeisterWarteliste({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useDashboardControllerHausmeisterWarteliste(options, queryClient) {
  const queryOptions = getDashboardControllerHausmeisterWartelisteQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDatevControllerPreviewUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/datev/preview?${stringifiedParams}` : `/api/datev/preview`;
};
var datevControllerPreview = async (params, options) => {
  return customFetch(
    getDatevControllerPreviewUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDatevControllerPreviewQueryKey = (params) => {
  return [
    `/api/datev/preview`,
    ...params ? [params] : []
  ];
};
var getDatevControllerPreviewQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDatevControllerPreviewQueryKey(params);
  const queryFn = ({ signal }) => datevControllerPreview(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useDatevControllerPreview(params, options, queryClient) {
  const queryOptions = getDatevControllerPreviewQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDatevControllerExportUrl = () => {
  return `/api/datev/export`;
};
var datevControllerExport = async (datevExportDto, options) => {
  return customFetch(
    getDatevControllerExportUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        datevExportDto
      )
    }
  );
};
var getDatevControllerExportMutationOptions = (options) => {
  const mutationKey = ["datevControllerExport"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return datevControllerExport(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDatevControllerExport = (options, queryClient) => {
  const mutationOptions = getDatevControllerExportMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDatevControllerRunsUrl = () => {
  return `/api/datev/runs`;
};
var datevControllerRuns = async (options) => {
  return customFetch(
    getDatevControllerRunsUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDatevControllerRunsQueryKey = () => {
  return [
    `/api/datev/runs`
  ];
};
var getDatevControllerRunsQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDatevControllerRunsQueryKey();
  const queryFn = ({ signal }) => datevControllerRuns({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useDatevControllerRuns(options, queryClient) {
  const queryOptions = getDatevControllerRunsQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDatevControllerDownloadUrl = (id, kind) => {
  return `/api/datev/runs/${id}/files/${kind}`;
};
var datevControllerDownload = async (id, kind, options) => {
  return customFetch(
    getDatevControllerDownloadUrl(id, kind),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDatevControllerDownloadQueryKey = (id, kind) => {
  return [
    `/api/datev/runs/${id}/files/${kind}`
  ];
};
var getDatevControllerDownloadQueryOptions = (id, kind, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDatevControllerDownloadQueryKey(id, kind);
  const queryFn = ({ signal }) => datevControllerDownload(id, kind, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!(id && kind), ...queryOptions };
};
function useDatevControllerDownload(id, kind, options, queryClient) {
  const queryOptions = getDatevControllerDownloadQueryOptions(id, kind, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDatevControllerAssignDebtorsUrl = () => {
  return `/api/datev/assign-debtors`;
};
var datevControllerAssignDebtors = async (assignDebtorsDto, options) => {
  return customFetch(
    getDatevControllerAssignDebtorsUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        assignDebtorsDto
      )
    }
  );
};
var getDatevControllerAssignDebtorsMutationOptions = (options) => {
  const mutationKey = ["datevControllerAssignDebtors"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return datevControllerAssignDebtors(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDatevControllerAssignDebtors = (options, queryClient) => {
  const mutationOptions = getDatevControllerAssignDebtorsMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDatanormControllerSuppliersUrl = () => {
  return `/api/import/datanorm/suppliers`;
};
var datanormControllerSuppliers = async (options) => {
  return customFetch(
    getDatanormControllerSuppliersUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDatanormControllerSuppliersQueryKey = () => {
  return [
    `/api/import/datanorm/suppliers`
  ];
};
var getDatanormControllerSuppliersQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDatanormControllerSuppliersQueryKey();
  const queryFn = ({ signal }) => datanormControllerSuppliers({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useDatanormControllerSuppliers(options, queryClient) {
  const queryOptions = getDatanormControllerSuppliersQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDatanormControllerDryRunUrl = () => {
  return `/api/import/datanorm/dry-run`;
};
var datanormControllerDryRun = async (datanormImportDto, options) => {
  return customFetch(
    getDatanormControllerDryRunUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        datanormImportDto
      )
    }
  );
};
var getDatanormControllerDryRunMutationOptions = (options) => {
  const mutationKey = ["datanormControllerDryRun"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return datanormControllerDryRun(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDatanormControllerDryRun = (options, queryClient) => {
  const mutationOptions = getDatanormControllerDryRunMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDatanormControllerCommitUrl = () => {
  return `/api/import/datanorm/commit`;
};
var datanormControllerCommit = async (datanormImportDto, options) => {
  return customFetch(
    getDatanormControllerCommitUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        datanormImportDto
      )
    }
  );
};
var getDatanormControllerCommitMutationOptions = (options) => {
  const mutationKey = ["datanormControllerCommit"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return datanormControllerCommit(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDatanormControllerCommit = (options, queryClient) => {
  const mutationOptions = getDatanormControllerCommitMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDatanormControllerErrorsCsvUrl = (batchId) => {
  return `/api/import/datanorm/${batchId}/errors.csv`;
};
var datanormControllerErrorsCsv = async (batchId, options) => {
  return customFetch(
    getDatanormControllerErrorsCsvUrl(batchId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDatanormControllerErrorsCsvQueryKey = (batchId) => {
  return [
    `/api/import/datanorm/${batchId}/errors.csv`
  ];
};
var getDatanormControllerErrorsCsvQueryOptions = (batchId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDatanormControllerErrorsCsvQueryKey(batchId);
  const queryFn = ({ signal }) => datanormControllerErrorsCsv(batchId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!batchId, ...queryOptions };
};
function useDatanormControllerErrorsCsv(batchId, options, queryClient) {
  const queryOptions = getDatanormControllerErrorsCsvQueryOptions(batchId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getLvImportControllerDryRunUrl = () => {
  return `/api/import/lv/dry-run`;
};
var lvImportControllerDryRun = async (lvImportDto, options) => {
  return customFetch(
    getLvImportControllerDryRunUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        lvImportDto
      )
    }
  );
};
var getLvImportControllerDryRunMutationOptions = (options) => {
  const mutationKey = ["lvImportControllerDryRun"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return lvImportControllerDryRun(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useLvImportControllerDryRun = (options, queryClient) => {
  const mutationOptions = getLvImportControllerDryRunMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getLvImportControllerCommitUrl = () => {
  return `/api/import/lv/commit`;
};
var lvImportControllerCommit = async (lvImportDto, options) => {
  return customFetch(
    getLvImportControllerCommitUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        lvImportDto
      )
    }
  );
};
var getLvImportControllerCommitMutationOptions = (options) => {
  const mutationKey = ["lvImportControllerCommit"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return lvImportControllerCommit(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useLvImportControllerCommit = (options, queryClient) => {
  const mutationOptions = getLvImportControllerCommitMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPortalOrderControllerPreviewUrl = () => {
  return `/api/import/portal-order/preview`;
};
var portalOrderControllerPreview = async (portalOrderUploadDto, options) => {
  return customFetch(
    getPortalOrderControllerPreviewUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        portalOrderUploadDto
      )
    }
  );
};
var getPortalOrderControllerPreviewMutationOptions = (options) => {
  const mutationKey = ["portalOrderControllerPreview"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return portalOrderControllerPreview(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePortalOrderControllerPreview = (options, queryClient) => {
  const mutationOptions = getPortalOrderControllerPreviewMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPortalOrderControllerCommitUrl = () => {
  return `/api/import/portal-order/commit`;
};
var portalOrderControllerCommit = async (portalOrderCommitDto, options) => {
  return customFetch(
    getPortalOrderControllerCommitUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        portalOrderCommitDto
      )
    }
  );
};
var getPortalOrderControllerCommitMutationOptions = (options) => {
  const mutationKey = ["portalOrderControllerCommit"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return portalOrderControllerCommit(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePortalOrderControllerCommit = (options, queryClient) => {
  const mutationOptions = getPortalOrderControllerCommitMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPortalOrderControllerBauzeitenplanUrl = () => {
  return `/api/import/portal-order/bauzeitenplan`;
};
var portalOrderControllerBauzeitenplan = async (portalOrderUploadDto, options) => {
  return customFetch(
    getPortalOrderControllerBauzeitenplanUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        portalOrderUploadDto
      )
    }
  );
};
var getPortalOrderControllerBauzeitenplanMutationOptions = (options) => {
  const mutationKey = ["portalOrderControllerBauzeitenplan"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return portalOrderControllerBauzeitenplan(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePortalOrderControllerBauzeitenplan = (options, queryClient) => {
  const mutationOptions = getPortalOrderControllerBauzeitenplanMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPortalPdfControllerVorschlagUrl = (orderId) => {
  return `/api/orders/${orderId}/portal-pdf/vorschlag`;
};
var portalPdfControllerVorschlag = async (orderId, options) => {
  return customFetch(
    getPortalPdfControllerVorschlagUrl(orderId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getPortalPdfControllerVorschlagQueryKey = (orderId) => {
  return [
    `/api/orders/${orderId}/portal-pdf/vorschlag`
  ];
};
var getPortalPdfControllerVorschlagQueryOptions = (orderId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getPortalPdfControllerVorschlagQueryKey(orderId);
  const queryFn = ({ signal }) => portalPdfControllerVorschlag(orderId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!orderId, ...queryOptions };
};
function usePortalPdfControllerVorschlag(orderId, options, queryClient) {
  const queryOptions = getPortalPdfControllerVorschlagQueryOptions(orderId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getPortalPdfControllerFuellenUrl = (orderId) => {
  return `/api/orders/${orderId}/portal-pdf`;
};
var portalPdfControllerFuellen = async (orderId, portalFillFieldsDto, options) => {
  return customFetch(
    getPortalPdfControllerFuellenUrl(orderId),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        portalFillFieldsDto
      )
    }
  );
};
var getPortalPdfControllerFuellenMutationOptions = (options) => {
  const mutationKey = ["portalPdfControllerFuellen"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { orderId, data } = props ?? {};
    return portalPdfControllerFuellen(orderId, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePortalPdfControllerFuellen = (options, queryClient) => {
  const mutationOptions = getPortalPdfControllerFuellenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getUsersControllerListUrl = () => {
  return `/api/users`;
};
var usersControllerList = async (options) => {
  return customFetch(
    getUsersControllerListUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getUsersControllerListQueryKey = () => {
  return [
    `/api/users`
  ];
};
var getUsersControllerListQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getUsersControllerListQueryKey();
  const queryFn = ({ signal }) => usersControllerList({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useUsersControllerList(options, queryClient) {
  const queryOptions = getUsersControllerListQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getUsersControllerCreateUrl = () => {
  return `/api/users`;
};
var usersControllerCreate = async (userCreateDto, options) => {
  return customFetch(
    getUsersControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        userCreateDto
      )
    }
  );
};
var getUsersControllerCreateMutationOptions = (options) => {
  const mutationKey = ["usersControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return usersControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useUsersControllerCreate = (options, queryClient) => {
  const mutationOptions = getUsersControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getUsersControllerUpdateUrl = (id) => {
  return `/api/users/${id}`;
};
var usersControllerUpdate = async (id, userUpdateDto, options) => {
  return customFetch(
    getUsersControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        userUpdateDto
      )
    }
  );
};
var getUsersControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["usersControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return usersControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useUsersControllerUpdate = (options, queryClient) => {
  const mutationOptions = getUsersControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getUsersControllerUpsertWorkScheduleUrl = (id) => {
  return `/api/users/${id}/work-schedule`;
};
var usersControllerUpsertWorkSchedule = async (id, workScheduleUpsertDto, options) => {
  return customFetch(
    getUsersControllerUpsertWorkScheduleUrl(id),
    {
      ...options,
      method: "PUT",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        workScheduleUpsertDto
      )
    }
  );
};
var getUsersControllerUpsertWorkScheduleMutationOptions = (options) => {
  const mutationKey = ["usersControllerUpsertWorkSchedule"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return usersControllerUpsertWorkSchedule(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useUsersControllerUpsertWorkSchedule = (options, queryClient) => {
  const mutationOptions = getUsersControllerUpsertWorkScheduleMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getLegacyArchiveControllerArchiveUrl = (id) => {
  return `/api/users/${id}/legacy-archive`;
};
var legacyArchiveControllerArchive = async (id, options) => {
  return customFetch(
    getLegacyArchiveControllerArchiveUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getLegacyArchiveControllerArchiveQueryKey = (id) => {
  return [
    `/api/users/${id}/legacy-archive`
  ];
};
var getLegacyArchiveControllerArchiveQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getLegacyArchiveControllerArchiveQueryKey(id);
  const queryFn = ({ signal }) => legacyArchiveControllerArchive(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useLegacyArchiveControllerArchive(id, options, queryClient) {
  const queryOptions = getLegacyArchiveControllerArchiveQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getLegacyArchiveControllerStartwerteProposalUrl = (id) => {
  return `/api/users/${id}/startwerte-proposal`;
};
var legacyArchiveControllerStartwerteProposal = async (id, options) => {
  return customFetch(
    getLegacyArchiveControllerStartwerteProposalUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getLegacyArchiveControllerStartwerteProposalQueryKey = (id) => {
  return [
    `/api/users/${id}/startwerte-proposal`
  ];
};
var getLegacyArchiveControllerStartwerteProposalQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getLegacyArchiveControllerStartwerteProposalQueryKey(id);
  const queryFn = ({ signal }) => legacyArchiveControllerStartwerteProposal(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useLegacyArchiveControllerStartwerteProposal(id, options, queryClient) {
  const queryOptions = getLegacyArchiveControllerStartwerteProposalQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getLegacyArchiveControllerStartwerteApplyUrl = (id) => {
  return `/api/users/${id}/startwerte-apply`;
};
var legacyArchiveControllerStartwerteApply = async (id, startwerteApplyDto, options) => {
  return customFetch(
    getLegacyArchiveControllerStartwerteApplyUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        startwerteApplyDto
      )
    }
  );
};
var getLegacyArchiveControllerStartwerteApplyMutationOptions = (options) => {
  const mutationKey = ["legacyArchiveControllerStartwerteApply"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return legacyArchiveControllerStartwerteApply(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useLegacyArchiveControllerStartwerteApply = (options, queryClient) => {
  const mutationOptions = getLegacyArchiveControllerStartwerteApplyMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechPairControllerPairUrl = () => {
  return `/api/tech/pair`;
};
var techPairControllerPair = async (techPairDto, options) => {
  return customFetch(
    getTechPairControllerPairUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techPairDto
      )
    }
  );
};
var getTechPairControllerPairMutationOptions = (options) => {
  const mutationKey = ["techPairControllerPair"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return techPairControllerPair(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechPairControllerPair = (options, queryClient) => {
  const mutationOptions = getTechPairControllerPairMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechPairControllerPairWithCodeUrl = () => {
  return `/api/tech/pair-with-code`;
};
var techPairControllerPairWithCode = async (techPairWithCodeDto, options) => {
  return customFetch(
    getTechPairControllerPairWithCodeUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techPairWithCodeDto
      )
    }
  );
};
var getTechPairControllerPairWithCodeMutationOptions = (options) => {
  const mutationKey = ["techPairControllerPairWithCode"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return techPairControllerPairWithCode(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechPairControllerPairWithCode = (options, queryClient) => {
  const mutationOptions = getTechPairControllerPairWithCodeMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechPairControllerReconnectInfoUrl = () => {
  return `/api/tech/reconnect-info`;
};
var techPairControllerReconnectInfo = async (techReconnectInfoDto, options) => {
  return customFetch(
    getTechPairControllerReconnectInfoUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techReconnectInfoDto
      )
    }
  );
};
var getTechPairControllerReconnectInfoMutationOptions = (options) => {
  const mutationKey = ["techPairControllerReconnectInfo"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return techPairControllerReconnectInfo(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechPairControllerReconnectInfo = (options, queryClient) => {
  const mutationOptions = getTechPairControllerReconnectInfoMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechPairControllerReconnectUrl = () => {
  return `/api/tech/reconnect`;
};
var techPairControllerReconnect = async (techReconnectDto, options) => {
  return customFetch(
    getTechPairControllerReconnectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techReconnectDto
      )
    }
  );
};
var getTechPairControllerReconnectMutationOptions = (options) => {
  const mutationKey = ["techPairControllerReconnect"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return techPairControllerReconnect(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechPairControllerReconnect = (options, queryClient) => {
  const mutationOptions = getTechPairControllerReconnectMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechControllerSyncPullUrl = () => {
  return `/api/tech/sync`;
};
var techControllerSyncPull = async (options) => {
  return customFetch(
    getTechControllerSyncPullUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerSyncPullQueryKey = () => {
  return [
    `/api/tech/sync`
  ];
};
var getTechControllerSyncPullQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerSyncPullQueryKey();
  const queryFn = ({ signal }) => techControllerSyncPull({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerSyncPull(options, queryClient) {
  const queryOptions = getTechControllerSyncPullQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerOffeneBestellungenUrl = () => {
  return `/api/tech/material/open`;
};
var techControllerOffeneBestellungen = async (options) => {
  return customFetch(
    getTechControllerOffeneBestellungenUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerOffeneBestellungenQueryKey = () => {
  return [
    `/api/tech/material/open`
  ];
};
var getTechControllerOffeneBestellungenQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerOffeneBestellungenQueryKey();
  const queryFn = ({ signal }) => techControllerOffeneBestellungen({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerOffeneBestellungen(options, queryClient) {
  const queryOptions = getTechControllerOffeneBestellungenQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerWareneingangUrl = (id) => {
  return `/api/tech/material/${id}/delivered`;
};
var techControllerWareneingang = async (id, techDeliveredDto, options) => {
  return customFetch(
    getTechControllerWareneingangUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techDeliveredDto
      )
    }
  );
};
var getTechControllerWareneingangMutationOptions = (options) => {
  const mutationKey = ["techControllerWareneingang"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return techControllerWareneingang(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechControllerWareneingang = (options, queryClient) => {
  const mutationOptions = getTechControllerWareneingangMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechControllerPushEventsUrl = () => {
  return `/api/tech/sync/events`;
};
var techControllerPushEvents = async (techEventsDto, options) => {
  return customFetch(
    getTechControllerPushEventsUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techEventsDto
      )
    }
  );
};
var getTechControllerPushEventsMutationOptions = (options) => {
  const mutationKey = ["techControllerPushEvents"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return techControllerPushEvents(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechControllerPushEvents = (options, queryClient) => {
  const mutationOptions = getTechControllerPushEventsMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechControllerCreateOrderUrl = () => {
  return `/api/tech/orders`;
};
var techControllerCreateOrder = async (techOrderCreateDto, options) => {
  return customFetch(
    getTechControllerCreateOrderUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techOrderCreateDto
      )
    }
  );
};
var getTechControllerCreateOrderMutationOptions = (options) => {
  const mutationKey = ["techControllerCreateOrder"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return techControllerCreateOrder(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechControllerCreateOrder = (options, queryClient) => {
  const mutationOptions = getTechControllerCreateOrderMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechControllerCatalogUrl = () => {
  return `/api/tech/catalog`;
};
var techControllerCatalog = async (options) => {
  return customFetch(
    getTechControllerCatalogUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerCatalogQueryKey = () => {
  return [
    `/api/tech/catalog`
  ];
};
var getTechControllerCatalogQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerCatalogQueryKey();
  const queryFn = ({ signal }) => techControllerCatalog({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerCatalog(options, queryClient) {
  const queryOptions = getTechControllerCatalogQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerCustomersUrl = () => {
  return `/api/tech/customers`;
};
var techControllerCustomers = async (options) => {
  return customFetch(
    getTechControllerCustomersUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerCustomersQueryKey = () => {
  return [
    `/api/tech/customers`
  ];
};
var getTechControllerCustomersQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerCustomersQueryKey();
  const queryFn = ({ signal }) => techControllerCustomers({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerCustomers(options, queryClient) {
  const queryOptions = getTechControllerCustomersQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerSearchRunUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/tech/search?${stringifiedParams}` : `/api/tech/search`;
};
var techControllerSearchRun = async (params, options) => {
  return customFetch(
    getTechControllerSearchRunUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerSearchRunQueryKey = (params) => {
  return [
    `/api/tech/search`,
    ...params ? [params] : []
  ];
};
var getTechControllerSearchRunQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerSearchRunQueryKey(params);
  const queryFn = ({ signal }) => techControllerSearchRun(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerSearchRun(params, options, queryClient) {
  const queryOptions = getTechControllerSearchRunQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerAttendanceStatusUrl = () => {
  return `/api/tech/attendance/status`;
};
var techControllerAttendanceStatus = async (options) => {
  return customFetch(
    getTechControllerAttendanceStatusUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerAttendanceStatusQueryKey = () => {
  return [
    `/api/tech/attendance/status`
  ];
};
var getTechControllerAttendanceStatusQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerAttendanceStatusQueryKey();
  const queryFn = ({ signal }) => techControllerAttendanceStatus({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerAttendanceStatus(options, queryClient) {
  const queryOptions = getTechControllerAttendanceStatusQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerTimeSummaryUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/tech/me/time-summary?${stringifiedParams}` : `/api/tech/me/time-summary`;
};
var techControllerTimeSummary = async (params, options) => {
  return customFetch(
    getTechControllerTimeSummaryUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerTimeSummaryQueryKey = (params) => {
  return [
    `/api/tech/me/time-summary`,
    ...params ? [params] : []
  ];
};
var getTechControllerTimeSummaryQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerTimeSummaryQueryKey(params);
  const queryFn = ({ signal }) => techControllerTimeSummary(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerTimeSummary(params, options, queryClient) {
  const queryOptions = getTechControllerTimeSummaryQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerDayStampsUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/tech/me/time-summary/day?${stringifiedParams}` : `/api/tech/me/time-summary/day`;
};
var techControllerDayStamps = async (params, options) => {
  return customFetch(
    getTechControllerDayStampsUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerDayStampsQueryKey = (params) => {
  return [
    `/api/tech/me/time-summary/day`,
    ...params ? [params] : []
  ];
};
var getTechControllerDayStampsQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerDayStampsQueryKey(params);
  const queryFn = ({ signal }) => techControllerDayStamps(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerDayStamps(params, options, queryClient) {
  const queryOptions = getTechControllerDayStampsQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerAbsenceRequestsUrl = () => {
  return `/api/tech/me/absence-requests`;
};
var techControllerAbsenceRequests = async (options) => {
  return customFetch(
    getTechControllerAbsenceRequestsUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerAbsenceRequestsQueryKey = () => {
  return [
    `/api/tech/me/absence-requests`
  ];
};
var getTechControllerAbsenceRequestsQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerAbsenceRequestsQueryKey();
  const queryFn = ({ signal }) => techControllerAbsenceRequests({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerAbsenceRequests(options, queryClient) {
  const queryOptions = getTechControllerAbsenceRequestsQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerCreateAbsenceRequestUrl = () => {
  return `/api/tech/me/absence-requests`;
};
var techControllerCreateAbsenceRequest = async (techAbsenceRequestCreateDto, options) => {
  return customFetch(
    getTechControllerCreateAbsenceRequestUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techAbsenceRequestCreateDto
      )
    }
  );
};
var getTechControllerCreateAbsenceRequestMutationOptions = (options) => {
  const mutationKey = ["techControllerCreateAbsenceRequest"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return techControllerCreateAbsenceRequest(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechControllerCreateAbsenceRequest = (options, queryClient) => {
  const mutationOptions = getTechControllerCreateAbsenceRequestMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechControllerEditAbsenceRequestUrl = (id) => {
  return `/api/tech/me/absence-requests/${id}`;
};
var techControllerEditAbsenceRequest = async (id, techAbsenceRequestEditDto, options) => {
  return customFetch(
    getTechControllerEditAbsenceRequestUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techAbsenceRequestEditDto
      )
    }
  );
};
var getTechControllerEditAbsenceRequestMutationOptions = (options) => {
  const mutationKey = ["techControllerEditAbsenceRequest"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return techControllerEditAbsenceRequest(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechControllerEditAbsenceRequest = (options, queryClient) => {
  const mutationOptions = getTechControllerEditAbsenceRequestMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechControllerCancelAbsenceRequestUrl = (id) => {
  return `/api/tech/me/absence-requests/${id}`;
};
var techControllerCancelAbsenceRequest = async (id, options) => {
  return customFetch(
    getTechControllerCancelAbsenceRequestUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getTechControllerCancelAbsenceRequestMutationOptions = (options) => {
  const mutationKey = ["techControllerCancelAbsenceRequest"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return techControllerCancelAbsenceRequest(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechControllerCancelAbsenceRequest = (options, queryClient) => {
  const mutationOptions = getTechControllerCancelAbsenceRequestMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechControllerLogsUrl = () => {
  return `/api/tech/logs`;
};
var techControllerLogs = async (techLogsDto, options) => {
  return customFetch(
    getTechControllerLogsUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techLogsDto
      )
    }
  );
};
var getTechControllerLogsMutationOptions = (options) => {
  const mutationKey = ["techControllerLogs"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return techControllerLogs(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechControllerLogs = (options, queryClient) => {
  const mutationOptions = getTechControllerLogsMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechControllerUploadUrl = () => {
  return `/api/tech/uploads`;
};
var techControllerUpload = async (techUploadDto, options) => {
  return customFetch(
    getTechControllerUploadUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techUploadDto
      )
    }
  );
};
var getTechControllerUploadMutationOptions = (options) => {
  const mutationKey = ["techControllerUpload"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return techControllerUpload(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechControllerUpload = (options, queryClient) => {
  const mutationOptions = getTechControllerUploadMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechControllerTeamKollegenUrl = () => {
  return `/api/tech/team/kollegen`;
};
var techControllerTeamKollegen = async (options) => {
  return customFetch(
    getTechControllerTeamKollegenUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerTeamKollegenQueryKey = () => {
  return [
    `/api/tech/team/kollegen`
  ];
};
var getTechControllerTeamKollegenQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerTeamKollegenQueryKey();
  const queryFn = ({ signal }) => techControllerTeamKollegen({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerTeamKollegen(options, queryClient) {
  const queryOptions = getTechControllerTeamKollegenQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerTeamWocheUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/tech/team/woche?${stringifiedParams}` : `/api/tech/team/woche`;
};
var techControllerTeamWoche = async (params, options) => {
  return customFetch(
    getTechControllerTeamWocheUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerTeamWocheQueryKey = (params) => {
  return [
    `/api/tech/team/woche`,
    ...params ? [params] : []
  ];
};
var getTechControllerTeamWocheQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerTeamWocheQueryKey(params);
  const queryFn = ({ signal }) => techControllerTeamWoche(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechControllerTeamWoche(params, options, queryClient) {
  const queryOptions = getTechControllerTeamWocheQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerTeamAuftragUrl = (orderId) => {
  return `/api/tech/team/auftrag/${orderId}`;
};
var techControllerTeamAuftrag = async (orderId, options) => {
  return customFetch(
    getTechControllerTeamAuftragUrl(orderId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerTeamAuftragQueryKey = (orderId) => {
  return [
    `/api/tech/team/auftrag/${orderId}`
  ];
};
var getTechControllerTeamAuftragQueryOptions = (orderId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerTeamAuftragQueryKey(orderId);
  const queryFn = ({ signal }) => techControllerTeamAuftrag(orderId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!orderId, ...queryOptions };
};
function useTechControllerTeamAuftrag(orderId, options, queryClient) {
  const queryOptions = getTechControllerTeamAuftragQueryOptions(orderId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerAuftragsFotosUrl = (orderId) => {
  return `/api/tech/auftrag/${orderId}/fotos`;
};
var techControllerAuftragsFotos = async (orderId, options) => {
  return customFetch(
    getTechControllerAuftragsFotosUrl(orderId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerAuftragsFotosQueryKey = (orderId) => {
  return [
    `/api/tech/auftrag/${orderId}/fotos`
  ];
};
var getTechControllerAuftragsFotosQueryOptions = (orderId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerAuftragsFotosQueryKey(orderId);
  const queryFn = ({ signal }) => techControllerAuftragsFotos(orderId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!orderId, ...queryOptions };
};
function useTechControllerAuftragsFotos(orderId, options, queryClient) {
  const queryOptions = getTechControllerAuftragsFotosQueryOptions(orderId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerAuftragsLeerwohnungUrl = (orderId) => {
  return `/api/tech/auftrag/${orderId}/leerwohnung`;
};
var techControllerAuftragsLeerwohnung = async (orderId, options) => {
  return customFetch(
    getTechControllerAuftragsLeerwohnungUrl(orderId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerAuftragsLeerwohnungQueryKey = (orderId) => {
  return [
    `/api/tech/auftrag/${orderId}/leerwohnung`
  ];
};
var getTechControllerAuftragsLeerwohnungQueryOptions = (orderId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerAuftragsLeerwohnungQueryKey(orderId);
  const queryFn = ({ signal }) => techControllerAuftragsLeerwohnung(orderId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!orderId, ...queryOptions };
};
function useTechControllerAuftragsLeerwohnung(orderId, options, queryClient) {
  const queryOptions = getTechControllerAuftragsLeerwohnungQueryOptions(orderId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerAuftragsFotoUrl = (orderId, fotoId) => {
  return `/api/tech/auftrag/${orderId}/foto/${fotoId}`;
};
var techControllerAuftragsFoto = async (orderId, fotoId, options) => {
  return customFetch(
    getTechControllerAuftragsFotoUrl(orderId, fotoId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerAuftragsFotoQueryKey = (orderId, fotoId) => {
  return [
    `/api/tech/auftrag/${orderId}/foto/${fotoId}`
  ];
};
var getTechControllerAuftragsFotoQueryOptions = (orderId, fotoId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerAuftragsFotoQueryKey(orderId, fotoId);
  const queryFn = ({ signal }) => techControllerAuftragsFoto(orderId, fotoId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!(orderId && fotoId), ...queryOptions };
};
function useTechControllerAuftragsFoto(orderId, fotoId, options, queryClient) {
  const queryOptions = getTechControllerAuftragsFotoQueryOptions(orderId, fotoId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerAuftragsAnhaengeUrl = (orderId) => {
  return `/api/tech/auftrag/${orderId}/anhaenge`;
};
var techControllerAuftragsAnhaenge = async (orderId, options) => {
  return customFetch(
    getTechControllerAuftragsAnhaengeUrl(orderId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerAuftragsAnhaengeQueryKey = (orderId) => {
  return [
    `/api/tech/auftrag/${orderId}/anhaenge`
  ];
};
var getTechControllerAuftragsAnhaengeQueryOptions = (orderId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerAuftragsAnhaengeQueryKey(orderId);
  const queryFn = ({ signal }) => techControllerAuftragsAnhaenge(orderId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!orderId, ...queryOptions };
};
function useTechControllerAuftragsAnhaenge(orderId, options, queryClient) {
  const queryOptions = getTechControllerAuftragsAnhaengeQueryOptions(orderId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerAuftragsAnhangUrl = (orderId, anhangId) => {
  return `/api/tech/auftrag/${orderId}/anhang/${anhangId}`;
};
var techControllerAuftragsAnhang = async (orderId, anhangId, options) => {
  return customFetch(
    getTechControllerAuftragsAnhangUrl(orderId, anhangId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerAuftragsAnhangQueryKey = (orderId, anhangId) => {
  return [
    `/api/tech/auftrag/${orderId}/anhang/${anhangId}`
  ];
};
var getTechControllerAuftragsAnhangQueryOptions = (orderId, anhangId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerAuftragsAnhangQueryKey(orderId, anhangId);
  const queryFn = ({ signal }) => techControllerAuftragsAnhang(orderId, anhangId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!(orderId && anhangId), ...queryOptions };
};
function useTechControllerAuftragsAnhang(orderId, anhangId, options, queryClient) {
  const queryOptions = getTechControllerAuftragsAnhangQueryOptions(orderId, anhangId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechControllerTeamFotoUrl = (uploadId) => {
  return `/api/tech/team/foto/${uploadId}`;
};
var techControllerTeamFoto = async (uploadId, options) => {
  return customFetch(
    getTechControllerTeamFotoUrl(uploadId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechControllerTeamFotoQueryKey = (uploadId) => {
  return [
    `/api/tech/team/foto/${uploadId}`
  ];
};
var getTechControllerTeamFotoQueryOptions = (uploadId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechControllerTeamFotoQueryKey(uploadId);
  const queryFn = ({ signal }) => techControllerTeamFoto(uploadId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!uploadId, ...queryOptions };
};
function useTechControllerTeamFoto(uploadId, options, queryClient) {
  const queryOptions = getTechControllerTeamFotoQueryOptions(uploadId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerClockUrl = () => {
  return `/api/attendance/clock`;
};
var attendanceControllerClock = async (clockDto, options) => {
  return customFetch(
    getAttendanceControllerClockUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        clockDto
      )
    }
  );
};
var getAttendanceControllerClockMutationOptions = (options) => {
  const mutationKey = ["attendanceControllerClock"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return attendanceControllerClock(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAttendanceControllerClock = (options, queryClient) => {
  const mutationOptions = getAttendanceControllerClockMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAttendanceControllerStatusUrl = () => {
  return `/api/attendance/status`;
};
var attendanceControllerStatus = async (options) => {
  return customFetch(
    getAttendanceControllerStatusUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAttendanceControllerStatusQueryKey = () => {
  return [
    `/api/attendance/status`
  ];
};
var getAttendanceControllerStatusQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAttendanceControllerStatusQueryKey();
  const queryFn = ({ signal }) => attendanceControllerStatus({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAttendanceControllerStatus(options, queryClient) {
  const queryOptions = getAttendanceControllerStatusQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerOrderWorkUrl = () => {
  return `/api/attendance/order-work`;
};
var attendanceControllerOrderWork = async (orderWorkDto, options) => {
  return customFetch(
    getAttendanceControllerOrderWorkUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderWorkDto
      )
    }
  );
};
var getAttendanceControllerOrderWorkMutationOptions = (options) => {
  const mutationKey = ["attendanceControllerOrderWork"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return attendanceControllerOrderWork(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAttendanceControllerOrderWork = (options, queryClient) => {
  const mutationOptions = getAttendanceControllerOrderWorkMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAttendanceControllerOrderWorkStatusUrl = () => {
  return `/api/attendance/order-work/status`;
};
var attendanceControllerOrderWorkStatus = async (options) => {
  return customFetch(
    getAttendanceControllerOrderWorkStatusUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAttendanceControllerOrderWorkStatusQueryKey = () => {
  return [
    `/api/attendance/order-work/status`
  ];
};
var getAttendanceControllerOrderWorkStatusQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAttendanceControllerOrderWorkStatusQueryKey();
  const queryFn = ({ signal }) => attendanceControllerOrderWorkStatus({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAttendanceControllerOrderWorkStatus(options, queryClient) {
  const queryOptions = getAttendanceControllerOrderWorkStatusQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerNowUrl = () => {
  return `/api/attendance/now`;
};
var attendanceControllerNow = async (options) => {
  return customFetch(
    getAttendanceControllerNowUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAttendanceControllerNowQueryKey = () => {
  return [
    `/api/attendance/now`
  ];
};
var getAttendanceControllerNowQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAttendanceControllerNowQueryKey();
  const queryFn = ({ signal }) => attendanceControllerNow({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAttendanceControllerNow(options, queryClient) {
  const queryOptions = getAttendanceControllerNowQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerMonthUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/attendance/month?${stringifiedParams}` : `/api/attendance/month`;
};
var attendanceControllerMonth = async (params, options) => {
  return customFetch(
    getAttendanceControllerMonthUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAttendanceControllerMonthQueryKey = (params) => {
  return [
    `/api/attendance/month`,
    ...params ? [params] : []
  ];
};
var getAttendanceControllerMonthQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAttendanceControllerMonthQueryKey(params);
  const queryFn = ({ signal }) => attendanceControllerMonth(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAttendanceControllerMonth(params, options, queryClient) {
  const queryOptions = getAttendanceControllerMonthQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerDayDetailUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/attendance/day-detail?${stringifiedParams}` : `/api/attendance/day-detail`;
};
var attendanceControllerDayDetail = async (params, options) => {
  return customFetch(
    getAttendanceControllerDayDetailUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAttendanceControllerDayDetailQueryKey = (params) => {
  return [
    `/api/attendance/day-detail`,
    ...params ? [params] : []
  ];
};
var getAttendanceControllerDayDetailQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAttendanceControllerDayDetailQueryKey(params);
  const queryFn = ({ signal }) => attendanceControllerDayDetail(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAttendanceControllerDayDetail(params, options, queryClient) {
  const queryOptions = getAttendanceControllerDayDetailQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerCorrectUrl = () => {
  return `/api/attendance/corrections`;
};
var attendanceControllerCorrect = async (correctionDto, options) => {
  return customFetch(
    getAttendanceControllerCorrectUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        correctionDto
      )
    }
  );
};
var getAttendanceControllerCorrectMutationOptions = (options) => {
  const mutationKey = ["attendanceControllerCorrect"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return attendanceControllerCorrect(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAttendanceControllerCorrect = (options, queryClient) => {
  const mutationOptions = getAttendanceControllerCorrectMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAttendanceControllerMonthCloseUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/attendance/month-close?${stringifiedParams}` : `/api/attendance/month-close`;
};
var attendanceControllerMonthClose = async (params, options) => {
  return customFetch(
    getAttendanceControllerMonthCloseUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAttendanceControllerMonthCloseQueryKey = (params) => {
  return [
    `/api/attendance/month-close`,
    ...params ? [params] : []
  ];
};
var getAttendanceControllerMonthCloseQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAttendanceControllerMonthCloseQueryKey(params);
  const queryFn = ({ signal }) => attendanceControllerMonthClose(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAttendanceControllerMonthClose(params, options, queryClient) {
  const queryOptions = getAttendanceControllerMonthCloseQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerCloseMonthUrl = () => {
  return `/api/attendance/month-close/close`;
};
var attendanceControllerCloseMonth = async (monthCloseActionDto, options) => {
  return customFetch(
    getAttendanceControllerCloseMonthUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        monthCloseActionDto
      )
    }
  );
};
var getAttendanceControllerCloseMonthMutationOptions = (options) => {
  const mutationKey = ["attendanceControllerCloseMonth"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return attendanceControllerCloseMonth(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAttendanceControllerCloseMonth = (options, queryClient) => {
  const mutationOptions = getAttendanceControllerCloseMonthMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAttendanceControllerReopenMonthUrl = () => {
  return `/api/attendance/month-close/reopen`;
};
var attendanceControllerReopenMonth = async (monthCloseActionDto, options) => {
  return customFetch(
    getAttendanceControllerReopenMonthUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        monthCloseActionDto
      )
    }
  );
};
var getAttendanceControllerReopenMonthMutationOptions = (options) => {
  const mutationKey = ["attendanceControllerReopenMonth"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return attendanceControllerReopenMonth(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAttendanceControllerReopenMonth = (options, queryClient) => {
  const mutationOptions = getAttendanceControllerReopenMonthMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAttendanceControllerCreateTimeAccountEntryUrl = () => {
  return `/api/attendance/time-account`;
};
var attendanceControllerCreateTimeAccountEntry = async (timeAccountEntryCreateDto, options) => {
  return customFetch(
    getAttendanceControllerCreateTimeAccountEntryUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        timeAccountEntryCreateDto
      )
    }
  );
};
var getAttendanceControllerCreateTimeAccountEntryMutationOptions = (options) => {
  const mutationKey = ["attendanceControllerCreateTimeAccountEntry"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return attendanceControllerCreateTimeAccountEntry(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAttendanceControllerCreateTimeAccountEntry = (options, queryClient) => {
  const mutationOptions = getAttendanceControllerCreateTimeAccountEntryMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAttendanceControllerListTimeAccountEntriesUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/attendance/time-account?${stringifiedParams}` : `/api/attendance/time-account`;
};
var attendanceControllerListTimeAccountEntries = async (params, options) => {
  return customFetch(
    getAttendanceControllerListTimeAccountEntriesUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAttendanceControllerListTimeAccountEntriesQueryKey = (params) => {
  return [
    `/api/attendance/time-account`,
    ...params ? [params] : []
  ];
};
var getAttendanceControllerListTimeAccountEntriesQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAttendanceControllerListTimeAccountEntriesQueryKey(params);
  const queryFn = ({ signal }) => attendanceControllerListTimeAccountEntries(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAttendanceControllerListTimeAccountEntries(params, options, queryClient) {
  const queryOptions = getAttendanceControllerListTimeAccountEntriesQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerCreateAbsencesUrl = () => {
  return `/api/attendance/absences`;
};
var attendanceControllerCreateAbsences = async (absenceCreateDto, options) => {
  return customFetch(
    getAttendanceControllerCreateAbsencesUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        absenceCreateDto
      )
    }
  );
};
var getAttendanceControllerCreateAbsencesMutationOptions = (options) => {
  const mutationKey = ["attendanceControllerCreateAbsences"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return attendanceControllerCreateAbsences(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAttendanceControllerCreateAbsences = (options, queryClient) => {
  const mutationOptions = getAttendanceControllerCreateAbsencesMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAttendanceControllerListAbsencesUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/attendance/absences?${stringifiedParams}` : `/api/attendance/absences`;
};
var attendanceControllerListAbsences = async (params, options) => {
  return customFetch(
    getAttendanceControllerListAbsencesUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAttendanceControllerListAbsencesQueryKey = (params) => {
  return [
    `/api/attendance/absences`,
    ...params ? [params] : []
  ];
};
var getAttendanceControllerListAbsencesQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAttendanceControllerListAbsencesQueryKey(params);
  const queryFn = ({ signal }) => attendanceControllerListAbsences(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAttendanceControllerListAbsences(params, options, queryClient) {
  const queryOptions = getAttendanceControllerListAbsencesQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerCreateAbsencesBulkUrl = () => {
  return `/api/attendance/absences/bulk`;
};
var attendanceControllerCreateAbsencesBulk = async (absenceBulkCreateDto, options) => {
  return customFetch(
    getAttendanceControllerCreateAbsencesBulkUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        absenceBulkCreateDto
      )
    }
  );
};
var getAttendanceControllerCreateAbsencesBulkMutationOptions = (options) => {
  const mutationKey = ["attendanceControllerCreateAbsencesBulk"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return attendanceControllerCreateAbsencesBulk(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAttendanceControllerCreateAbsencesBulk = (options, queryClient) => {
  const mutationOptions = getAttendanceControllerCreateAbsencesBulkMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAttendanceControllerDeleteAbsenceUrl = (id) => {
  return `/api/attendance/absences/${id}`;
};
var attendanceControllerDeleteAbsence = async (id, options) => {
  return customFetch(
    getAttendanceControllerDeleteAbsenceUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getAttendanceControllerDeleteAbsenceMutationOptions = (options) => {
  const mutationKey = ["attendanceControllerDeleteAbsence"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return attendanceControllerDeleteAbsence(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAttendanceControllerDeleteAbsence = (options, queryClient) => {
  const mutationOptions = getAttendanceControllerDeleteAbsenceMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAttendanceControllerLohnCsvUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/attendance/lohn-export/csv?${stringifiedParams}` : `/api/attendance/lohn-export/csv`;
};
var attendanceControllerLohnCsv = async (params, options) => {
  return customFetch(
    getAttendanceControllerLohnCsvUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAttendanceControllerLohnCsvQueryKey = (params) => {
  return [
    `/api/attendance/lohn-export/csv`,
    ...params ? [params] : []
  ];
};
var getAttendanceControllerLohnCsvQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAttendanceControllerLohnCsvQueryKey(params);
  const queryFn = ({ signal }) => attendanceControllerLohnCsv(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAttendanceControllerLohnCsv(params, options, queryClient) {
  const queryOptions = getAttendanceControllerLohnCsvQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerLohnStatusUrl = () => {
  return `/api/attendance/lohn-export/status`;
};
var attendanceControllerLohnStatus = async (options) => {
  return customFetch(
    getAttendanceControllerLohnStatusUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAttendanceControllerLohnStatusQueryKey = () => {
  return [
    `/api/attendance/lohn-export/status`
  ];
};
var getAttendanceControllerLohnStatusQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAttendanceControllerLohnStatusQueryKey();
  const queryFn = ({ signal }) => attendanceControllerLohnStatus({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAttendanceControllerLohnStatus(options, queryClient) {
  const queryOptions = getAttendanceControllerLohnStatusQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAttendanceControllerSendLohnExportUrl = () => {
  return `/api/attendance/lohn-export/send`;
};
var attendanceControllerSendLohnExport = async (monthCloseActionDto, options) => {
  return customFetch(
    getAttendanceControllerSendLohnExportUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        monthCloseActionDto
      )
    }
  );
};
var getAttendanceControllerSendLohnExportMutationOptions = (options) => {
  const mutationKey = ["attendanceControllerSendLohnExport"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return attendanceControllerSendLohnExport(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAttendanceControllerSendLohnExport = (options, queryClient) => {
  const mutationOptions = getAttendanceControllerSendLohnExportMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAbsenceRequestControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/absence-requests?${stringifiedParams}` : `/api/absence-requests`;
};
var absenceRequestControllerList = async (params, options) => {
  return customFetch(
    getAbsenceRequestControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAbsenceRequestControllerListQueryKey = (params) => {
  return [
    `/api/absence-requests`,
    ...params ? [params] : []
  ];
};
var getAbsenceRequestControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAbsenceRequestControllerListQueryKey(params);
  const queryFn = ({ signal }) => absenceRequestControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAbsenceRequestControllerList(params, options, queryClient) {
  const queryOptions = getAbsenceRequestControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAbsenceRequestControllerDecideUrl = (id) => {
  return `/api/absence-requests/${id}/decide`;
};
var absenceRequestControllerDecide = async (id, absenceRequestDecideDto, options) => {
  return customFetch(
    getAbsenceRequestControllerDecideUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        absenceRequestDecideDto
      )
    }
  );
};
var getAbsenceRequestControllerDecideMutationOptions = (options) => {
  const mutationKey = ["absenceRequestControllerDecide"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return absenceRequestControllerDecide(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAbsenceRequestControllerDecide = (options, queryClient) => {
  const mutationOptions = getAbsenceRequestControllerDecideMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAbsenceRequestControllerCorrectUrl = (id) => {
  return `/api/absence-requests/${id}/correct`;
};
var absenceRequestControllerCorrect = async (id, absenceRequestCorrectDto, options) => {
  return customFetch(
    getAbsenceRequestControllerCorrectUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        absenceRequestCorrectDto
      )
    }
  );
};
var getAbsenceRequestControllerCorrectMutationOptions = (options) => {
  const mutationKey = ["absenceRequestControllerCorrect"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return absenceRequestControllerCorrect(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useAbsenceRequestControllerCorrect = (options, queryClient) => {
  const mutationOptions = getAbsenceRequestControllerCorrectMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDevicesControllerListUrl = () => {
  return `/api/devices`;
};
var devicesControllerList = async (options) => {
  return customFetch(
    getDevicesControllerListUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDevicesControllerListQueryKey = () => {
  return [
    `/api/devices`
  ];
};
var getDevicesControllerListQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDevicesControllerListQueryKey();
  const queryFn = ({ signal }) => devicesControllerList({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useDevicesControllerList(options, queryClient) {
  const queryOptions = getDevicesControllerListQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDevicesControllerCreatePairingCodeUrl = () => {
  return `/api/devices/pairing-codes`;
};
var devicesControllerCreatePairingCode = async (pairingCodeCreateDto, options) => {
  return customFetch(
    getDevicesControllerCreatePairingCodeUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        pairingCodeCreateDto
      )
    }
  );
};
var getDevicesControllerCreatePairingCodeMutationOptions = (options) => {
  const mutationKey = ["devicesControllerCreatePairingCode"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return devicesControllerCreatePairingCode(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDevicesControllerCreatePairingCode = (options, queryClient) => {
  const mutationOptions = getDevicesControllerCreatePairingCodeMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDevicesControllerRevokeUrl = (id) => {
  return `/api/devices/${id}/revoke`;
};
var devicesControllerRevoke = async (id, options) => {
  return customFetch(
    getDevicesControllerRevokeUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getDevicesControllerRevokeMutationOptions = (options) => {
  const mutationKey = ["devicesControllerRevoke"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return devicesControllerRevoke(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDevicesControllerRevoke = (options, queryClient) => {
  const mutationOptions = getDevicesControllerRevokeMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDevicesControllerCleanupUrl = () => {
  return `/api/devices/revoked`;
};
var devicesControllerCleanup = async (options) => {
  return customFetch(
    getDevicesControllerCleanupUrl(),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getDevicesControllerCleanupMutationOptions = (options) => {
  const mutationKey = ["devicesControllerCleanup"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = () => {
    return devicesControllerCleanup(requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDevicesControllerCleanup = (options, queryClient) => {
  const mutationOptions = getDevicesControllerCleanupMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDevicesControllerDownloadLogUrl = (id) => {
  return `/api/devices/${id}/log`;
};
var devicesControllerDownloadLog = async (id, options) => {
  return customFetch(
    getDevicesControllerDownloadLogUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDevicesControllerDownloadLogQueryKey = (id) => {
  return [
    `/api/devices/${id}/log`
  ];
};
var getDevicesControllerDownloadLogQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDevicesControllerDownloadLogQueryKey(id);
  const queryFn = ({ signal }) => devicesControllerDownloadLog(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useDevicesControllerDownloadLog(id, options, queryClient) {
  const queryOptions = getDevicesControllerDownloadLogQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getPlantafelControllerWeekUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/plantafel?${stringifiedParams}` : `/api/plantafel`;
};
var plantafelControllerWeek = async (params, options) => {
  return customFetch(
    getPlantafelControllerWeekUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getPlantafelControllerWeekQueryKey = (params) => {
  return [
    `/api/plantafel`,
    ...params ? [params] : []
  ];
};
var getPlantafelControllerWeekQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getPlantafelControllerWeekQueryKey(params);
  const queryFn = ({ signal }) => plantafelControllerWeek(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function usePlantafelControllerWeek(params, options, queryClient) {
  const queryOptions = getPlantafelControllerWeekQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getPlantafelControllerCreateUrl = () => {
  return `/api/assignments`;
};
var plantafelControllerCreate = async (assignmentCreateDto, options) => {
  return customFetch(
    getPlantafelControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        assignmentCreateDto
      )
    }
  );
};
var getPlantafelControllerCreateMutationOptions = (options) => {
  const mutationKey = ["plantafelControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return plantafelControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePlantafelControllerCreate = (options, queryClient) => {
  const mutationOptions = getPlantafelControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPlantafelControllerMoveUrl = (id) => {
  return `/api/assignments/${id}`;
};
var plantafelControllerMove = async (id, assignmentMoveDto, options) => {
  return customFetch(
    getPlantafelControllerMoveUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        assignmentMoveDto
      )
    }
  );
};
var getPlantafelControllerMoveMutationOptions = (options) => {
  const mutationKey = ["plantafelControllerMove"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return plantafelControllerMove(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePlantafelControllerMove = (options, queryClient) => {
  const mutationOptions = getPlantafelControllerMoveMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPlantafelControllerRevokeUrl = (id) => {
  return `/api/assignments/${id}`;
};
var plantafelControllerRevoke = async (id, options) => {
  return customFetch(
    getPlantafelControllerRevokeUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getPlantafelControllerRevokeMutationOptions = (options) => {
  const mutationKey = ["plantafelControllerRevoke"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return plantafelControllerRevoke(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePlantafelControllerRevoke = (options, queryClient) => {
  const mutationOptions = getPlantafelControllerRevokeMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPlantafelControllerExtendUrl = (id) => {
  return `/api/assignments/${id}/extend`;
};
var plantafelControllerExtend = async (id, assignmentExtendDto, options) => {
  return customFetch(
    getPlantafelControllerExtendUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        assignmentExtendDto
      )
    }
  );
};
var getPlantafelControllerExtendMutationOptions = (options) => {
  const mutationKey = ["plantafelControllerExtend"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return plantafelControllerExtend(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePlantafelControllerExtend = (options, queryClient) => {
  const mutationOptions = getPlantafelControllerExtendMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPlantafelControllerCompleteUrl = (id) => {
  return `/api/orders/${id}/complete`;
};
var plantafelControllerComplete = async (id, adHocCompleteDto, options) => {
  return customFetch(
    getPlantafelControllerCompleteUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        adHocCompleteDto
      )
    }
  );
};
var getPlantafelControllerCompleteMutationOptions = (options) => {
  const mutationKey = ["plantafelControllerComplete"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return plantafelControllerComplete(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePlantafelControllerComplete = (options, queryClient) => {
  const mutationOptions = getPlantafelControllerCompleteMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPlantafelControllerCreateEntriesUrl = () => {
  return `/api/plantafel/entries`;
};
var plantafelControllerCreateEntries = async (planEntryCreateDto, options) => {
  return customFetch(
    getPlantafelControllerCreateEntriesUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        planEntryCreateDto
      )
    }
  );
};
var getPlantafelControllerCreateEntriesMutationOptions = (options) => {
  const mutationKey = ["plantafelControllerCreateEntries"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return plantafelControllerCreateEntries(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePlantafelControllerCreateEntries = (options, queryClient) => {
  const mutationOptions = getPlantafelControllerCreateEntriesMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPlantafelControllerEntryTitlesUrl = () => {
  return `/api/plantafel/entries/titles`;
};
var plantafelControllerEntryTitles = async (options) => {
  return customFetch(
    getPlantafelControllerEntryTitlesUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getPlantafelControllerEntryTitlesQueryKey = () => {
  return [
    `/api/plantafel/entries/titles`
  ];
};
var getPlantafelControllerEntryTitlesQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getPlantafelControllerEntryTitlesQueryKey();
  const queryFn = ({ signal }) => plantafelControllerEntryTitles({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function usePlantafelControllerEntryTitles(options, queryClient) {
  const queryOptions = getPlantafelControllerEntryTitlesQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getPlantafelControllerUpdateEntryUrl = (id) => {
  return `/api/plantafel/entries/${id}`;
};
var plantafelControllerUpdateEntry = async (id, planEntryUpdateDto, options) => {
  return customFetch(
    getPlantafelControllerUpdateEntryUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        planEntryUpdateDto
      )
    }
  );
};
var getPlantafelControllerUpdateEntryMutationOptions = (options) => {
  const mutationKey = ["plantafelControllerUpdateEntry"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return plantafelControllerUpdateEntry(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePlantafelControllerUpdateEntry = (options, queryClient) => {
  const mutationOptions = getPlantafelControllerUpdateEntryMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getPlantafelControllerDeleteEntryUrl = (id, params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/plantafel/entries/${id}?${stringifiedParams}` : `/api/plantafel/entries/${id}`;
};
var plantafelControllerDeleteEntry = async (id, params, options) => {
  return customFetch(
    getPlantafelControllerDeleteEntryUrl(id, params),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getPlantafelControllerDeleteEntryMutationOptions = (options) => {
  const mutationKey = ["plantafelControllerDeleteEntry"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, params } = props ?? {};
    return plantafelControllerDeleteEntry(id, params, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var usePlantafelControllerDeleteEntry = (options, queryClient) => {
  const mutationOptions = getPlantafelControllerDeleteEntryMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTechPlanungControllerVorratUrl = () => {
  return `/api/tech/planung/vorrat`;
};
var techPlanungControllerVorrat = async (options) => {
  return customFetch(
    getTechPlanungControllerVorratUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTechPlanungControllerVorratQueryKey = () => {
  return [
    `/api/tech/planung/vorrat`
  ];
};
var getTechPlanungControllerVorratQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTechPlanungControllerVorratQueryKey();
  const queryFn = ({ signal }) => techPlanungControllerVorrat({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTechPlanungControllerVorrat(options, queryClient) {
  const queryOptions = getTechPlanungControllerVorratQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTechPlanungControllerEinplanenUrl = () => {
  return `/api/tech/planung/einplanen`;
};
var techPlanungControllerEinplanen = async (techPlanungEinplanenDto, options) => {
  return customFetch(
    getTechPlanungControllerEinplanenUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        techPlanungEinplanenDto
      )
    }
  );
};
var getTechPlanungControllerEinplanenMutationOptions = (options) => {
  const mutationKey = ["techPlanungControllerEinplanen"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return techPlanungControllerEinplanen(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTechPlanungControllerEinplanen = (options, queryClient) => {
  const mutationOptions = getTechPlanungControllerEinplanenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getKalenderControllerStatusUrl = () => {
  return `/api/auth/me/kalender-abo`;
};
var kalenderControllerStatus = async (options) => {
  return customFetch(
    getKalenderControllerStatusUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getKalenderControllerStatusQueryKey = () => {
  return [
    `/api/auth/me/kalender-abo`
  ];
};
var getKalenderControllerStatusQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getKalenderControllerStatusQueryKey();
  const queryFn = ({ signal }) => kalenderControllerStatus({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useKalenderControllerStatus(options, queryClient) {
  const queryOptions = getKalenderControllerStatusQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getKalenderControllerErzeugenUrl = () => {
  return `/api/auth/me/kalender-abo`;
};
var kalenderControllerErzeugen = async (options) => {
  return customFetch(
    getKalenderControllerErzeugenUrl(),
    {
      ...options,
      method: "POST"
    }
  );
};
var getKalenderControllerErzeugenMutationOptions = (options) => {
  const mutationKey = ["kalenderControllerErzeugen"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = () => {
    return kalenderControllerErzeugen(requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useKalenderControllerErzeugen = (options, queryClient) => {
  const mutationOptions = getKalenderControllerErzeugenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getKalenderControllerBeendenUrl = () => {
  return `/api/auth/me/kalender-abo`;
};
var kalenderControllerBeenden = async (options) => {
  return customFetch(
    getKalenderControllerBeendenUrl(),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getKalenderControllerBeendenMutationOptions = (options) => {
  const mutationKey = ["kalenderControllerBeenden"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = () => {
    return kalenderControllerBeenden(requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useKalenderControllerBeenden = (options, queryClient) => {
  const mutationOptions = getKalenderControllerBeendenMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getKalenderControllerFeedUrl = (datei) => {
  return `/api/kalender/${datei}`;
};
var kalenderControllerFeed = async (datei, options) => {
  return customFetch(
    getKalenderControllerFeedUrl(datei),
    {
      ...options,
      method: "GET"
    }
  );
};
var getKalenderControllerFeedQueryKey = (datei) => {
  return [
    `/api/kalender/${datei}`
  ];
};
var getKalenderControllerFeedQueryOptions = (datei, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getKalenderControllerFeedQueryKey(datei);
  const queryFn = ({ signal }) => kalenderControllerFeed(datei, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!datei, ...queryOptions };
};
function useKalenderControllerFeed(datei, options, queryClient) {
  const queryOptions = getKalenderControllerFeedQueryOptions(datei, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getBillingControllerBillUrl = (id) => {
  return `/api/orders/${id}/bill`;
};
var billingControllerBill = async (id, billDto, options) => {
  return customFetch(
    getBillingControllerBillUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        billDto
      )
    }
  );
};
var getBillingControllerBillMutationOptions = (options) => {
  const mutationKey = ["billingControllerBill"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return billingControllerBill(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useBillingControllerBill = (options, queryClient) => {
  const mutationOptions = getBillingControllerBillMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getBillingControllerSetTravelBillingUrl = (id) => {
  return `/api/customers/${id}/travel-billing`;
};
var billingControllerSetTravelBilling = async (id, travelBillingUpdateDto, options) => {
  return customFetch(
    getBillingControllerSetTravelBillingUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        travelBillingUpdateDto
      )
    }
  );
};
var getBillingControllerSetTravelBillingMutationOptions = (options) => {
  const mutationKey = ["billingControllerSetTravelBilling"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return billingControllerSetTravelBilling(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useBillingControllerSetTravelBilling = (options, queryClient) => {
  const mutationOptions = getBillingControllerSetTravelBillingMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getRoutingControllerPlzVorschlagUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/routing/plz-vorschlag?${stringifiedParams}` : `/api/routing/plz-vorschlag`;
};
var routingControllerPlzVorschlag = async (params, options) => {
  return customFetch(
    getRoutingControllerPlzVorschlagUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getRoutingControllerPlzVorschlagQueryKey = (params) => {
  return [
    `/api/routing/plz-vorschlag`,
    ...params ? [params] : []
  ];
};
var getRoutingControllerPlzVorschlagQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getRoutingControllerPlzVorschlagQueryKey(params);
  const queryFn = ({ signal }) => routingControllerPlzVorschlag(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useRoutingControllerPlzVorschlag(params, options, queryClient) {
  const queryOptions = getRoutingControllerPlzVorschlagQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getRoutingControllerDocumentDistanceUrl = (id) => {
  return `/api/routing/document/${id}/distance`;
};
var routingControllerDocumentDistance = async (id, options) => {
  return customFetch(
    getRoutingControllerDocumentDistanceUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getRoutingControllerDocumentDistanceQueryKey = (id) => {
  return [
    `/api/routing/document/${id}/distance`
  ];
};
var getRoutingControllerDocumentDistanceQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getRoutingControllerDocumentDistanceQueryKey(id);
  const queryFn = ({ signal }) => routingControllerDocumentDistance(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useRoutingControllerDocumentDistance(id, options, queryClient) {
  const queryOptions = getRoutingControllerDocumentDistanceQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getRoutingControllerDistanceUrl = (id) => {
  return `/api/routing/property/${id}/distance`;
};
var routingControllerDistance = async (id, options) => {
  return customFetch(
    getRoutingControllerDistanceUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getRoutingControllerDistanceMutationOptions = (options) => {
  const mutationKey = ["routingControllerDistance"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return routingControllerDistance(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useRoutingControllerDistance = (options, queryClient) => {
  const mutationOptions = getRoutingControllerDistanceMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getRoutingControllerCustomerDistanceUrl = (id) => {
  return `/api/routing/customer/${id}/distance`;
};
var routingControllerCustomerDistance = async (id, options) => {
  return customFetch(
    getRoutingControllerCustomerDistanceUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getRoutingControllerCustomerDistanceMutationOptions = (options) => {
  const mutationKey = ["routingControllerCustomerDistance"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return routingControllerCustomerDistance(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useRoutingControllerCustomerDistance = (options, queryClient) => {
  const mutationOptions = getRoutingControllerCustomerDistanceMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getRoutingControllerStartEntfernungslaufUrl = () => {
  return `/api/routing/entfernungslauf`;
};
var routingControllerStartEntfernungslauf = async (entfernungslaufStartDto, options) => {
  return customFetch(
    getRoutingControllerStartEntfernungslaufUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        entfernungslaufStartDto
      )
    }
  );
};
var getRoutingControllerStartEntfernungslaufMutationOptions = (options) => {
  const mutationKey = ["routingControllerStartEntfernungslauf"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return routingControllerStartEntfernungslauf(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useRoutingControllerStartEntfernungslauf = (options, queryClient) => {
  const mutationOptions = getRoutingControllerStartEntfernungslaufMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getRoutingControllerEntfernungslaufStatusUrl = () => {
  return `/api/routing/entfernungslauf/status`;
};
var routingControllerEntfernungslaufStatus = async (options) => {
  return customFetch(
    getRoutingControllerEntfernungslaufStatusUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getRoutingControllerEntfernungslaufStatusQueryKey = () => {
  return [
    `/api/routing/entfernungslauf/status`
  ];
};
var getRoutingControllerEntfernungslaufStatusQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getRoutingControllerEntfernungslaufStatusQueryKey();
  const queryFn = ({ signal }) => routingControllerEntfernungslaufStatus({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useRoutingControllerEntfernungslaufStatus(options, queryClient) {
  const queryOptions = getRoutingControllerEntfernungslaufStatusQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDunningControllerProposeUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/dunning/proposal?${stringifiedParams}` : `/api/dunning/proposal`;
};
var dunningControllerPropose = async (params, options) => {
  return customFetch(
    getDunningControllerProposeUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDunningControllerProposeQueryKey = (params) => {
  return [
    `/api/dunning/proposal`,
    ...params ? [params] : []
  ];
};
var getDunningControllerProposeQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDunningControllerProposeQueryKey(params);
  const queryFn = ({ signal }) => dunningControllerPropose(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useDunningControllerPropose(params, options, queryClient) {
  const queryOptions = getDunningControllerProposeQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getDunningControllerRunUrl = () => {
  return `/api/dunning/run`;
};
var dunningControllerRun = async (dunningRunDto, options) => {
  return customFetch(
    getDunningControllerRunUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        dunningRunDto
      )
    }
  );
};
var getDunningControllerRunMutationOptions = (options) => {
  const mutationKey = ["dunningControllerRun"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return dunningControllerRun(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useDunningControllerRun = (options, queryClient) => {
  const mutationOptions = getDunningControllerRunMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getDunningControllerListUrl = () => {
  return `/api/dunning`;
};
var dunningControllerList = async (options) => {
  return customFetch(
    getDunningControllerListUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getDunningControllerListQueryKey = () => {
  return [
    `/api/dunning`
  ];
};
var getDunningControllerListQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getDunningControllerListQueryKey();
  const queryFn = ({ signal }) => dunningControllerList({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useDunningControllerList(options, queryClient) {
  const queryOptions = getDunningControllerListQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getFollowupControllerBoardUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/quotes/board?${stringifiedParams}` : `/api/quotes/board`;
};
var followupControllerBoard = async (params, options) => {
  return customFetch(
    getFollowupControllerBoardUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getFollowupControllerBoardQueryKey = (params) => {
  return [
    `/api/quotes/board`,
    ...params ? [params] : []
  ];
};
var getFollowupControllerBoardQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getFollowupControllerBoardQueryKey(params);
  const queryFn = ({ signal }) => followupControllerBoard(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useFollowupControllerBoard(params, options, queryClient) {
  const queryOptions = getFollowupControllerBoardQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getFollowupControllerFollowUpUrl = (id) => {
  return `/api/quotes/${id}/follow-up`;
};
var followupControllerFollowUp = async (id, quoteFollowUpDto, options) => {
  return customFetch(
    getFollowupControllerFollowUpUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        quoteFollowUpDto
      )
    }
  );
};
var getFollowupControllerFollowUpMutationOptions = (options) => {
  const mutationKey = ["followupControllerFollowUp"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return followupControllerFollowUp(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useFollowupControllerFollowUp = (options, queryClient) => {
  const mutationOptions = getFollowupControllerFollowUpMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getFollowupControllerWonUrl = (id) => {
  return `/api/quotes/${id}/won`;
};
var followupControllerWon = async (id, options) => {
  return customFetch(
    getFollowupControllerWonUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getFollowupControllerWonMutationOptions = (options) => {
  const mutationKey = ["followupControllerWon"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return followupControllerWon(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useFollowupControllerWon = (options, queryClient) => {
  const mutationOptions = getFollowupControllerWonMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getFollowupControllerLostUrl = (id) => {
  return `/api/quotes/${id}/lost`;
};
var followupControllerLost = async (id, quoteLostDto, options) => {
  return customFetch(
    getFollowupControllerLostUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        quoteLostDto
      )
    }
  );
};
var getFollowupControllerLostMutationOptions = (options) => {
  const mutationKey = ["followupControllerLost"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return followupControllerLost(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useFollowupControllerLost = (options, queryClient) => {
  const mutationOptions = getFollowupControllerLostMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getRecurringControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/recurring-templates?${stringifiedParams}` : `/api/recurring-templates`;
};
var recurringControllerList = async (params, options) => {
  return customFetch(
    getRecurringControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getRecurringControllerListQueryKey = (params) => {
  return [
    `/api/recurring-templates`,
    ...params ? [params] : []
  ];
};
var getRecurringControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getRecurringControllerListQueryKey(params);
  const queryFn = ({ signal }) => recurringControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useRecurringControllerList(params, options, queryClient) {
  const queryOptions = getRecurringControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getRecurringControllerCreateUrl = () => {
  return `/api/recurring-templates`;
};
var recurringControllerCreate = async (recurringCreateDto, options) => {
  return customFetch(
    getRecurringControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        recurringCreateDto
      )
    }
  );
};
var getRecurringControllerCreateMutationOptions = (options) => {
  const mutationKey = ["recurringControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return recurringControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useRecurringControllerCreate = (options, queryClient) => {
  const mutationOptions = getRecurringControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getRecurringControllerUpdateUrl = (id) => {
  return `/api/recurring-templates/${id}`;
};
var recurringControllerUpdate = async (id, recurringUpdateDto, options) => {
  return customFetch(
    getRecurringControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        recurringUpdateDto
      )
    }
  );
};
var getRecurringControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["recurringControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return recurringControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useRecurringControllerUpdate = (options, queryClient) => {
  const mutationOptions = getRecurringControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getRecurringControllerRemoveUrl = (id) => {
  return `/api/recurring-templates/${id}`;
};
var recurringControllerRemove = async (id, options) => {
  return customFetch(
    getRecurringControllerRemoveUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getRecurringControllerRemoveMutationOptions = (options) => {
  const mutationKey = ["recurringControllerRemove"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return recurringControllerRemove(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useRecurringControllerRemove = (options, queryClient) => {
  const mutationOptions = getRecurringControllerRemoveMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getRecurringControllerRunUrl = () => {
  return `/api/recurring-templates/run`;
};
var recurringControllerRun = async (recurringRunDto, options) => {
  return customFetch(
    getRecurringControllerRunUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        recurringRunDto
      )
    }
  );
};
var getRecurringControllerRunMutationOptions = (options) => {
  const mutationKey = ["recurringControllerRun"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return recurringControllerRun(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useRecurringControllerRun = (options, queryClient) => {
  const mutationOptions = getRecurringControllerRunMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getAnalyticsControllerRevenueUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/revenue?${stringifiedParams}` : `/api/analytics/revenue`;
};
var analyticsControllerRevenue = async (params, options) => {
  return customFetch(
    getAnalyticsControllerRevenueUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerRevenueQueryKey = (params) => {
  return [
    `/api/analytics/revenue`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerRevenueQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerRevenueQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerRevenue(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerRevenue(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerRevenueQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerRevenueCsvUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/revenue/csv?${stringifiedParams}` : `/api/analytics/revenue/csv`;
};
var analyticsControllerRevenueCsv = async (params, options) => {
  return customFetch(
    getAnalyticsControllerRevenueCsvUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerRevenueCsvQueryKey = (params) => {
  return [
    `/api/analytics/revenue/csv`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerRevenueCsvQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerRevenueCsvQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerRevenueCsv(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerRevenueCsv(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerRevenueCsvQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerOpenItemsUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/open-items?${stringifiedParams}` : `/api/analytics/open-items`;
};
var analyticsControllerOpenItems = async (params, options) => {
  return customFetch(
    getAnalyticsControllerOpenItemsUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerOpenItemsQueryKey = (params) => {
  return [
    `/api/analytics/open-items`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerOpenItemsQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerOpenItemsQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerOpenItems(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerOpenItems(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerOpenItemsQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerOpenItemsCsvUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/open-items/csv?${stringifiedParams}` : `/api/analytics/open-items/csv`;
};
var analyticsControllerOpenItemsCsv = async (params, options) => {
  return customFetch(
    getAnalyticsControllerOpenItemsCsvUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerOpenItemsCsvQueryKey = (params) => {
  return [
    `/api/analytics/open-items/csv`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerOpenItemsCsvQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerOpenItemsCsvQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerOpenItemsCsv(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerOpenItemsCsv(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerOpenItemsCsvQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerUnbilledUrl = () => {
  return `/api/analytics/unbilled`;
};
var analyticsControllerUnbilled = async (options) => {
  return customFetch(
    getAnalyticsControllerUnbilledUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerUnbilledQueryKey = () => {
  return [
    `/api/analytics/unbilled`
  ];
};
var getAnalyticsControllerUnbilledQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerUnbilledQueryKey();
  const queryFn = ({ signal }) => analyticsControllerUnbilled({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerUnbilled(options, queryClient) {
  const queryOptions = getAnalyticsControllerUnbilledQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerUnbilledCsvUrl = () => {
  return `/api/analytics/unbilled/csv`;
};
var analyticsControllerUnbilledCsv = async (options) => {
  return customFetch(
    getAnalyticsControllerUnbilledCsvUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerUnbilledCsvQueryKey = () => {
  return [
    `/api/analytics/unbilled/csv`
  ];
};
var getAnalyticsControllerUnbilledCsvQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerUnbilledCsvQueryKey();
  const queryFn = ({ signal }) => analyticsControllerUnbilledCsv({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerUnbilledCsv(options, queryClient) {
  const queryOptions = getAnalyticsControllerUnbilledCsvQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerHoursUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/hours?${stringifiedParams}` : `/api/analytics/hours`;
};
var analyticsControllerHours = async (params, options) => {
  return customFetch(
    getAnalyticsControllerHoursUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerHoursQueryKey = (params) => {
  return [
    `/api/analytics/hours`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerHoursQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerHoursQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerHours(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerHours(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerHoursQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerHoursCsvUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/hours/csv?${stringifiedParams}` : `/api/analytics/hours/csv`;
};
var analyticsControllerHoursCsv = async (params, options) => {
  return customFetch(
    getAnalyticsControllerHoursCsvUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerHoursCsvQueryKey = (params) => {
  return [
    `/api/analytics/hours/csv`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerHoursCsvQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerHoursCsvQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerHoursCsv(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerHoursCsv(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerHoursCsvQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerQuotesUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/quotes?${stringifiedParams}` : `/api/analytics/quotes`;
};
var analyticsControllerQuotes = async (params, options) => {
  return customFetch(
    getAnalyticsControllerQuotesUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerQuotesQueryKey = (params) => {
  return [
    `/api/analytics/quotes`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerQuotesQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerQuotesQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerQuotes(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerQuotes(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerQuotesQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerQuotesCsvUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/quotes/csv?${stringifiedParams}` : `/api/analytics/quotes/csv`;
};
var analyticsControllerQuotesCsv = async (params, options) => {
  return customFetch(
    getAnalyticsControllerQuotesCsvUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerQuotesCsvQueryKey = (params) => {
  return [
    `/api/analytics/quotes/csv`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerQuotesCsvQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerQuotesCsvQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerQuotesCsv(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerQuotesCsv(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerQuotesCsvQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerEmergencyUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/emergency?${stringifiedParams}` : `/api/analytics/emergency`;
};
var analyticsControllerEmergency = async (params, options) => {
  return customFetch(
    getAnalyticsControllerEmergencyUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerEmergencyQueryKey = (params) => {
  return [
    `/api/analytics/emergency`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerEmergencyQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerEmergencyQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerEmergency(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerEmergency(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerEmergencyQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerEmergencyCsvUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/emergency/csv?${stringifiedParams}` : `/api/analytics/emergency/csv`;
};
var analyticsControllerEmergencyCsv = async (params, options) => {
  return customFetch(
    getAnalyticsControllerEmergencyCsvUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerEmergencyCsvQueryKey = (params) => {
  return [
    `/api/analytics/emergency/csv`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerEmergencyCsvQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerEmergencyCsvQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerEmergencyCsv(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerEmergencyCsv(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerEmergencyCsvQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerOrderHoursUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/order-hours?${stringifiedParams}` : `/api/analytics/order-hours`;
};
var analyticsControllerOrderHours = async (params, options) => {
  return customFetch(
    getAnalyticsControllerOrderHoursUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerOrderHoursQueryKey = (params) => {
  return [
    `/api/analytics/order-hours`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerOrderHoursQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerOrderHoursQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerOrderHours(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerOrderHours(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerOrderHoursQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getAnalyticsControllerOrderHoursCsvUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/analytics/order-hours/csv?${stringifiedParams}` : `/api/analytics/order-hours/csv`;
};
var analyticsControllerOrderHoursCsv = async (params, options) => {
  return customFetch(
    getAnalyticsControllerOrderHoursCsvUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getAnalyticsControllerOrderHoursCsvQueryKey = (params) => {
  return [
    `/api/analytics/order-hours/csv`,
    ...params ? [params] : []
  ];
};
var getAnalyticsControllerOrderHoursCsvQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getAnalyticsControllerOrderHoursCsvQueryKey(params);
  const queryFn = ({ signal }) => analyticsControllerOrderHoursCsv(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useAnalyticsControllerOrderHoursCsv(params, options, queryClient) {
  const queryOptions = getAnalyticsControllerOrderHoursCsvQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getActivityControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/activity?${stringifiedParams}` : `/api/activity`;
};
var activityControllerList = async (params, options) => {
  return customFetch(
    getActivityControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getActivityControllerListQueryKey = (params) => {
  return [
    `/api/activity`,
    ...params ? [params] : []
  ];
};
var getActivityControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getActivityControllerListQueryKey(params);
  const queryFn = ({ signal }) => activityControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useActivityControllerList(params, options, queryClient) {
  const queryOptions = getActivityControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMeasurementControllerListItemsUrl = (orderId) => {
  return `/api/orders/${orderId}/items`;
};
var measurementControllerListItems = async (orderId, options) => {
  return customFetch(
    getMeasurementControllerListItemsUrl(orderId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getMeasurementControllerListItemsQueryKey = (orderId) => {
  return [
    `/api/orders/${orderId}/items`
  ];
};
var getMeasurementControllerListItemsQueryOptions = (orderId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getMeasurementControllerListItemsQueryKey(orderId);
  const queryFn = ({ signal }) => measurementControllerListItems(orderId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!orderId, ...queryOptions };
};
function useMeasurementControllerListItems(orderId, options, queryClient) {
  const queryOptions = getMeasurementControllerListItemsQueryOptions(orderId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMeasurementControllerCreateItemUrl = (orderId) => {
  return `/api/orders/${orderId}/items`;
};
var measurementControllerCreateItem = async (orderId, orderItemCreateDto, options) => {
  return customFetch(
    getMeasurementControllerCreateItemUrl(orderId),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderItemCreateDto
      )
    }
  );
};
var getMeasurementControllerCreateItemMutationOptions = (options) => {
  const mutationKey = ["measurementControllerCreateItem"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { orderId, data } = props ?? {};
    return measurementControllerCreateItem(orderId, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerCreateItem = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerCreateItemMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerItemsFromWorkReportUrl = (orderId) => {
  return `/api/orders/${orderId}/items/from-work-report`;
};
var measurementControllerItemsFromWorkReport = async (orderId, itemsFromWorkReportDto, options) => {
  return customFetch(
    getMeasurementControllerItemsFromWorkReportUrl(orderId),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        itemsFromWorkReportDto
      )
    }
  );
};
var getMeasurementControllerItemsFromWorkReportMutationOptions = (options) => {
  const mutationKey = ["measurementControllerItemsFromWorkReport"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { orderId, data } = props ?? {};
    return measurementControllerItemsFromWorkReport(orderId, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerItemsFromWorkReport = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerItemsFromWorkReportMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerUpdateItemUrl = (id) => {
  return `/api/order-items/${id}`;
};
var measurementControllerUpdateItem = async (id, orderItemUpdateDto, options) => {
  return customFetch(
    getMeasurementControllerUpdateItemUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderItemUpdateDto
      )
    }
  );
};
var getMeasurementControllerUpdateItemMutationOptions = (options) => {
  const mutationKey = ["measurementControllerUpdateItem"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return measurementControllerUpdateItem(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerUpdateItem = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerUpdateItemMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerDeleteItemUrl = (id) => {
  return `/api/order-items/${id}`;
};
var measurementControllerDeleteItem = async (id, options) => {
  return customFetch(
    getMeasurementControllerDeleteItemUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMeasurementControllerDeleteItemMutationOptions = (options) => {
  const mutationKey = ["measurementControllerDeleteItem"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return measurementControllerDeleteItem(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerDeleteItem = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerDeleteItemMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerReorderItemsUrl = (orderId) => {
  return `/api/orders/${orderId}/items/reorder`;
};
var measurementControllerReorderItems = async (orderId, orderItemsReorderDto, options) => {
  return customFetch(
    getMeasurementControllerReorderItemsUrl(orderId),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderItemsReorderDto
      )
    }
  );
};
var getMeasurementControllerReorderItemsMutationOptions = (options) => {
  const mutationKey = ["measurementControllerReorderItems"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { orderId, data } = props ?? {};
    return measurementControllerReorderItems(orderId, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerReorderItems = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerReorderItemsMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerAddItemAttachmentUrl = (id) => {
  return `/api/order-items/${id}/attachments`;
};
var measurementControllerAddItemAttachment = async (id, orderItemAttachmentCreateDto, options) => {
  return customFetch(
    getMeasurementControllerAddItemAttachmentUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderItemAttachmentCreateDto
      )
    }
  );
};
var getMeasurementControllerAddItemAttachmentMutationOptions = (options) => {
  const mutationKey = ["measurementControllerAddItemAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return measurementControllerAddItemAttachment(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerAddItemAttachment = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerAddItemAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerUpdateItemAttachmentUrl = (attachmentId) => {
  return `/api/order-item-attachments/${attachmentId}`;
};
var measurementControllerUpdateItemAttachment = async (attachmentId, orderItemAttachmentUpdateDto, options) => {
  return customFetch(
    getMeasurementControllerUpdateItemAttachmentUrl(attachmentId),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        orderItemAttachmentUpdateDto
      )
    }
  );
};
var getMeasurementControllerUpdateItemAttachmentMutationOptions = (options) => {
  const mutationKey = ["measurementControllerUpdateItemAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { attachmentId, data } = props ?? {};
    return measurementControllerUpdateItemAttachment(attachmentId, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerUpdateItemAttachment = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerUpdateItemAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerRemoveItemAttachmentUrl = (attachmentId) => {
  return `/api/order-item-attachments/${attachmentId}`;
};
var measurementControllerRemoveItemAttachment = async (attachmentId, options) => {
  return customFetch(
    getMeasurementControllerRemoveItemAttachmentUrl(attachmentId),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMeasurementControllerRemoveItemAttachmentMutationOptions = (options) => {
  const mutationKey = ["measurementControllerRemoveItemAttachment"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { attachmentId } = props ?? {};
    return measurementControllerRemoveItemAttachment(attachmentId, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerRemoveItemAttachment = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerRemoveItemAttachmentMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerItemAttachmentFileUrl = (attachmentId) => {
  return `/api/order-item-attachments/${attachmentId}/file`;
};
var measurementControllerItemAttachmentFile = async (attachmentId, options) => {
  return customFetch(
    getMeasurementControllerItemAttachmentFileUrl(attachmentId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getMeasurementControllerItemAttachmentFileQueryKey = (attachmentId) => {
  return [
    `/api/order-item-attachments/${attachmentId}/file`
  ];
};
var getMeasurementControllerItemAttachmentFileQueryOptions = (attachmentId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getMeasurementControllerItemAttachmentFileQueryKey(attachmentId);
  const queryFn = ({ signal }) => measurementControllerItemAttachmentFile(attachmentId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!attachmentId, ...queryOptions };
};
function useMeasurementControllerItemAttachmentFile(attachmentId, options, queryClient) {
  const queryOptions = getMeasurementControllerItemAttachmentFileQueryOptions(attachmentId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMeasurementControllerListSheetsUrl = (orderId) => {
  return `/api/orders/${orderId}/measurement-sheets`;
};
var measurementControllerListSheets = async (orderId, options) => {
  return customFetch(
    getMeasurementControllerListSheetsUrl(orderId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getMeasurementControllerListSheetsQueryKey = (orderId) => {
  return [
    `/api/orders/${orderId}/measurement-sheets`
  ];
};
var getMeasurementControllerListSheetsQueryOptions = (orderId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getMeasurementControllerListSheetsQueryKey(orderId);
  const queryFn = ({ signal }) => measurementControllerListSheets(orderId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!orderId, ...queryOptions };
};
function useMeasurementControllerListSheets(orderId, options, queryClient) {
  const queryOptions = getMeasurementControllerListSheetsQueryOptions(orderId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMeasurementControllerCreateSheetUrl = (orderId) => {
  return `/api/orders/${orderId}/measurement-sheets`;
};
var measurementControllerCreateSheet = async (orderId, sheetCreateDto, options) => {
  return customFetch(
    getMeasurementControllerCreateSheetUrl(orderId),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        sheetCreateDto
      )
    }
  );
};
var getMeasurementControllerCreateSheetMutationOptions = (options) => {
  const mutationKey = ["measurementControllerCreateSheet"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { orderId, data } = props ?? {};
    return measurementControllerCreateSheet(orderId, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerCreateSheet = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerCreateSheetMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerGetSheetUrl = (id) => {
  return `/api/measurement-sheets/${id}`;
};
var measurementControllerGetSheet = async (id, options) => {
  return customFetch(
    getMeasurementControllerGetSheetUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getMeasurementControllerGetSheetQueryKey = (id) => {
  return [
    `/api/measurement-sheets/${id}`
  ];
};
var getMeasurementControllerGetSheetQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getMeasurementControllerGetSheetQueryKey(id);
  const queryFn = ({ signal }) => measurementControllerGetSheet(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useMeasurementControllerGetSheet(id, options, queryClient) {
  const queryOptions = getMeasurementControllerGetSheetQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMeasurementControllerUpdateSheetUrl = (id) => {
  return `/api/measurement-sheets/${id}`;
};
var measurementControllerUpdateSheet = async (id, sheetUpdateDto, options) => {
  return customFetch(
    getMeasurementControllerUpdateSheetUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        sheetUpdateDto
      )
    }
  );
};
var getMeasurementControllerUpdateSheetMutationOptions = (options) => {
  const mutationKey = ["measurementControllerUpdateSheet"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return measurementControllerUpdateSheet(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerUpdateSheet = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerUpdateSheetMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerDeleteSheetUrl = (id) => {
  return `/api/measurement-sheets/${id}`;
};
var measurementControllerDeleteSheet = async (id, options) => {
  return customFetch(
    getMeasurementControllerDeleteSheetUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMeasurementControllerDeleteSheetMutationOptions = (options) => {
  const mutationKey = ["measurementControllerDeleteSheet"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return measurementControllerDeleteSheet(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerDeleteSheet = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerDeleteSheetMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerSheetPdfUrl = (id) => {
  return `/api/measurement-sheets/${id}/pdf`;
};
var measurementControllerSheetPdf = async (id, options) => {
  return customFetch(
    getMeasurementControllerSheetPdfUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getMeasurementControllerSheetPdfQueryKey = (id) => {
  return [
    `/api/measurement-sheets/${id}/pdf`
  ];
};
var getMeasurementControllerSheetPdfQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getMeasurementControllerSheetPdfQueryKey(id);
  const queryFn = ({ signal }) => measurementControllerSheetPdf(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useMeasurementControllerSheetPdf(id, options, queryClient) {
  const queryOptions = getMeasurementControllerSheetPdfQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getMeasurementControllerCreateRowUrl = (id) => {
  return `/api/measurement-sheets/${id}/rows`;
};
var measurementControllerCreateRow = async (id, rowCreateDto, options) => {
  return customFetch(
    getMeasurementControllerCreateRowUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        rowCreateDto
      )
    }
  );
};
var getMeasurementControllerCreateRowMutationOptions = (options) => {
  const mutationKey = ["measurementControllerCreateRow"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return measurementControllerCreateRow(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerCreateRow = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerCreateRowMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerUpdateRowUrl = (id) => {
  return `/api/measurement-rows/${id}`;
};
var measurementControllerUpdateRow = async (id, rowUpdateDto, options) => {
  return customFetch(
    getMeasurementControllerUpdateRowUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        rowUpdateDto
      )
    }
  );
};
var getMeasurementControllerUpdateRowMutationOptions = (options) => {
  const mutationKey = ["measurementControllerUpdateRow"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return measurementControllerUpdateRow(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerUpdateRow = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerUpdateRowMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerDeleteRowUrl = (id) => {
  return `/api/measurement-rows/${id}`;
};
var measurementControllerDeleteRow = async (id, options) => {
  return customFetch(
    getMeasurementControllerDeleteRowUrl(id),
    {
      ...options,
      method: "DELETE"
    }
  );
};
var getMeasurementControllerDeleteRowMutationOptions = (options) => {
  const mutationKey = ["measurementControllerDeleteRow"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return measurementControllerDeleteRow(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerDeleteRow = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerDeleteRowMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getMeasurementControllerTransferUrl = (id) => {
  return `/api/measurement-sheets/${id}/transfer`;
};
var measurementControllerTransfer = async (id, options) => {
  return customFetch(
    getMeasurementControllerTransferUrl(id),
    {
      ...options,
      method: "POST"
    }
  );
};
var getMeasurementControllerTransferMutationOptions = (options) => {
  const mutationKey = ["measurementControllerTransfer"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id } = props ?? {};
    return measurementControllerTransfer(id, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useMeasurementControllerTransfer = (options, queryClient) => {
  const mutationOptions = getMeasurementControllerTransferMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getCollectiveControllerBillableOrdersUrl = (customerId) => {
  return `/api/customers/${customerId}/billable-orders`;
};
var collectiveControllerBillableOrders = async (customerId, options) => {
  return customFetch(
    getCollectiveControllerBillableOrdersUrl(customerId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getCollectiveControllerBillableOrdersQueryKey = (customerId) => {
  return [
    `/api/customers/${customerId}/billable-orders`
  ];
};
var getCollectiveControllerBillableOrdersQueryOptions = (customerId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getCollectiveControllerBillableOrdersQueryKey(customerId);
  const queryFn = ({ signal }) => collectiveControllerBillableOrders(customerId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!customerId, ...queryOptions };
};
function useCollectiveControllerBillableOrders(customerId, options, queryClient) {
  const queryOptions = getCollectiveControllerBillableOrdersQueryOptions(customerId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getCollectiveControllerCreateUrl = () => {
  return `/api/collective-invoices`;
};
var collectiveControllerCreate = async (collectiveCreateDto, options) => {
  return customFetch(
    getCollectiveControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        collectiveCreateDto
      )
    }
  );
};
var getCollectiveControllerCreateMutationOptions = (options) => {
  const mutationKey = ["collectiveControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return collectiveControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useCollectiveControllerCreate = (options, queryClient) => {
  const mutationOptions = getCollectiveControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTicketsControllerListUrl = (params) => {
  const normalizedParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== void 0) {
      normalizedParams.append(key, value === null ? "null" : value.toString());
    }
  });
  const stringifiedParams = normalizedParams.toString();
  return stringifiedParams.length > 0 ? `/api/tickets?${stringifiedParams}` : `/api/tickets`;
};
var ticketsControllerList = async (params, options) => {
  return customFetch(
    getTicketsControllerListUrl(params),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTicketsControllerListQueryKey = (params) => {
  return [
    `/api/tickets`,
    ...params ? [params] : []
  ];
};
var getTicketsControllerListQueryOptions = (params, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTicketsControllerListQueryKey(params);
  const queryFn = ({ signal }) => ticketsControllerList(params, { signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTicketsControllerList(params, options, queryClient) {
  const queryOptions = getTicketsControllerListQueryOptions(params, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTicketsControllerCreateUrl = () => {
  return `/api/tickets`;
};
var ticketsControllerCreate = async (ticketCreateDto, options) => {
  return customFetch(
    getTicketsControllerCreateUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        ticketCreateDto
      )
    }
  );
};
var getTicketsControllerCreateMutationOptions = (options) => {
  const mutationKey = ["ticketsControllerCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { data } = props ?? {};
    return ticketsControllerCreate(data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTicketsControllerCreate = (options, queryClient) => {
  const mutationOptions = getTicketsControllerCreateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTicketsControllerUnreadUrl = () => {
  return `/api/tickets/unread`;
};
var ticketsControllerUnread = async (options) => {
  return customFetch(
    getTicketsControllerUnreadUrl(),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTicketsControllerUnreadQueryKey = () => {
  return [
    `/api/tickets/unread`
  ];
};
var getTicketsControllerUnreadQueryOptions = (options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTicketsControllerUnreadQueryKey();
  const queryFn = ({ signal }) => ticketsControllerUnread({ signal, ...requestOptions });
  return { queryKey, queryFn, ...queryOptions };
};
function useTicketsControllerUnread(options, queryClient) {
  const queryOptions = getTicketsControllerUnreadQueryOptions(options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTicketsControllerDetailUrl = (id) => {
  return `/api/tickets/${id}`;
};
var ticketsControllerDetail = async (id, options) => {
  return customFetch(
    getTicketsControllerDetailUrl(id),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTicketsControllerDetailQueryKey = (id) => {
  return [
    `/api/tickets/${id}`
  ];
};
var getTicketsControllerDetailQueryOptions = (id, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTicketsControllerDetailQueryKey(id);
  const queryFn = ({ signal }) => ticketsControllerDetail(id, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};
function useTicketsControllerDetail(id, options, queryClient) {
  const queryOptions = getTicketsControllerDetailQueryOptions(id, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}
var getTicketsControllerUpdateUrl = (id) => {
  return `/api/tickets/${id}`;
};
var ticketsControllerUpdate = async (id, ticketUpdateDto, options) => {
  return customFetch(
    getTicketsControllerUpdateUrl(id),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        ticketUpdateDto
      )
    }
  );
};
var getTicketsControllerUpdateMutationOptions = (options) => {
  const mutationKey = ["ticketsControllerUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return ticketsControllerUpdate(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTicketsControllerUpdate = (options, queryClient) => {
  const mutationOptions = getTicketsControllerUpdateMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTicketsControllerReplyUrl = (id) => {
  return `/api/tickets/${id}/messages`;
};
var ticketsControllerReply = async (id, ticketReplyDto, options) => {
  return customFetch(
    getTicketsControllerReplyUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(
        ticketReplyDto
      )
    }
  );
};
var getTicketsControllerReplyMutationOptions = (options) => {
  const mutationKey = ["ticketsControllerReply"];
  const { mutation: mutationOptions, request: requestOptions } = options ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey ? options : { ...options, mutation: { ...options.mutation, mutationKey } } : { mutation: { mutationKey }, request: void 0 };
  const mutationFn = (props) => {
    const { id, data } = props ?? {};
    return ticketsControllerReply(id, data, requestOptions);
  };
  return { mutationFn, ...mutationOptions };
};
var useTicketsControllerReply = (options, queryClient) => {
  const mutationOptions = getTicketsControllerReplyMutationOptions(options);
  return useMutation(mutationOptions, queryClient);
};
var getTicketsControllerFileUrl = (attachmentId) => {
  return `/api/tickets/attachments/${attachmentId}/file`;
};
var ticketsControllerFile = async (attachmentId, options) => {
  return customFetch(
    getTicketsControllerFileUrl(attachmentId),
    {
      ...options,
      method: "GET"
    }
  );
};
var getTicketsControllerFileQueryKey = (attachmentId) => {
  return [
    `/api/tickets/attachments/${attachmentId}/file`
  ];
};
var getTicketsControllerFileQueryOptions = (attachmentId, options) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getTicketsControllerFileQueryKey(attachmentId);
  const queryFn = ({ signal }) => ticketsControllerFile(attachmentId, { signal, ...requestOptions });
  return { queryKey, queryFn, enabled: !!attachmentId, ...queryOptions };
};
function useTicketsControllerFile(attachmentId, options, queryClient) {
  const queryOptions = getTicketsControllerFileQueryOptions(attachmentId, options);
  const query = useQuery(queryOptions, queryClient);
  query.queryKey = queryOptions.queryKey;
  return query;
}

// src/client/model/absenceBulkCreateDtoKind.ts
var AbsenceBulkCreateDtoKind = {
  VACATION: "VACATION",
  SICK: "SICK",
  KIND_KRANK: "KIND_KRANK",
  BETRIEBSURLAUB: "BETRIEBSURLAUB",
  SONDERURLAUB: "SONDERURLAUB",
  UNBEZAHLT: "UNBEZAHLT",
  BESCHAEFTIGUNGSVERBOT: "BESCHAEFTIGUNGSVERBOT",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/absenceCreateDtoKind.ts
var AbsenceCreateDtoKind = {
  VACATION: "VACATION",
  SICK: "SICK",
  KIND_KRANK: "KIND_KRANK",
  BETRIEBSURLAUB: "BETRIEBSURLAUB",
  SONDERURLAUB: "SONDERURLAUB",
  UNBEZAHLT: "UNBEZAHLT",
  BESCHAEFTIGUNGSVERBOT: "BESCHAEFTIGUNGSVERBOT",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/absenceRequestControllerListStatus.ts
var AbsenceRequestControllerListStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  REPORTED: "REPORTED",
  CANCELLED: "CANCELLED"
};

// src/client/model/absenceRequestDecideDtoDecision.ts
var AbsenceRequestDecideDtoDecision = {
  APPROVED: "APPROVED",
  REJECTED: "REJECTED"
};

// src/client/model/absenceRequestListResponseDtoOutputItemsItemStatus.ts
var AbsenceRequestListResponseDtoOutputItemsItemStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  REPORTED: "REPORTED",
  CANCELLED: "CANCELLED"
};

// src/client/model/absenceRequestListResponseDtoOutputItemsItemType.ts
var AbsenceRequestListResponseDtoOutputItemsItemType = {
  VACATION: "VACATION",
  SICK: "SICK",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH"
};

// src/client/model/absenceResponseDtoOutputKind.ts
var AbsenceResponseDtoOutputKind = {
  VACATION: "VACATION",
  SICK: "SICK",
  KIND_KRANK: "KIND_KRANK",
  BETRIEBSURLAUB: "BETRIEBSURLAUB",
  SONDERURLAUB: "SONDERURLAUB",
  UNBEZAHLT: "UNBEZAHLT",
  BESCHAEFTIGUNGSVERBOT: "BESCHAEFTIGUNGSVERBOT",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/activityControllerListType.ts
var ActivityControllerListType = {
  ORDER: "ORDER",
  ADHOC: "ADHOC",
  WORK_REPORT: "WORK_REPORT",
  ABSENCE: "ABSENCE"
};

// src/client/model/activityResponseDtoOutputEntriesItemCategory.ts
var ActivityResponseDtoOutputEntriesItemCategory = {
  ORDER: "ORDER",
  ADHOC: "ADHOC",
  WORK_REPORT: "WORK_REPORT",
  ABSENCE: "ABSENCE"
};

// src/client/model/activityResponseDtoOutputEntriesItemKind.ts
var ActivityResponseDtoOutputEntriesItemKind = {
  WORK_START: "WORK_START",
  STOP: "STOP",
  PAUSE_START: "PAUSE_START",
  PAUSE_END: "PAUSE_END",
  TECH_DONE: "TECH_DONE",
  PROBLEM_REPORTED: "PROBLEM_REPORTED",
  PROBLEM_RESOLVED: "PROBLEM_RESOLVED",
  ADHOC_CREATED: "ADHOC_CREATED",
  WORK_REPORT_CREATED: "WORK_REPORT_CREATED",
  ABSENCE_REQUESTED: "ABSENCE_REQUESTED",
  ABSENCE_REPORTED: "ABSENCE_REPORTED",
  ABSENCE_APPROVED: "ABSENCE_APPROVED",
  ABSENCE_REJECTED: "ABSENCE_REJECTED",
  ABSENCE_CANCELLED: "ABSENCE_CANCELLED"
};

// src/client/model/analyticsControllerOrderHoursCsvIncludeLegacy.ts
var AnalyticsControllerOrderHoursCsvIncludeLegacy = {
  NUMBER_0: "0",
  NUMBER_1: "1",
  true: "true",
  false: "false"
};

// src/client/model/analyticsControllerOrderHoursIncludeLegacy.ts
var AnalyticsControllerOrderHoursIncludeLegacy = {
  NUMBER_0: "0",
  NUMBER_1: "1",
  true: "true",
  false: "false"
};

// src/client/model/articleCreateDtoDefaultTaxCode.ts
var ArticleCreateDtoDefaultTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/articleCreateDtoItemKind.ts
var ArticleCreateDtoItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/articleCreateDtoKind.ts
var ArticleCreateDtoKind = {
  MATERIAL: "MATERIAL",
  SERVICE: "SERVICE"
};

// src/client/model/articleResponseDtoOutputDefaultTaxCode.ts
var ArticleResponseDtoOutputDefaultTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/articleResponseDtoOutputItemKind.ts
var ArticleResponseDtoOutputItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/articleResponseDtoOutputKind.ts
var ArticleResponseDtoOutputKind = {
  MATERIAL: "MATERIAL",
  SERVICE: "SERVICE"
};

// src/client/model/articleUpdateDtoDefaultTaxCode.ts
var ArticleUpdateDtoDefaultTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/articleUpdateDtoItemKind.ts
var ArticleUpdateDtoItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/articleUpdateDtoKind.ts
var ArticleUpdateDtoKind = {
  MATERIAL: "MATERIAL",
  SERVICE: "SERVICE"
};

// src/client/model/articlesControllerListArchived.ts
var ArticlesControllerListArchived = {
  true: "true",
  false: "false",
  all: "all"
};

// src/client/model/articlesControllerListKind.ts
var ArticlesControllerListKind = {
  MATERIAL: "MATERIAL",
  SERVICE: "SERVICE"
};

// src/client/model/assignmentResponseDtoOutputAusgang.ts
var AssignmentResponseDtoOutputAusgang = {
  ERFOLGREICH: "ERFOLGREICH",
  NICHT_ABGESCHLOSSEN: "NICHT_ABGESCHLOSSEN"
};

// src/client/model/assignmentResponseDtoOutputLiveStatusKind.ts
var AssignmentResponseDtoOutputLiveStatusKind = {
  planned: "planned",
  running: "running",
  paused: "paused",
  done: "done"
};

// src/client/model/assignmentResponseDtoOutputOrderHoldReason.ts
var AssignmentResponseDtoOutputOrderHoldReason = {
  MATERIAL_FEHLT: "MATERIAL_FEHLT",
  MIETER_NICHT_ERREICHBAR: "MIETER_NICHT_ERREICHBAR",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/attendanceNowResponseDtoOutputConflictsItemKind.ts
var AttendanceNowResponseDtoOutputConflictsItemKind = {
  MISSING_OUT: "MISSING_OUT",
  OPEN_BREAK: "OPEN_BREAK"
};

// src/client/model/attendanceNowResponseDtoOutputNowItemState.ts
var AttendanceNowResponseDtoOutputNowItemState = {
  PRESENT: "PRESENT",
  BREAK: "BREAK"
};

// src/client/model/bankImportPruefungDtoOutputVorschauItemArt.ts
var BankImportPruefungDtoOutputVorschauItemArt = {
  EINDEUTIG: "EINDEUTIG",
  EINDEUTIG_SKONTO: "EINDEUTIG_SKONTO",
  KEINE_NUMMER: "KEINE_NUMMER",
  UNBEKANNTE_NUMMER: "UNBEKANNTE_NUMMER",
  MEHRERE_RECHNUNGEN: "MEHRERE_RECHNUNGEN",
  BETRAG_ZU_NIEDRIG: "BETRAG_ZU_NIEDRIG",
  BETRAG_ZU_HOCH: "BETRAG_ZU_HOCH"
};

// src/client/model/bankTransaktionDtoOutputStatus.ts
var BankTransaktionDtoOutputStatus = {
  OPEN: "OPEN",
  AUTO_BOOKED: "AUTO_BOOKED",
  BOOKED: "BOOKED",
  IGNORED: "IGNORED"
};

// src/client/model/businessAreasControllerListArchived.ts
var BusinessAreasControllerListArchived = {
  true: "true",
  false: "false",
  all: "all"
};

// src/client/model/clockDtoKind.ts
var ClockDtoKind = {
  IN: "IN",
  OUT: "OUT",
  BREAK_START: "BREAK_START",
  BREAK_END: "BREAK_END"
};

// src/client/model/clockStatusResponseDtoOutputState.ts
var ClockStatusResponseDtoOutputState = {
  OUT: "OUT",
  PRESENT: "PRESENT",
  BREAK: "BREAK"
};

// src/client/model/contactCreateDtoRole.ts
var ContactCreateDtoRole = {
  CARETAKER: "CARETAKER",
  PROPERTY_MGMT: "PROPERTY_MGMT",
  TENANT: "TENANT",
  BILLING: "BILLING",
  OTHER: "OTHER"
};

// src/client/model/contactResponseDtoOutputRole.ts
var ContactResponseDtoOutputRole = {
  CARETAKER: "CARETAKER",
  PROPERTY_MGMT: "PROPERTY_MGMT",
  TENANT: "TENANT",
  BILLING: "BILLING",
  OTHER: "OTHER"
};

// src/client/model/contactUpdateDtoRole.ts
var ContactUpdateDtoRole = {
  CARETAKER: "CARETAKER",
  PROPERTY_MGMT: "PROPERTY_MGMT",
  TENANT: "TENANT",
  BILLING: "BILLING",
  OTHER: "OTHER"
};

// src/client/model/customerCreateDtoInvoiceChannel.ts
var CustomerCreateDtoInvoiceChannel = {
  XRECHNUNG_EMAIL: "XRECHNUNG_EMAIL",
  ZUGFERD_EMAIL: "ZUGFERD_EMAIL",
  PDF_EMAIL: "PDF_EMAIL",
  PORTAL_MANUAL: "PORTAL_MANUAL"
};

// src/client/model/customerCreateDtoKind.ts
var CustomerCreateDtoKind = {
  COMPANY: "COMPANY",
  PRIVATE: "PRIVATE"
};

// src/client/model/customerFileResponseDtoOutputContactsItemRole.ts
var CustomerFileResponseDtoOutputContactsItemRole = {
  CARETAKER: "CARETAKER",
  PROPERTY_MGMT: "PROPERTY_MGMT",
  TENANT: "TENANT",
  BILLING: "BILLING",
  OTHER: "OTHER"
};

// src/client/model/customerFileResponseDtoOutputInvoiceChannel.ts
var CustomerFileResponseDtoOutputInvoiceChannel = {
  XRECHNUNG_EMAIL: "XRECHNUNG_EMAIL",
  ZUGFERD_EMAIL: "ZUGFERD_EMAIL",
  PDF_EMAIL: "PDF_EMAIL",
  PORTAL_MANUAL: "PORTAL_MANUAL"
};

// src/client/model/customerFileResponseDtoOutputKind.ts
var CustomerFileResponseDtoOutputKind = {
  COMPANY: "COMPANY",
  PRIVATE: "PRIVATE"
};

// src/client/model/customerFileResponseDtoOutputMaintenanceContractsItemStatus.ts
var CustomerFileResponseDtoOutputMaintenanceContractsItemStatus = {
  ACTIVE: "ACTIVE",
  ENDED: "ENDED"
};

// src/client/model/customerFileResponseDtoOutputPriceListsItemItemsItemItemKind.ts
var CustomerFileResponseDtoOutputPriceListsItemItemsItemItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/customerFileResponseDtoOutputPriceListsItemItemsItemTaxCode.ts
var CustomerFileResponseDtoOutputPriceListsItemItemsItemTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/customerFileResponseDtoOutputPropertiesItemContactsItemRole.ts
var CustomerFileResponseDtoOutputPropertiesItemContactsItemRole = {
  CARETAKER: "CARETAKER",
  PROPERTY_MGMT: "PROPERTY_MGMT",
  TENANT: "TENANT",
  BILLING: "BILLING",
  OTHER: "OTHER"
};

// src/client/model/customerResponseDtoOutputInvoiceChannel.ts
var CustomerResponseDtoOutputInvoiceChannel = {
  XRECHNUNG_EMAIL: "XRECHNUNG_EMAIL",
  ZUGFERD_EMAIL: "ZUGFERD_EMAIL",
  PDF_EMAIL: "PDF_EMAIL",
  PORTAL_MANUAL: "PORTAL_MANUAL"
};

// src/client/model/customerResponseDtoOutputKind.ts
var CustomerResponseDtoOutputKind = {
  COMPANY: "COMPANY",
  PRIVATE: "PRIVATE"
};

// src/client/model/customerUpdateDtoInvoiceChannel.ts
var CustomerUpdateDtoInvoiceChannel = {
  XRECHNUNG_EMAIL: "XRECHNUNG_EMAIL",
  ZUGFERD_EMAIL: "ZUGFERD_EMAIL",
  PDF_EMAIL: "PDF_EMAIL",
  PORTAL_MANUAL: "PORTAL_MANUAL"
};

// src/client/model/customerUpdateDtoKind.ts
var CustomerUpdateDtoKind = {
  COMPANY: "COMPANY",
  PRIVATE: "PRIVATE"
};

// src/client/model/customersControllerFileIncludeItems.ts
var CustomersControllerFileIncludeItems = {
  true: "true",
  false: "false"
};

// src/client/model/customersControllerListArchived.ts
var CustomersControllerListArchived = {
  true: "true",
  false: "false",
  all: "all"
};

// src/client/model/dashboardResponseDtoOutputRecentFinalizedItemInvoiceKind.ts
var DashboardResponseDtoOutputRecentFinalizedItemInvoiceKind = {
  STANDARD: "STANDARD",
  PARTIAL: "PARTIAL",
  INTERIM: "INTERIM",
  FINAL: "FINAL"
};

// src/client/model/dashboardResponseDtoOutputRecentFinalizedItemStatus.ts
var DashboardResponseDtoOutputRecentFinalizedItemStatus = {
  DRAFT: "DRAFT",
  FINALIZED: "FINALIZED",
  SENT: "SENT",
  PAID: "PAID",
  CANCELLED: "CANCELLED"
};

// src/client/model/dashboardResponseDtoOutputRecentFinalizedItemType.ts
var DashboardResponseDtoOutputRecentFinalizedItemType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE",
  WORK_REPORT: "WORK_REPORT"
};

// src/client/model/datanormImportDtoEncoding.ts
var DatanormImportDtoEncoding = {
  "utf-8": "utf-8",
  cp1252: "cp1252",
  auto: "auto"
};

// src/client/model/datevControllerPreviewCorrection.ts
var DatevControllerPreviewCorrection = {
  true: "true",
  false: "false"
};

// src/client/model/datevPreviewResponseDtoOutputDocumentsItemInvoiceKind.ts
var DatevPreviewResponseDtoOutputDocumentsItemInvoiceKind = {
  STANDARD: "STANDARD",
  PARTIAL: "PARTIAL",
  INTERIM: "INTERIM",
  FINAL: "FINAL"
};

// src/client/model/datevPreviewResponseDtoOutputDocumentsItemType.ts
var DatevPreviewResponseDtoOutputDocumentsItemType = {
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE"
};

// src/client/model/datevRunDtoOutputFilesItemKind.ts
var DatevRunDtoOutputFilesItemKind = {
  BOOKINGS: "BOOKINGS",
  DEBTORS: "DEBTORS",
  DOCS_ZIP: "DOCS_ZIP"
};

// src/client/model/datevRunListResponseDtoOutputRunsItemFilesItemKind.ts
var DatevRunListResponseDtoOutputRunsItemFilesItemKind = {
  BOOKINGS: "BOOKINGS",
  DEBTORS: "DEBTORS",
  DOCS_ZIP: "DOCS_ZIP"
};

// src/client/model/dayDetailResponseDtoOutputAbsencesItemKind.ts
var DayDetailResponseDtoOutputAbsencesItemKind = {
  VACATION: "VACATION",
  SICK: "SICK",
  KIND_KRANK: "KIND_KRANK",
  BETRIEBSURLAUB: "BETRIEBSURLAUB",
  SONDERURLAUB: "SONDERURLAUB",
  UNBEZAHLT: "UNBEZAHLT",
  BESCHAEFTIGUNGSVERBOT: "BESCHAEFTIGUNGSVERBOT",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/documentCreateDtoInvoiceKind.ts
var DocumentCreateDtoInvoiceKind = {
  STANDARD: "STANDARD",
  PARTIAL: "PARTIAL",
  INTERIM: "INTERIM",
  FINAL: "FINAL"
};

// src/client/model/documentCreateDtoPartialMode.ts
var DocumentCreateDtoPartialMode = {
  AMOUNT: "AMOUNT",
  PERCENT: "PERCENT"
};

// src/client/model/documentCreateDtoPartialTaxCode.ts
var DocumentCreateDtoPartialTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/documentCreateDtoPrefill.ts
var DocumentCreateDtoPrefill = {
  AUTO: "AUTO",
  NONE: "NONE"
};

// src/client/model/documentCreateDtoType.ts
var DocumentCreateDtoType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE",
  WORK_REPORT: "WORK_REPORT"
};

// src/client/model/documentDeriveDtoInvoiceKind.ts
var DocumentDeriveDtoInvoiceKind = {
  STANDARD: "STANDARD",
  PARTIAL: "PARTIAL",
  INTERIM: "INTERIM",
  FINAL: "FINAL"
};

// src/client/model/documentDeriveDtoType.ts
var DocumentDeriveDtoType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE",
  WORK_REPORT: "WORK_REPORT"
};

// src/client/model/documentDetailResponseDtoOutputInvoiceChannel.ts
var DocumentDetailResponseDtoOutputInvoiceChannel = {
  XRECHNUNG_EMAIL: "XRECHNUNG_EMAIL",
  ZUGFERD_EMAIL: "ZUGFERD_EMAIL",
  PDF_EMAIL: "PDF_EMAIL",
  PORTAL_MANUAL: "PORTAL_MANUAL"
};

// src/client/model/documentDetailResponseDtoOutputInvoiceKind.ts
var DocumentDetailResponseDtoOutputInvoiceKind = {
  STANDARD: "STANDARD",
  PARTIAL: "PARTIAL",
  INTERIM: "INTERIM",
  FINAL: "FINAL"
};

// src/client/model/documentDetailResponseDtoOutputLinesItemAttachmentsItemDisplayWidth.ts
var DocumentDetailResponseDtoOutputLinesItemAttachmentsItemDisplayWidth = {
  SMALL: "SMALL",
  MEDIUM: "MEDIUM",
  FULL: "FULL"
};

// src/client/model/documentDetailResponseDtoOutputLinesItemItemKind.ts
var DocumentDetailResponseDtoOutputLinesItemItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/documentDetailResponseDtoOutputLinesItemTaxCode.ts
var DocumentDetailResponseDtoOutputLinesItemTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/documentDetailResponseDtoOutputLnHinweis.ts
var DocumentDetailResponseDtoOutputLnHinweis = {
  MATERIAL: "MATERIAL",
  FOLGETERMIN: "FOLGETERMIN",
  FREIGABE_OFFEN: "FREIGABE_OFFEN",
  ABRECHNUNG: "ABRECHNUNG"
};

// src/client/model/documentDetailResponseDtoOutputQuoteOutcome.ts
var DocumentDetailResponseDtoOutputQuoteOutcome = {
  OPEN: "OPEN",
  WON: "WON",
  LOST: "LOST"
};

// src/client/model/documentDetailResponseDtoOutputStatus.ts
var DocumentDetailResponseDtoOutputStatus = {
  DRAFT: "DRAFT",
  FINALIZED: "FINALIZED",
  SENT: "SENT",
  PAID: "PAID",
  CANCELLED: "CANCELLED"
};

// src/client/model/documentDetailResponseDtoOutputTaxBreakdownItemCode.ts
var DocumentDetailResponseDtoOutputTaxBreakdownItemCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/documentDetailResponseDtoOutputType.ts
var DocumentDetailResponseDtoOutputType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE",
  WORK_REPORT: "WORK_REPORT"
};

// src/client/model/documentSummaryDtoOutputInvoiceKind.ts
var DocumentSummaryDtoOutputInvoiceKind = {
  STANDARD: "STANDARD",
  PARTIAL: "PARTIAL",
  INTERIM: "INTERIM",
  FINAL: "FINAL"
};

// src/client/model/documentSummaryDtoOutputLnHinweis.ts
var DocumentSummaryDtoOutputLnHinweis = {
  MATERIAL: "MATERIAL",
  FOLGETERMIN: "FOLGETERMIN",
  FREIGABE_OFFEN: "FREIGABE_OFFEN",
  ABRECHNUNG: "ABRECHNUNG"
};

// src/client/model/documentSummaryDtoOutputStatus.ts
var DocumentSummaryDtoOutputStatus = {
  DRAFT: "DRAFT",
  FINALIZED: "FINALIZED",
  SENT: "SENT",
  PAID: "PAID",
  CANCELLED: "CANCELLED"
};

// src/client/model/documentSummaryDtoOutputType.ts
var DocumentSummaryDtoOutputType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE",
  WORK_REPORT: "WORK_REPORT"
};

// src/client/model/documentUpdateDtoLinesItemItemKind.ts
var DocumentUpdateDtoLinesItemItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/documentUpdateDtoLinesItemTaxCode.ts
var DocumentUpdateDtoLinesItemTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/documentsControllerListDraftOnly.ts
var DocumentsControllerListDraftOnly = {
  true: "true",
  false: "false"
};

// src/client/model/documentsControllerListStatus.ts
var DocumentsControllerListStatus = {
  DRAFT: "DRAFT",
  FINALIZED: "FINALIZED",
  SENT: "SENT",
  PAID: "PAID",
  CANCELLED: "CANCELLED"
};

// src/client/model/documentsControllerListType.ts
var DocumentsControllerListType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE",
  WORK_REPORT: "WORK_REPORT"
};

// src/client/model/dunningListResponseDtoOutputItemLevel.ts
var DunningListResponseDtoOutputItemLevel = {
  REMINDER: "REMINDER",
  LEVEL1: "LEVEL1",
  LEVEL2: "LEVEL2"
};

// src/client/model/dunningProposalResponseDtoOutputGroupsItemLevel.ts
var DunningProposalResponseDtoOutputGroupsItemLevel = {
  REMINDER: "REMINDER",
  LEVEL1: "LEVEL1",
  LEVEL2: "LEVEL2"
};

// src/client/model/dunningRunDtoSelectionsItemLevel.ts
var DunningRunDtoSelectionsItemLevel = {
  REMINDER: "REMINDER",
  LEVEL1: "LEVEL1",
  LEVEL2: "LEVEL2"
};

// src/client/model/dunningRunResponseDtoOutputCreatedItemLevel.ts
var DunningRunResponseDtoOutputCreatedItemLevel = {
  REMINDER: "REMINDER",
  LEVEL1: "LEVEL1",
  LEVEL2: "LEVEL2"
};

// src/client/model/emailLogDtoOutputStatus.ts
var EmailLogDtoOutputStatus = {
  QUEUED: "QUEUED",
  SENT: "SENT",
  FAILED: "FAILED"
};

// src/client/model/fahrtEntfernungDtoOutputQuelle.ts
var FahrtEntfernungDtoOutputQuelle = {
  OBJEKT: "OBJEKT",
  OBJEKT_BERECHNET: "OBJEKT_BERECHNET",
  FREIE_ADRESSE: "FREIE_ADRESSE",
  KUNDE: "KUNDE"
};

// src/client/model/followupControllerBoardFilter.ts
var FollowupControllerBoardFilter = {
  all: "all",
  open: "open",
  due: "due",
  won: "won",
  lost: "lost"
};

// src/client/model/healthResponseDtoOutputStatus.ts
var HealthResponseDtoOutputStatus = {
  ok: "ok",
  degraded: "degraded"
};

// src/client/model/holdSetDtoReason.ts
var HoldSetDtoReason = {
  MATERIAL_FEHLT: "MATERIAL_FEHLT",
  MIETER_NICHT_ERREICHBAR: "MIETER_NICHT_ERREICHBAR",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/importMetaDtoOutputEntitiesItemEntity.ts
var ImportMetaDtoOutputEntitiesItemEntity = {
  customer: "customer",
  contact: "contact",
  article: "article",
  open_item: "open_item",
  unit: "unit"
};

// src/client/model/importReportDtoOutputEntity.ts
var ImportReportDtoOutputEntity = {
  customer: "customer",
  contact: "contact",
  article: "article",
  open_item: "open_item",
  unit: "unit"
};

// src/client/model/importReportDtoOutputRowsItemAction.ts
var ImportReportDtoOutputRowsItemAction = {
  created: "created",
  updated: "updated",
  error: "error"
};

// src/client/model/importRunDtoEncoding.ts
var ImportRunDtoEncoding = {
  "utf-8": "utf-8",
  cp1252: "cp1252",
  auto: "auto"
};

// src/client/model/importRunDtoEntity.ts
var ImportRunDtoEntity = {
  customer: "customer",
  contact: "contact",
  article: "article",
  open_item: "open_item",
  unit: "unit"
};

// src/client/model/leerwohnungResponseDtoOutputAufnahmenItemPayloadNeueTuerenItemAnschlag.ts
var LeerwohnungResponseDtoOutputAufnahmenItemPayloadNeueTuerenItemAnschlag = {
  DIN_LINKS: "DIN_LINKS",
  DIN_RECHTS: "DIN_RECHTS"
};

// src/client/model/legacyArchiveResponseDtoOutputAbsencesItemMappedKind.ts
var LegacyArchiveResponseDtoOutputAbsencesItemMappedKind = {
  VACATION: "VACATION",
  SICK: "SICK",
  KIND_KRANK: "KIND_KRANK",
  BETRIEBSURLAUB: "BETRIEBSURLAUB",
  SONDERURLAUB: "SONDERURLAUB",
  UNBEZAHLT: "UNBEZAHLT",
  BESCHAEFTIGUNGSVERBOT: "BESCHAEFTIGUNGSVERBOT",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/lineAttachmentCreateDtoDisplayWidth.ts
var LineAttachmentCreateDtoDisplayWidth = {
  SMALL: "SMALL",
  MEDIUM: "MEDIUM",
  FULL: "FULL"
};

// src/client/model/lineAttachmentCreateDtoMimeType.ts
var LineAttachmentCreateDtoMimeType = {
  "image/jpeg": "image/jpeg",
  "image/png": "image/png",
  "application/pdf": "application/pdf"
};

// src/client/model/lineAttachmentDtoOutputDisplayWidth.ts
var LineAttachmentDtoOutputDisplayWidth = {
  SMALL: "SMALL",
  MEDIUM: "MEDIUM",
  FULL: "FULL"
};

// src/client/model/lineAttachmentUpdateDtoDisplayWidth.ts
var LineAttachmentUpdateDtoDisplayWidth = {
  SMALL: "SMALL",
  MEDIUM: "MEDIUM",
  FULL: "FULL"
};

// src/client/model/logoUploadDtoMimeType.ts
var LogoUploadDtoMimeType = {
  "image/png": "image/png",
  "image/jpeg": "image/jpeg",
  "image/svg+xml": "image/svg+xml"
};

// src/client/model/maintenanceContractCreateDtoStatus.ts
var MaintenanceContractCreateDtoStatus = {
  ACTIVE: "ACTIVE",
  ENDED: "ENDED"
};

// src/client/model/maintenanceContractListResponseDtoOutputItemStatus.ts
var MaintenanceContractListResponseDtoOutputItemStatus = {
  ACTIVE: "ACTIVE",
  ENDED: "ENDED"
};

// src/client/model/maintenanceContractResponseDtoOutputStatus.ts
var MaintenanceContractResponseDtoOutputStatus = {
  ACTIVE: "ACTIVE",
  ENDED: "ENDED"
};

// src/client/model/maintenanceContractUpdateDtoStatus.ts
var MaintenanceContractUpdateDtoStatus = {
  ACTIVE: "ACTIVE",
  ENDED: "ENDED"
};

// src/client/model/meResponseDtoOutputRole.ts
var MeResponseDtoOutputRole = {
  ADMIN: "ADMIN",
  OFFICE: "OFFICE",
  TECHNICIAN: "TECHNICIAN"
};

// src/client/model/membraCommitResultDtoOutputFileType.ts
var MembraCommitResultDtoOutputFileType = {
  stechzeiten: "stechzeiten",
  zeitkonto: "zeitkonto",
  kostenstellen: "kostenstellen",
  abwesenheiten: "abwesenheiten"
};

// src/client/model/membraDryRunResultDtoOutputFileType.ts
var MembraDryRunResultDtoOutputFileType = {
  stechzeiten: "stechzeiten",
  zeitkonto: "zeitkonto",
  kostenstellen: "kostenstellen",
  abwesenheiten: "abwesenheiten"
};

// src/client/model/membraImportDtoEncoding.ts
var MembraImportDtoEncoding = {
  "utf-8": "utf-8",
  cp1252: "cp1252",
  auto: "auto"
};

// src/client/model/membraImportDtoFileType.ts
var MembraImportDtoFileType = {
  stechzeiten: "stechzeiten",
  zeitkonto: "zeitkonto",
  kostenstellen: "kostenstellen",
  abwesenheiten: "abwesenheiten"
};

// src/client/model/monthCloseResponseDtoOutputStatus.ts
var MonthCloseResponseDtoOutputStatus = {
  OPEN: "OPEN",
  CLOSED: "CLOSED"
};

// src/client/model/monthResponseDtoOutputAccountEntriesItemType.ts
var MonthResponseDtoOutputAccountEntriesItemType = {
  PAYOUT: "PAYOUT",
  CORRECTION: "CORRECTION"
};

// src/client/model/monthResponseDtoOutputDaysItemAbsenceKind.ts
var MonthResponseDtoOutputDaysItemAbsenceKind = {
  VACATION: "VACATION",
  SICK: "SICK",
  KIND_KRANK: "KIND_KRANK",
  BETRIEBSURLAUB: "BETRIEBSURLAUB",
  SONDERURLAUB: "SONDERURLAUB",
  UNBEZAHLT: "UNBEZAHLT",
  BESCHAEFTIGUNGSVERBOT: "BESCHAEFTIGUNGSVERBOT",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/monthResponseDtoOutputMonthCloseStatus.ts
var MonthResponseDtoOutputMonthCloseStatus = {
  OPEN: "OPEN",
  CLOSED: "CLOSED"
};

// src/client/model/openItemsResponseDtoOutputItemsItemDunningLevel.ts
var OpenItemsResponseDtoOutputItemsItemDunningLevel = {
  REMINDER: "REMINDER",
  LEVEL1: "LEVEL1",
  LEVEL2: "LEVEL2"
};

// src/client/model/openItemsResponseDtoOutputItemsItemInvoiceKind.ts
var OpenItemsResponseDtoOutputItemsItemInvoiceKind = {
  STANDARD: "STANDARD",
  PARTIAL: "PARTIAL",
  INTERIM: "INTERIM",
  FINAL: "FINAL"
};

// src/client/model/orderAttachmentCreateDtoMimeType.ts
var OrderAttachmentCreateDtoMimeType = {
  "image/jpeg": "image/jpeg",
  "image/png": "image/png",
  "application/pdf": "application/pdf"
};

// src/client/model/orderCreateDtoCommissionType.ts
var OrderCreateDtoCommissionType = {
  GWH_BEAUFTRAGUNG: "GWH_BEAUFTRAGUNG",
  MIETERDIREKT: "MIETERDIREKT",
  SONSTIGE: "SONSTIGE"
};

// src/client/model/orderCreateDtoSource.ts
var OrderCreateDtoSource = {
  PHONE: "PHONE",
  EMAIL: "EMAIL",
  MAREON: "MAREON",
  YAROWA: "YAROWA",
  IMMOOFFICE: "IMMOOFFICE",
  OTHER: "OTHER"
};

// src/client/model/orderDetailResponseDtoOutputCommissionType.ts
var OrderDetailResponseDtoOutputCommissionType = {
  GWH_BEAUFTRAGUNG: "GWH_BEAUFTRAGUNG",
  MIETERDIREKT: "MIETERDIREKT",
  SONSTIGE: "SONSTIGE"
};

// src/client/model/orderDetailResponseDtoOutputDocumentsItemInvoiceKind.ts
var OrderDetailResponseDtoOutputDocumentsItemInvoiceKind = {
  STANDARD: "STANDARD",
  PARTIAL: "PARTIAL",
  INTERIM: "INTERIM",
  FINAL: "FINAL"
};

// src/client/model/orderDetailResponseDtoOutputDocumentsItemStatus.ts
var OrderDetailResponseDtoOutputDocumentsItemStatus = {
  DRAFT: "DRAFT",
  FINALIZED: "FINALIZED",
  SENT: "SENT",
  PAID: "PAID",
  CANCELLED: "CANCELLED"
};

// src/client/model/orderDetailResponseDtoOutputDocumentsItemType.ts
var OrderDetailResponseDtoOutputDocumentsItemType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE",
  WORK_REPORT: "WORK_REPORT"
};

// src/client/model/orderDetailResponseDtoOutputHoldInfoReason.ts
var OrderDetailResponseDtoOutputHoldInfoReason = {
  MATERIAL_FEHLT: "MATERIAL_FEHLT",
  MIETER_NICHT_ERREICHBAR: "MIETER_NICHT_ERREICHBAR",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/orderDetailResponseDtoOutputHoldInfoSource.ts
var OrderDetailResponseDtoOutputHoldInfoSource = {
  MONTEUR: "MONTEUR",
  OFFICE: "OFFICE"
};

// src/client/model/orderDetailResponseDtoOutputSource.ts
var OrderDetailResponseDtoOutputSource = {
  PHONE: "PHONE",
  EMAIL: "EMAIL",
  MAREON: "MAREON",
  YAROWA: "YAROWA",
  IMMOOFFICE: "IMMOOFFICE",
  OTHER: "OTHER"
};

// src/client/model/orderDetailResponseDtoOutputStatus.ts
var OrderDetailResponseDtoOutputStatus = {
  DRAFT: "DRAFT",
  ACTIVE: "ACTIVE",
  IN_PROGRESS: "IN_PROGRESS",
  DONE: "DONE",
  BILLED: "BILLED",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED"
};

// src/client/model/orderItemAttachmentCreateDtoDisplayWidth.ts
var OrderItemAttachmentCreateDtoDisplayWidth = {
  SMALL: "SMALL",
  MEDIUM: "MEDIUM",
  FULL: "FULL"
};

// src/client/model/orderItemAttachmentCreateDtoMimeType.ts
var OrderItemAttachmentCreateDtoMimeType = {
  "image/png": "image/png",
  "image/jpeg": "image/jpeg",
  "application/pdf": "application/pdf"
};

// src/client/model/orderItemAttachmentDtoOutputDisplayWidth.ts
var OrderItemAttachmentDtoOutputDisplayWidth = {
  SMALL: "SMALL",
  MEDIUM: "MEDIUM",
  FULL: "FULL"
};

// src/client/model/orderItemAttachmentUpdateDtoDisplayWidth.ts
var OrderItemAttachmentUpdateDtoDisplayWidth = {
  SMALL: "SMALL",
  MEDIUM: "MEDIUM",
  FULL: "FULL"
};

// src/client/model/orderItemCreateDtoItemKind.ts
var OrderItemCreateDtoItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/orderItemCreateDtoTaxCode.ts
var OrderItemCreateDtoTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/orderItemResponseDtoOutputAttachmentsItemDisplayWidth.ts
var OrderItemResponseDtoOutputAttachmentsItemDisplayWidth = {
  SMALL: "SMALL",
  MEDIUM: "MEDIUM",
  FULL: "FULL"
};

// src/client/model/orderItemResponseDtoOutputItemKind.ts
var OrderItemResponseDtoOutputItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/orderItemResponseDtoOutputTaxCode.ts
var OrderItemResponseDtoOutputTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/orderItemUpdateDtoItemKind.ts
var OrderItemUpdateDtoItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/orderItemUpdateDtoTaxCode.ts
var OrderItemUpdateDtoTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/orderListItemDtoOutputCommissionType.ts
var OrderListItemDtoOutputCommissionType = {
  GWH_BEAUFTRAGUNG: "GWH_BEAUFTRAGUNG",
  MIETERDIREKT: "MIETERDIREKT",
  SONSTIGE: "SONSTIGE"
};

// src/client/model/orderListItemDtoOutputHoldInfoReason.ts
var OrderListItemDtoOutputHoldInfoReason = {
  MATERIAL_FEHLT: "MATERIAL_FEHLT",
  MIETER_NICHT_ERREICHBAR: "MIETER_NICHT_ERREICHBAR",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/orderListItemDtoOutputHoldInfoSource.ts
var OrderListItemDtoOutputHoldInfoSource = {
  MONTEUR: "MONTEUR",
  OFFICE: "OFFICE"
};

// src/client/model/orderListItemDtoOutputSource.ts
var OrderListItemDtoOutputSource = {
  PHONE: "PHONE",
  EMAIL: "EMAIL",
  MAREON: "MAREON",
  YAROWA: "YAROWA",
  IMMOOFFICE: "IMMOOFFICE",
  OTHER: "OTHER"
};

// src/client/model/orderListItemDtoOutputStatus.ts
var OrderListItemDtoOutputStatus = {
  DRAFT: "DRAFT",
  ACTIVE: "ACTIVE",
  IN_PROGRESS: "IN_PROGRESS",
  DONE: "DONE",
  BILLED: "BILLED",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED"
};

// src/client/model/orderLoeschVorschauDtoOutputGruppenItemKey.ts
var OrderLoeschVorschauDtoOutputGruppenItemKey = {
  belegEntwuerfe: "belegEntwuerfe",
  belege: "belege",
  zeitbuchungen: "zeitbuchungen",
  ereignisse: "ereignisse",
  einsaetze: "einsaetze",
  anhaenge: "anhaenge",
  materialbestellungen: "materialbestellungen",
  unterschriften: "unterschriften"
};

// src/client/model/orderLoeschenDtoMitnehmenItem.ts
var OrderLoeschenDtoMitnehmenItem = {
  belegEntwuerfe: "belegEntwuerfe",
  belege: "belege",
  zeitbuchungen: "zeitbuchungen",
  ereignisse: "ereignisse",
  einsaetze: "einsaetze",
  anhaenge: "anhaenge",
  materialbestellungen: "materialbestellungen",
  unterschriften: "unterschriften"
};

// src/client/model/orderResponseDtoOutputCommissionType.ts
var OrderResponseDtoOutputCommissionType = {
  GWH_BEAUFTRAGUNG: "GWH_BEAUFTRAGUNG",
  MIETERDIREKT: "MIETERDIREKT",
  SONSTIGE: "SONSTIGE"
};

// src/client/model/orderResponseDtoOutputHoldInfoReason.ts
var OrderResponseDtoOutputHoldInfoReason = {
  MATERIAL_FEHLT: "MATERIAL_FEHLT",
  MIETER_NICHT_ERREICHBAR: "MIETER_NICHT_ERREICHBAR",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/orderResponseDtoOutputHoldInfoSource.ts
var OrderResponseDtoOutputHoldInfoSource = {
  MONTEUR: "MONTEUR",
  OFFICE: "OFFICE"
};

// src/client/model/orderResponseDtoOutputSource.ts
var OrderResponseDtoOutputSource = {
  PHONE: "PHONE",
  EMAIL: "EMAIL",
  MAREON: "MAREON",
  YAROWA: "YAROWA",
  IMMOOFFICE: "IMMOOFFICE",
  OTHER: "OTHER"
};

// src/client/model/orderResponseDtoOutputStatus.ts
var OrderResponseDtoOutputStatus = {
  DRAFT: "DRAFT",
  ACTIVE: "ACTIVE",
  IN_PROGRESS: "IN_PROGRESS",
  DONE: "DONE",
  BILLED: "BILLED",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED"
};

// src/client/model/orderUpdateDtoCommissionType.ts
var OrderUpdateDtoCommissionType = {
  GWH_BEAUFTRAGUNG: "GWH_BEAUFTRAGUNG",
  MIETERDIREKT: "MIETERDIREKT",
  SONSTIGE: "SONSTIGE"
};

// src/client/model/orderUpdateDtoSource.ts
var OrderUpdateDtoSource = {
  PHONE: "PHONE",
  EMAIL: "EMAIL",
  MAREON: "MAREON",
  YAROWA: "YAROWA",
  IMMOOFFICE: "IMMOOFFICE",
  OTHER: "OTHER"
};

// src/client/model/orderUpdateDtoStatus.ts
var OrderUpdateDtoStatus = {
  DRAFT: "DRAFT",
  ACTIVE: "ACTIVE",
  IN_PROGRESS: "IN_PROGRESS",
  DONE: "DONE",
  BILLED: "BILLED",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED"
};

// src/client/model/orderWorkDtoAction.ts
var OrderWorkDtoAction = {
  start: "start",
  switch: "switch",
  stop: "stop"
};

// src/client/model/ordersControllerListStatus.ts
var OrdersControllerListStatus = {
  DRAFT: "DRAFT",
  ACTIVE: "ACTIVE",
  IN_PROGRESS: "IN_PROGRESS",
  DONE: "DONE",
  BILLED: "BILLED",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED"
};

// src/client/model/parsedPortalOrderDtoOutputPortal.ts
var ParsedPortalOrderDtoOutputPortal = {
  GSW: "GSW",
  WOHNBAU: "WOHNBAU",
  BISCHOF: "BISCHOF",
  YAROWA: "YAROWA"
};

// src/client/model/parsedPortalOrderDtoPortal.ts
var ParsedPortalOrderDtoPortal = {
  GSW: "GSW",
  WOHNBAU: "WOHNBAU"
};

// src/client/model/paymentCreateResponseDtoOutputSummaryStatus.ts
var PaymentCreateResponseDtoOutputSummaryStatus = {
  DRAFT: "DRAFT",
  FINALIZED: "FINALIZED",
  SENT: "SENT",
  PAID: "PAID",
  CANCELLED: "CANCELLED"
};

// src/client/model/paymentListResponseDtoOutputSummaryStatus.ts
var PaymentListResponseDtoOutputSummaryStatus = {
  DRAFT: "DRAFT",
  FINALIZED: "FINALIZED",
  SENT: "SENT",
  PAID: "PAID",
  CANCELLED: "CANCELLED"
};

// src/client/model/paymentsControllerOpenItemsCsvOverdueOnly.ts
var PaymentsControllerOpenItemsCsvOverdueOnly = {
  true: "true",
  false: "false"
};

// src/client/model/paymentsControllerOpenItemsOverdueOnly.ts
var PaymentsControllerOpenItemsOverdueOnly = {
  true: "true",
  false: "false"
};

// src/client/model/planEntriesResponseDtoOutputEntriesItemKind.ts
var PlanEntriesResponseDtoOutputEntriesItemKind = {
  STANDBY: "STANDBY",
  INTERNAL_BLOCK: "INTERNAL_BLOCK"
};

// src/client/model/planEntryCreateDtoKind.ts
var PlanEntryCreateDtoKind = {
  STANDBY: "STANDBY",
  INTERNAL_BLOCK: "INTERNAL_BLOCK"
};

// src/client/model/planEntryResponseDtoOutputKind.ts
var PlanEntryResponseDtoOutputKind = {
  STANDBY: "STANDBY",
  INTERNAL_BLOCK: "INTERNAL_BLOCK"
};

// src/client/model/plantafelControllerDeleteEntryRest.ts
var PlantafelControllerDeleteEntryRest = {
  true: "true",
  false: "false"
};

// src/client/model/plantafelControllerWeekSaturday.ts
var PlantafelControllerWeekSaturday = {
  true: "true",
  false: "false"
};

// src/client/model/plantafelControllerWeekSunday.ts
var PlantafelControllerWeekSunday = {
  true: "true",
  false: "false"
};

// src/client/model/plantafelResponseDtoOutputAbsencesItemHalf.ts
var PlantafelResponseDtoOutputAbsencesItemHalf = {
  full: "full",
  half: "half"
};

// src/client/model/plantafelResponseDtoOutputAbsencesItemKind.ts
var PlantafelResponseDtoOutputAbsencesItemKind = {
  VACATION: "VACATION",
  SICK: "SICK",
  KIND_KRANK: "KIND_KRANK",
  BETRIEBSURLAUB: "BETRIEBSURLAUB",
  SONDERURLAUB: "SONDERURLAUB",
  UNBEZAHLT: "UNBEZAHLT",
  BESCHAEFTIGUNGSVERBOT: "BESCHAEFTIGUNGSVERBOT",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/plantafelResponseDtoOutputAssignmentsItemAusgang.ts
var PlantafelResponseDtoOutputAssignmentsItemAusgang = {
  ERFOLGREICH: "ERFOLGREICH",
  NICHT_ABGESCHLOSSEN: "NICHT_ABGESCHLOSSEN"
};

// src/client/model/plantafelResponseDtoOutputAssignmentsItemLiveStatusKind.ts
var PlantafelResponseDtoOutputAssignmentsItemLiveStatusKind = {
  planned: "planned",
  running: "running",
  paused: "paused",
  done: "done"
};

// src/client/model/plantafelResponseDtoOutputAssignmentsItemOrderHoldReason.ts
var PlantafelResponseDtoOutputAssignmentsItemOrderHoldReason = {
  MATERIAL_FEHLT: "MATERIAL_FEHLT",
  MIETER_NICHT_ERREICHBAR: "MIETER_NICHT_ERREICHBAR",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/plantafelResponseDtoOutputEntriesItemKind.ts
var PlantafelResponseDtoOutputEntriesItemKind = {
  STANDBY: "STANDBY",
  INTERNAL_BLOCK: "INTERNAL_BLOCK"
};

// src/client/model/plantafelResponseDtoOutputUnassignedItemHoldReason.ts
var PlantafelResponseDtoOutputUnassignedItemHoldReason = {
  MATERIAL_FEHLT: "MATERIAL_FEHLT",
  MIETER_NICHT_ERREICHBAR: "MIETER_NICHT_ERREICHBAR",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/portalFillVorschlagDtoOutputUnterschriftenItemRolle.ts
var PortalFillVorschlagDtoOutputUnterschriftenItemRolle = {
  MIETER_HAUSWART: "MIETER_HAUSWART",
  HANDWERKER: "HANDWERKER"
};

// src/client/model/portalOrderCommitDtoCommissionType.ts
var PortalOrderCommitDtoCommissionType = {
  GWH_BEAUFTRAGUNG: "GWH_BEAUFTRAGUNG",
  MIETERDIREKT: "MIETERDIREKT",
  SONSTIGE: "SONSTIGE"
};

// src/client/model/portalOrderCommitDtoPortal.ts
var PortalOrderCommitDtoPortal = {
  GSW: "GSW",
  WOHNBAU: "WOHNBAU",
  BISCHOF: "BISCHOF",
  YAROWA: "YAROWA"
};

// src/client/model/priceListDetailResponseDtoOutputItemsItemItemKind.ts
var PriceListDetailResponseDtoOutputItemsItemItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/priceListDetailResponseDtoOutputItemsItemTaxCode.ts
var PriceListDetailResponseDtoOutputItemsItemTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/priceListItemCreateDtoItemKind.ts
var PriceListItemCreateDtoItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/priceListItemCreateDtoTaxCode.ts
var PriceListItemCreateDtoTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/priceListItemResponseDtoOutputItemKind.ts
var PriceListItemResponseDtoOutputItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/priceListItemResponseDtoOutputTaxCode.ts
var PriceListItemResponseDtoOutputTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/priceListItemUpdateDtoItemKind.ts
var PriceListItemUpdateDtoItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/priceListItemUpdateDtoTaxCode.ts
var PriceListItemUpdateDtoTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/quoteBoardResponseDtoOutputItemsItemOutcome.ts
var QuoteBoardResponseDtoOutputItemsItemOutcome = {
  OPEN: "OPEN",
  WON: "WON",
  LOST: "LOST"
};

// src/client/model/recurringCreateDtoInterval.ts
var RecurringCreateDtoInterval = {
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  YEARLY: "YEARLY"
};

// src/client/model/recurringCreateDtoItemsItemItemKind.ts
var RecurringCreateDtoItemsItemItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/recurringCreateDtoItemsItemTaxCode.ts
var RecurringCreateDtoItemsItemTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/recurringListResponseDtoOutputItemInterval.ts
var RecurringListResponseDtoOutputItemInterval = {
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  YEARLY: "YEARLY"
};

// src/client/model/recurringListResponseDtoOutputItemItemsItemItemKind.ts
var RecurringListResponseDtoOutputItemItemsItemItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/recurringListResponseDtoOutputItemItemsItemTaxCode.ts
var RecurringListResponseDtoOutputItemItemsItemTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/recurringResponseDtoOutputInterval.ts
var RecurringResponseDtoOutputInterval = {
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  YEARLY: "YEARLY"
};

// src/client/model/recurringResponseDtoOutputItemsItemItemKind.ts
var RecurringResponseDtoOutputItemsItemItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/recurringResponseDtoOutputItemsItemTaxCode.ts
var RecurringResponseDtoOutputItemsItemTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/recurringUpdateDtoInterval.ts
var RecurringUpdateDtoInterval = {
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  YEARLY: "YEARLY"
};

// src/client/model/recurringUpdateDtoItemsItemItemKind.ts
var RecurringUpdateDtoItemsItemItemKind = {
  LABOR: "LABOR",
  MATERIAL: "MATERIAL",
  TRAVEL: "TRAVEL",
  EQUIPMENT: "EQUIPMENT",
  OTHER: "OTHER"
};

// src/client/model/recurringUpdateDtoItemsItemTaxCode.ts
var RecurringUpdateDtoItemsItemTaxCode = {
  DE_19: "DE_19",
  DE_7: "DE_7",
  RC_13B: "RC_13B"
};

// src/client/model/rowCreateDtoFormula.ts
var RowCreateDtoFormula = {
  COUNT: "COUNT",
  LENGTH: "LENGTH",
  AREA: "AREA",
  VOLUME: "VOLUME"
};

// src/client/model/rowResponseDtoOutputFormula.ts
var RowResponseDtoOutputFormula = {
  COUNT: "COUNT",
  LENGTH: "LENGTH",
  AREA: "AREA",
  VOLUME: "VOLUME"
};

// src/client/model/rowUpdateDtoFormula.ts
var RowUpdateDtoFormula = {
  COUNT: "COUNT",
  LENGTH: "LENGTH",
  AREA: "AREA",
  VOLUME: "VOLUME"
};

// src/client/model/searchResultDtoOutputCustomersItemKind.ts
var SearchResultDtoOutputCustomersItemKind = {
  COMPANY: "COMPANY",
  PRIVATE: "PRIVATE"
};

// src/client/model/settingsResponseDtoOutputDatevFinalInvoiceMode.ts
var SettingsResponseDtoOutputDatevFinalInvoiceMode = {
  GROSS_TOTAL: "GROSS_TOTAL",
  REMAINDER: "REMAINDER"
};

// src/client/model/settingsResponseDtoOutputHolidayState.ts
var SettingsResponseDtoOutputHolidayState = {
  RP: "RP",
  HE: "HE"
};

// src/client/model/settingsResponseDtoOutputMailTransport.ts
var SettingsResponseDtoOutputMailTransport = {
  smtp: "smtp",
  graph: "graph"
};

// src/client/model/settingsResponseDtoOutputSignatureThresholdBasis.ts
var SettingsResponseDtoOutputSignatureThresholdBasis = {
  GROSS: "GROSS",
  NET: "NET"
};

// src/client/model/settingsUpdateDtoDatevFinalInvoiceMode.ts
var SettingsUpdateDtoDatevFinalInvoiceMode = {
  GROSS_TOTAL: "GROSS_TOTAL",
  REMAINDER: "REMAINDER"
};

// src/client/model/settingsUpdateDtoHolidayState.ts
var SettingsUpdateDtoHolidayState = {
  RP: "RP",
  HE: "HE"
};

// src/client/model/settingsUpdateDtoMailTransport.ts
var SettingsUpdateDtoMailTransport = {
  smtp: "smtp",
  graph: "graph"
};

// src/client/model/settingsUpdateDtoSignatureThresholdBasis.ts
var SettingsUpdateDtoSignatureThresholdBasis = {
  GROSS: "GROSS",
  NET: "NET"
};

// src/client/model/sheetResponseDtoOutputRowsItemFormula.ts
var SheetResponseDtoOutputRowsItemFormula = {
  COUNT: "COUNT",
  LENGTH: "LENGTH",
  AREA: "AREA",
  VOLUME: "VOLUME"
};

// src/client/model/signatureObligationDtoOutputReason.ts
var SignatureObligationDtoOutputReason = {
  GWH: "GWH",
  VALUE: "VALUE",
  MIETERDIREKT_BELOW: "MIETERDIREKT_BELOW",
  BELOW: "BELOW"
};

// src/client/model/signatureRequestListDtoOutputRequestsItemStatus.ts
var SignatureRequestListDtoOutputRequestsItemStatus = {
  PENDING: "PENDING",
  SIGNED: "SIGNED",
  EXPIRED: "EXPIRED",
  REVOKED: "REVOKED"
};

// src/client/model/signatureRequestViewDtoOutputStatus.ts
var SignatureRequestViewDtoOutputStatus = {
  PENDING: "PENDING",
  SIGNED: "SIGNED",
  EXPIRED: "EXPIRED",
  REVOKED: "REVOKED"
};

// src/client/model/techAbsenceRequestCreateDtoType.ts
var TechAbsenceRequestCreateDtoType = {
  VACATION: "VACATION",
  SICK: "SICK",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH"
};

// src/client/model/techAbsenceRequestCreateResponseDtoOutputRequestStatus.ts
var TechAbsenceRequestCreateResponseDtoOutputRequestStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  REPORTED: "REPORTED",
  CANCELLED: "CANCELLED"
};

// src/client/model/techAbsenceRequestCreateResponseDtoOutputRequestType.ts
var TechAbsenceRequestCreateResponseDtoOutputRequestType = {
  VACATION: "VACATION",
  SICK: "SICK",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH"
};

// src/client/model/techAbsenceRequestEditResponseDtoOutputRequestStatus.ts
var TechAbsenceRequestEditResponseDtoOutputRequestStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  REPORTED: "REPORTED",
  CANCELLED: "CANCELLED"
};

// src/client/model/techAbsenceRequestEditResponseDtoOutputRequestType.ts
var TechAbsenceRequestEditResponseDtoOutputRequestType = {
  VACATION: "VACATION",
  SICK: "SICK",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH"
};

// src/client/model/techAbsenceRequestsResponseDtoOutputItemsItemStatus.ts
var TechAbsenceRequestsResponseDtoOutputItemsItemStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  REPORTED: "REPORTED",
  CANCELLED: "CANCELLED"
};

// src/client/model/techAbsenceRequestsResponseDtoOutputItemsItemType.ts
var TechAbsenceRequestsResponseDtoOutputItemsItemType = {
  VACATION: "VACATION",
  SICK: "SICK",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH"
};

// src/client/model/techAttendanceStatusDtoOutputState.ts
var TechAttendanceStatusDtoOutputState = {
  OUT: "OUT",
  PRESENT: "PRESENT",
  BREAK: "BREAK"
};

// src/client/model/techAuftragFotosDtoOutputFotosItemQuelle.ts
var TechAuftragFotosDtoOutputFotosItemQuelle = {
  APP: "APP",
  BUERO: "BUERO"
};

// src/client/model/techAuftragLeerwohnungDtoOutputAufnahmenItemPayloadNeueTuerenItemAnschlag.ts
var TechAuftragLeerwohnungDtoOutputAufnahmenItemPayloadNeueTuerenItemAnschlag = {
  DIN_LINKS: "DIN_LINKS",
  DIN_RECHTS: "DIN_RECHTS"
};

// src/client/model/techEventsDtoEventsItemType.ts
var TechEventsDtoEventsItemType = {
  TRAVEL_START: "TRAVEL_START",
  WORK_START: "WORK_START",
  PAUSE_START: "PAUSE_START",
  PAUSE_END: "PAUSE_END",
  STOP: "STOP",
  TECH_DONE: "TECH_DONE",
  MATERIAL_ADDED: "MATERIAL_ADDED",
  PHOTO: "PHOTO",
  NOTE: "NOTE",
  REPORT: "REPORT",
  SIGNATURE: "SIGNATURE",
  ATT_IN: "ATT_IN",
  ATT_OUT: "ATT_OUT",
  ATT_BREAK_START: "ATT_BREAK_START",
  ATT_BREAK_END: "ATT_BREAK_END",
  PROBLEM_REPORTED: "PROBLEM_REPORTED",
  PROBLEM_RESOLVED: "PROBLEM_RESOLVED",
  EMPTY_APARTMENT_RECORDED: "EMPTY_APARTMENT_RECORDED",
  ENTRY_RETRACTED: "ENTRY_RETRACTED",
  INTERNAL_NOTE: "INTERNAL_NOTE"
};

// src/client/model/techEventsResponseDtoOutputResultsItemStatus.ts
var TechEventsResponseDtoOutputResultsItemStatus = {
  accepted: "accepted",
  accepted_noop: "accepted_noop",
  duplicate: "duplicate",
  rejected: "rejected"
};

// src/client/model/techSyncResponseDtoOutputAssignmentsItemOrderCommissionType.ts
var TechSyncResponseDtoOutputAssignmentsItemOrderCommissionType = {
  GWH_BEAUFTRAGUNG: "GWH_BEAUFTRAGUNG",
  MIETERDIREKT: "MIETERDIREKT",
  SONSTIGE: "SONSTIGE"
};

// src/client/model/techSyncResponseDtoOutputAssignmentsItemOrderHoldInfoReason.ts
var TechSyncResponseDtoOutputAssignmentsItemOrderHoldInfoReason = {
  MATERIAL_FEHLT: "MATERIAL_FEHLT",
  MIETER_NICHT_ERREICHBAR: "MIETER_NICHT_ERREICHBAR",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/techSyncResponseDtoOutputAssignmentsItemOrderHoldInfoSource.ts
var TechSyncResponseDtoOutputAssignmentsItemOrderHoldInfoSource = {
  MONTEUR: "MONTEUR",
  OFFICE: "OFFICE"
};

// src/client/model/techSyncResponseDtoOutputPlanEntriesItemKind.ts
var TechSyncResponseDtoOutputPlanEntriesItemKind = {
  STANDBY: "STANDBY",
  INTERNAL_BLOCK: "INTERNAL_BLOCK"
};

// src/client/model/techTeamAuftragDtoOutputOrderCommissionType.ts
var TechTeamAuftragDtoOutputOrderCommissionType = {
  GWH_BEAUFTRAGUNG: "GWH_BEAUFTRAGUNG",
  MIETERDIREKT: "MIETERDIREKT",
  SONSTIGE: "SONSTIGE"
};

// src/client/model/techTeamAuftragDtoOutputOrderHoldInfoReason.ts
var TechTeamAuftragDtoOutputOrderHoldInfoReason = {
  MATERIAL_FEHLT: "MATERIAL_FEHLT",
  MIETER_NICHT_ERREICHBAR: "MIETER_NICHT_ERREICHBAR",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/techTeamAuftragDtoOutputOrderHoldInfoSource.ts
var TechTeamAuftragDtoOutputOrderHoldInfoSource = {
  MONTEUR: "MONTEUR",
  OFFICE: "OFFICE"
};

// src/client/model/techTimeDayStampsResponseDtoOutputStampsItemKind.ts
var TechTimeDayStampsResponseDtoOutputStampsItemKind = {
  IN: "IN",
  OUT: "OUT",
  BREAK_START: "BREAK_START",
  BREAK_END: "BREAK_END"
};

// src/client/model/techTimeSummaryResponseDtoOutputAccountEntriesItemType.ts
var TechTimeSummaryResponseDtoOutputAccountEntriesItemType = {
  PAYOUT: "PAYOUT",
  CORRECTION: "CORRECTION"
};

// src/client/model/techTimeSummaryResponseDtoOutputDaysItemAbsenceKind.ts
var TechTimeSummaryResponseDtoOutputDaysItemAbsenceKind = {
  VACATION: "VACATION",
  SICK: "SICK",
  KIND_KRANK: "KIND_KRANK",
  BETRIEBSURLAUB: "BETRIEBSURLAUB",
  SONDERURLAUB: "SONDERURLAUB",
  UNBEZAHLT: "UNBEZAHLT",
  BESCHAEFTIGUNGSVERBOT: "BESCHAEFTIGUNGSVERBOT",
  UEBERSTUNDEN_AUSGLEICH: "UEBERSTUNDEN_AUSGLEICH",
  SONSTIGES: "SONSTIGES"
};

// src/client/model/techUploadDtoKind.ts
var TechUploadDtoKind = {
  PHOTO: "PHOTO",
  SIGNATURE: "SIGNATURE"
};

// src/client/model/textTemplateCreateDtoDocType.ts
var TextTemplateCreateDtoDocType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE"
};

// src/client/model/textTemplateCreateDtoDunningLevel.ts
var TextTemplateCreateDtoDunningLevel = {
  REMINDER: "REMINDER",
  LEVEL1: "LEVEL1",
  LEVEL2: "LEVEL2"
};

// src/client/model/textTemplateCreateDtoSlot.ts
var TextTemplateCreateDtoSlot = {
  DOC_INTRO: "DOC_INTRO",
  DOC_FOOTER: "DOC_FOOTER",
  DOC_PAYMENT_TERMS: "DOC_PAYMENT_TERMS",
  DOC_WARRANTY: "DOC_WARRANTY",
  DOC_REVOCATION: "DOC_REVOCATION",
  POSITION_TEXT: "POSITION_TEXT",
  MAIL_SUBJECT: "MAIL_SUBJECT",
  MAIL_BODY: "MAIL_BODY",
  MAIL_SIGNATURE: "MAIL_SIGNATURE",
  DUNNING_TEXT: "DUNNING_TEXT",
  HOLD_CUSTOMER_MAIL: "HOLD_CUSTOMER_MAIL",
  PAYROLL_MAIL_SUBJECT: "PAYROLL_MAIL_SUBJECT",
  PAYROLL_MAIL_BODY: "PAYROLL_MAIL_BODY"
};

// src/client/model/textTemplateDtoOutputDocType.ts
var TextTemplateDtoOutputDocType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE"
};

// src/client/model/textTemplateDtoOutputDunningLevel.ts
var TextTemplateDtoOutputDunningLevel = {
  REMINDER: "REMINDER",
  LEVEL1: "LEVEL1",
  LEVEL2: "LEVEL2"
};

// src/client/model/textTemplateDtoOutputSlot.ts
var TextTemplateDtoOutputSlot = {
  DOC_INTRO: "DOC_INTRO",
  DOC_FOOTER: "DOC_FOOTER",
  DOC_PAYMENT_TERMS: "DOC_PAYMENT_TERMS",
  DOC_WARRANTY: "DOC_WARRANTY",
  DOC_REVOCATION: "DOC_REVOCATION",
  POSITION_TEXT: "POSITION_TEXT",
  MAIL_SUBJECT: "MAIL_SUBJECT",
  MAIL_BODY: "MAIL_BODY",
  MAIL_SIGNATURE: "MAIL_SIGNATURE",
  DUNNING_TEXT: "DUNNING_TEXT",
  HOLD_CUSTOMER_MAIL: "HOLD_CUSTOMER_MAIL",
  PAYROLL_MAIL_SUBJECT: "PAYROLL_MAIL_SUBJECT",
  PAYROLL_MAIL_BODY: "PAYROLL_MAIL_BODY"
};

// src/client/model/textTemplateListDtoOutputItemDocType.ts
var TextTemplateListDtoOutputItemDocType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE"
};

// src/client/model/textTemplateListDtoOutputItemDunningLevel.ts
var TextTemplateListDtoOutputItemDunningLevel = {
  REMINDER: "REMINDER",
  LEVEL1: "LEVEL1",
  LEVEL2: "LEVEL2"
};

// src/client/model/textTemplateListDtoOutputItemSlot.ts
var TextTemplateListDtoOutputItemSlot = {
  DOC_INTRO: "DOC_INTRO",
  DOC_FOOTER: "DOC_FOOTER",
  DOC_PAYMENT_TERMS: "DOC_PAYMENT_TERMS",
  DOC_WARRANTY: "DOC_WARRANTY",
  DOC_REVOCATION: "DOC_REVOCATION",
  POSITION_TEXT: "POSITION_TEXT",
  MAIL_SUBJECT: "MAIL_SUBJECT",
  MAIL_BODY: "MAIL_BODY",
  MAIL_SIGNATURE: "MAIL_SIGNATURE",
  DUNNING_TEXT: "DUNNING_TEXT",
  HOLD_CUSTOMER_MAIL: "HOLD_CUSTOMER_MAIL",
  PAYROLL_MAIL_SUBJECT: "PAYROLL_MAIL_SUBJECT",
  PAYROLL_MAIL_BODY: "PAYROLL_MAIL_BODY"
};

// src/client/model/textTemplateUpdateDtoDocType.ts
var TextTemplateUpdateDtoDocType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE"
};

// src/client/model/textTemplateUpdateDtoDunningLevel.ts
var TextTemplateUpdateDtoDunningLevel = {
  REMINDER: "REMINDER",
  LEVEL1: "LEVEL1",
  LEVEL2: "LEVEL2"
};

// src/client/model/textTemplatesControllerListSlot.ts
var TextTemplatesControllerListSlot = {
  DOC_INTRO: "DOC_INTRO",
  DOC_FOOTER: "DOC_FOOTER",
  DOC_PAYMENT_TERMS: "DOC_PAYMENT_TERMS",
  DOC_WARRANTY: "DOC_WARRANTY",
  DOC_REVOCATION: "DOC_REVOCATION",
  POSITION_TEXT: "POSITION_TEXT",
  MAIL_SUBJECT: "MAIL_SUBJECT",
  MAIL_BODY: "MAIL_BODY",
  MAIL_SIGNATURE: "MAIL_SIGNATURE",
  DUNNING_TEXT: "DUNNING_TEXT",
  HOLD_CUSTOMER_MAIL: "HOLD_CUSTOMER_MAIL",
  PAYROLL_MAIL_SUBJECT: "PAYROLL_MAIL_SUBJECT",
  PAYROLL_MAIL_BODY: "PAYROLL_MAIL_BODY"
};

// src/client/model/ticketCreateDtoAttachmentsItemMimeType.ts
var TicketCreateDtoAttachmentsItemMimeType = {
  "image/jpeg": "image/jpeg",
  "image/png": "image/png",
  "application/pdf": "application/pdf"
};

// src/client/model/ticketCreateDtoPriority.ts
var TicketCreateDtoPriority = {
  LOW: "LOW",
  NORMAL: "NORMAL",
  HIGH: "HIGH"
};

// src/client/model/ticketDetailDtoOutputPriority.ts
var TicketDetailDtoOutputPriority = {
  LOW: "LOW",
  NORMAL: "NORMAL",
  HIGH: "HIGH"
};

// src/client/model/ticketDetailDtoOutputStatus.ts
var TicketDetailDtoOutputStatus = {
  OPEN: "OPEN",
  IN_PROGRESS: "IN_PROGRESS",
  WAITING: "WAITING",
  DONE: "DONE"
};

// src/client/model/ticketListDtoOutputItemsItemPriority.ts
var TicketListDtoOutputItemsItemPriority = {
  LOW: "LOW",
  NORMAL: "NORMAL",
  HIGH: "HIGH"
};

// src/client/model/ticketListDtoOutputItemsItemStatus.ts
var TicketListDtoOutputItemsItemStatus = {
  OPEN: "OPEN",
  IN_PROGRESS: "IN_PROGRESS",
  WAITING: "WAITING",
  DONE: "DONE"
};

// src/client/model/ticketReplyDtoAttachmentsItemMimeType.ts
var TicketReplyDtoAttachmentsItemMimeType = {
  "image/jpeg": "image/jpeg",
  "image/png": "image/png",
  "application/pdf": "application/pdf"
};

// src/client/model/ticketUpdateDtoPriority.ts
var TicketUpdateDtoPriority = {
  LOW: "LOW",
  NORMAL: "NORMAL",
  HIGH: "HIGH"
};

// src/client/model/ticketUpdateDtoStatus.ts
var TicketUpdateDtoStatus = {
  OPEN: "OPEN",
  IN_PROGRESS: "IN_PROGRESS",
  WAITING: "WAITING",
  DONE: "DONE"
};

// src/client/model/ticketsControllerListIncludeDone.ts
var TicketsControllerListIncludeDone = {
  true: "true",
  false: "false"
};

// src/client/model/ticketsControllerListMine.ts
var TicketsControllerListMine = {
  true: "true",
  false: "false"
};

// src/client/model/ticketsControllerListStatus.ts
var TicketsControllerListStatus = {
  OPEN: "OPEN",
  IN_PROGRESS: "IN_PROGRESS",
  WAITING: "WAITING",
  DONE: "DONE"
};

// src/client/model/timeAccountEntryCreateDtoType.ts
var TimeAccountEntryCreateDtoType = {
  PAYOUT: "PAYOUT",
  CORRECTION: "CORRECTION"
};

// src/client/model/timeAccountEntryResponseDtoOutputType.ts
var TimeAccountEntryResponseDtoOutputType = {
  PAYOUT: "PAYOUT",
  CORRECTION: "CORRECTION"
};

// src/client/model/travelBillingUpdateDtoTravelBillingOneOfFiveMode.ts
var TravelBillingUpdateDtoTravelBillingOneOfFiveMode = {
  PER_KM: "PER_KM"
};

// src/client/model/travelBillingUpdateDtoTravelBillingOneOfMode.ts
var TravelBillingUpdateDtoTravelBillingOneOfMode = {
  NONE: "NONE"
};

// src/client/model/travelBillingUpdateDtoTravelBillingOneOfNineMode.ts
var TravelBillingUpdateDtoTravelBillingOneOfNineMode = {
  ZONES: "ZONES"
};

// src/client/model/travelBillingUpdateDtoTravelBillingOneOfSevenMode.ts
var TravelBillingUpdateDtoTravelBillingOneOfSevenMode = {
  TRIP_POSITIONS: "TRIP_POSITIONS"
};

// src/client/model/travelBillingUpdateDtoTravelBillingOneOfThreeMode.ts
var TravelBillingUpdateDtoTravelBillingOneOfThreeMode = {
  FLAT: "FLAT"
};

// src/client/model/userCreateDtoRole.ts
var UserCreateDtoRole = {
  ADMIN: "ADMIN",
  OFFICE: "OFFICE",
  TECHNICIAN: "TECHNICIAN"
};

// src/client/model/userResponseDtoOutputRole.ts
var UserResponseDtoOutputRole = {
  ADMIN: "ADMIN",
  OFFICE: "OFFICE",
  TECHNICIAN: "TECHNICIAN"
};

// src/client/model/userUpdateDtoRole.ts
var UserUpdateDtoRole = {
  ADMIN: "ADMIN",
  OFFICE: "OFFICE",
  TECHNICIAN: "TECHNICIAN"
};

// src/client/model/vorgangResponseDtoOutputChainItemDunningLevel.ts
var VorgangResponseDtoOutputChainItemDunningLevel = {
  REMINDER: "REMINDER",
  LEVEL1: "LEVEL1",
  LEVEL2: "LEVEL2"
};

// src/client/model/vorgangResponseDtoOutputChainItemInvoiceKind.ts
var VorgangResponseDtoOutputChainItemInvoiceKind = {
  STANDARD: "STANDARD",
  PARTIAL: "PARTIAL",
  INTERIM: "INTERIM",
  FINAL: "FINAL"
};

// src/client/model/vorgangResponseDtoOutputChainItemKind.ts
var VorgangResponseDtoOutputChainItemKind = {
  DOCUMENT: "DOCUMENT",
  MEASUREMENT: "MEASUREMENT",
  DUNNING: "DUNNING",
  FOLLOWUP_ORDER: "FOLLOWUP_ORDER"
};

// src/client/model/vorgangResponseDtoOutputChainItemPaymentStatus.ts
var VorgangResponseDtoOutputChainItemPaymentStatus = {
  offen: "offen",
  teilbezahlt: "teilbezahlt",
  bezahlt: "bezahlt",
  \u00FCberf\u00E4llig: "\xFCberf\xE4llig"
};

// src/client/model/vorgangResponseDtoOutputChainItemStatus.ts
var VorgangResponseDtoOutputChainItemStatus = {
  DRAFT: "DRAFT",
  FINALIZED: "FINALIZED",
  SENT: "SENT",
  PAID: "PAID",
  CANCELLED: "CANCELLED"
};

// src/client/model/vorgangResponseDtoOutputChainItemType.ts
var VorgangResponseDtoOutputChainItemType = {
  QUOTE: "QUOTE",
  ORDER_CONF: "ORDER_CONF",
  DELIVERY_NOTE: "DELIVERY_NOTE",
  INVOICE: "INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE",
  WORK_REPORT: "WORK_REPORT",
  AUFMASS: "AUFMASS",
  MAHNUNG: "MAHNUNG",
  FOLGEAUFTRAG: "FOLGEAUFTRAG"
};

// src/client/model/wrFotoUploadDtoMimeType.ts
var WrFotoUploadDtoMimeType = {
  "image/jpeg": "image/jpeg",
  "image/png": "image/png"
};
export {
  AbsenceBulkCreateDtoKind,
  AbsenceCreateDtoKind,
  AbsenceRequestControllerListStatus,
  AbsenceRequestDecideDtoDecision,
  AbsenceRequestListResponseDtoOutputItemsItemStatus,
  AbsenceRequestListResponseDtoOutputItemsItemType,
  AbsenceResponseDtoOutputKind,
  ActivityControllerListType,
  ActivityResponseDtoOutputEntriesItemCategory,
  ActivityResponseDtoOutputEntriesItemKind,
  AnalyticsControllerOrderHoursCsvIncludeLegacy,
  AnalyticsControllerOrderHoursIncludeLegacy,
  ApiError,
  ArticleCreateDtoDefaultTaxCode,
  ArticleCreateDtoItemKind,
  ArticleCreateDtoKind,
  ArticleResponseDtoOutputDefaultTaxCode,
  ArticleResponseDtoOutputItemKind,
  ArticleResponseDtoOutputKind,
  ArticleUpdateDtoDefaultTaxCode,
  ArticleUpdateDtoItemKind,
  ArticleUpdateDtoKind,
  ArticlesControllerListArchived,
  ArticlesControllerListKind,
  AssignmentResponseDtoOutputAusgang,
  AssignmentResponseDtoOutputLiveStatusKind,
  AssignmentResponseDtoOutputOrderHoldReason,
  AttendanceNowResponseDtoOutputConflictsItemKind,
  AttendanceNowResponseDtoOutputNowItemState,
  BankImportPruefungDtoOutputVorschauItemArt,
  BankTransaktionDtoOutputStatus,
  BusinessAreasControllerListArchived,
  ClockDtoKind,
  ClockStatusResponseDtoOutputState,
  ContactCreateDtoRole,
  ContactResponseDtoOutputRole,
  ContactUpdateDtoRole,
  CustomerCreateDtoInvoiceChannel,
  CustomerCreateDtoKind,
  CustomerFileResponseDtoOutputContactsItemRole,
  CustomerFileResponseDtoOutputInvoiceChannel,
  CustomerFileResponseDtoOutputKind,
  CustomerFileResponseDtoOutputMaintenanceContractsItemStatus,
  CustomerFileResponseDtoOutputPriceListsItemItemsItemItemKind,
  CustomerFileResponseDtoOutputPriceListsItemItemsItemTaxCode,
  CustomerFileResponseDtoOutputPropertiesItemContactsItemRole,
  CustomerResponseDtoOutputInvoiceChannel,
  CustomerResponseDtoOutputKind,
  CustomerUpdateDtoInvoiceChannel,
  CustomerUpdateDtoKind,
  CustomersControllerFileIncludeItems,
  CustomersControllerListArchived,
  DOC_TYPE_LABELS,
  DUNNING_LEVEL_LABELS,
  DashboardResponseDtoOutputRecentFinalizedItemInvoiceKind,
  DashboardResponseDtoOutputRecentFinalizedItemStatus,
  DashboardResponseDtoOutputRecentFinalizedItemType,
  DatanormImportDtoEncoding,
  DatevControllerPreviewCorrection,
  DatevPreviewResponseDtoOutputDocumentsItemInvoiceKind,
  DatevPreviewResponseDtoOutputDocumentsItemType,
  DatevRunDtoOutputFilesItemKind,
  DatevRunListResponseDtoOutputRunsItemFilesItemKind,
  DayDetailResponseDtoOutputAbsencesItemKind,
  DocumentCreateDtoInvoiceKind,
  DocumentCreateDtoPartialMode,
  DocumentCreateDtoPartialTaxCode,
  DocumentCreateDtoPrefill,
  DocumentCreateDtoType,
  DocumentDeriveDtoInvoiceKind,
  DocumentDeriveDtoType,
  DocumentDetailResponseDtoOutputInvoiceChannel,
  DocumentDetailResponseDtoOutputInvoiceKind,
  DocumentDetailResponseDtoOutputLinesItemAttachmentsItemDisplayWidth,
  DocumentDetailResponseDtoOutputLinesItemItemKind,
  DocumentDetailResponseDtoOutputLinesItemTaxCode,
  DocumentDetailResponseDtoOutputLnHinweis,
  DocumentDetailResponseDtoOutputQuoteOutcome,
  DocumentDetailResponseDtoOutputStatus,
  DocumentDetailResponseDtoOutputTaxBreakdownItemCode,
  DocumentDetailResponseDtoOutputType,
  DocumentSummaryDtoOutputInvoiceKind,
  DocumentSummaryDtoOutputLnHinweis,
  DocumentSummaryDtoOutputStatus,
  DocumentSummaryDtoOutputType,
  DocumentUpdateDtoLinesItemItemKind,
  DocumentUpdateDtoLinesItemTaxCode,
  DocumentsControllerListDraftOnly,
  DocumentsControllerListStatus,
  DocumentsControllerListType,
  DunningListResponseDtoOutputItemLevel,
  DunningProposalResponseDtoOutputGroupsItemLevel,
  DunningRunDtoSelectionsItemLevel,
  DunningRunResponseDtoOutputCreatedItemLevel,
  EmailLogDtoOutputStatus,
  FahrtEntfernungDtoOutputQuelle,
  FollowupControllerBoardFilter,
  HOLD_REASON_LABELS,
  HealthResponseDtoOutputStatus,
  HoldSetDtoReason,
  INVOICE_KIND_LABELS,
  ITEM_KIND_LABELS,
  ImportMetaDtoOutputEntitiesItemEntity,
  ImportReportDtoOutputEntity,
  ImportReportDtoOutputRowsItemAction,
  ImportRunDtoEncoding,
  ImportRunDtoEntity,
  LEERWOHNUNG_FELDER,
  LEERWOHNUNG_GRENZEN,
  LeerwohnungResponseDtoOutputAufnahmenItemPayloadNeueTuerenItemAnschlag,
  LegacyArchiveResponseDtoOutputAbsencesItemMappedKind,
  LineAttachmentCreateDtoDisplayWidth,
  LineAttachmentCreateDtoMimeType,
  LineAttachmentDtoOutputDisplayWidth,
  LineAttachmentUpdateDtoDisplayWidth,
  LogoUploadDtoMimeType,
  MaintenanceContractCreateDtoStatus,
  MaintenanceContractListResponseDtoOutputItemStatus,
  MaintenanceContractResponseDtoOutputStatus,
  MaintenanceContractUpdateDtoStatus,
  MeResponseDtoOutputRole,
  MembraCommitResultDtoOutputFileType,
  MembraDryRunResultDtoOutputFileType,
  MembraImportDtoEncoding,
  MembraImportDtoFileType,
  MonthCloseResponseDtoOutputStatus,
  MonthResponseDtoOutputAccountEntriesItemType,
  MonthResponseDtoOutputDaysItemAbsenceKind,
  MonthResponseDtoOutputMonthCloseStatus,
  OpenItemsResponseDtoOutputItemsItemDunningLevel,
  OpenItemsResponseDtoOutputItemsItemInvoiceKind,
  OrderAttachmentCreateDtoMimeType,
  OrderCreateDtoCommissionType,
  OrderCreateDtoSource,
  OrderDetailResponseDtoOutputCommissionType,
  OrderDetailResponseDtoOutputDocumentsItemInvoiceKind,
  OrderDetailResponseDtoOutputDocumentsItemStatus,
  OrderDetailResponseDtoOutputDocumentsItemType,
  OrderDetailResponseDtoOutputHoldInfoReason,
  OrderDetailResponseDtoOutputHoldInfoSource,
  OrderDetailResponseDtoOutputSource,
  OrderDetailResponseDtoOutputStatus,
  OrderItemAttachmentCreateDtoDisplayWidth,
  OrderItemAttachmentCreateDtoMimeType,
  OrderItemAttachmentDtoOutputDisplayWidth,
  OrderItemAttachmentUpdateDtoDisplayWidth,
  OrderItemCreateDtoItemKind,
  OrderItemCreateDtoTaxCode,
  OrderItemResponseDtoOutputAttachmentsItemDisplayWidth,
  OrderItemResponseDtoOutputItemKind,
  OrderItemResponseDtoOutputTaxCode,
  OrderItemUpdateDtoItemKind,
  OrderItemUpdateDtoTaxCode,
  OrderListItemDtoOutputCommissionType,
  OrderListItemDtoOutputHoldInfoReason,
  OrderListItemDtoOutputHoldInfoSource,
  OrderListItemDtoOutputSource,
  OrderListItemDtoOutputStatus,
  OrderLoeschVorschauDtoOutputGruppenItemKey,
  OrderLoeschenDtoMitnehmenItem,
  OrderResponseDtoOutputCommissionType,
  OrderResponseDtoOutputHoldInfoReason,
  OrderResponseDtoOutputHoldInfoSource,
  OrderResponseDtoOutputSource,
  OrderResponseDtoOutputStatus,
  OrderUpdateDtoCommissionType,
  OrderUpdateDtoSource,
  OrderUpdateDtoStatus,
  OrderWorkDtoAction,
  OrdersControllerListStatus,
  PLACEHOLDER_CATALOG,
  PLACEHOLDER_GROUPS,
  ParsedPortalOrderDtoOutputPortal,
  ParsedPortalOrderDtoPortal,
  PaymentCreateResponseDtoOutputSummaryStatus,
  PaymentListResponseDtoOutputSummaryStatus,
  PaymentsControllerOpenItemsCsvOverdueOnly,
  PaymentsControllerOpenItemsOverdueOnly,
  PlanEntriesResponseDtoOutputEntriesItemKind,
  PlanEntryCreateDtoKind,
  PlanEntryResponseDtoOutputKind,
  PlantafelControllerDeleteEntryRest,
  PlantafelControllerWeekSaturday,
  PlantafelControllerWeekSunday,
  PlantafelResponseDtoOutputAbsencesItemHalf,
  PlantafelResponseDtoOutputAbsencesItemKind,
  PlantafelResponseDtoOutputAssignmentsItemAusgang,
  PlantafelResponseDtoOutputAssignmentsItemLiveStatusKind,
  PlantafelResponseDtoOutputAssignmentsItemOrderHoldReason,
  PlantafelResponseDtoOutputEntriesItemKind,
  PlantafelResponseDtoOutputUnassignedItemHoldReason,
  PortalFillVorschlagDtoOutputUnterschriftenItemRolle,
  PortalOrderCommitDtoCommissionType,
  PortalOrderCommitDtoPortal,
  PriceListDetailResponseDtoOutputItemsItemItemKind,
  PriceListDetailResponseDtoOutputItemsItemTaxCode,
  PriceListItemCreateDtoItemKind,
  PriceListItemCreateDtoTaxCode,
  PriceListItemResponseDtoOutputItemKind,
  PriceListItemResponseDtoOutputTaxCode,
  PriceListItemUpdateDtoItemKind,
  PriceListItemUpdateDtoTaxCode,
  QuoteBoardResponseDtoOutputItemsItemOutcome,
  RecurringCreateDtoInterval,
  RecurringCreateDtoItemsItemItemKind,
  RecurringCreateDtoItemsItemTaxCode,
  RecurringListResponseDtoOutputItemInterval,
  RecurringListResponseDtoOutputItemItemsItemItemKind,
  RecurringListResponseDtoOutputItemItemsItemTaxCode,
  RecurringResponseDtoOutputInterval,
  RecurringResponseDtoOutputItemsItemItemKind,
  RecurringResponseDtoOutputItemsItemTaxCode,
  RecurringUpdateDtoInterval,
  RecurringUpdateDtoItemsItemItemKind,
  RecurringUpdateDtoItemsItemTaxCode,
  RowCreateDtoFormula,
  RowResponseDtoOutputFormula,
  RowUpdateDtoFormula,
  SearchResultDtoOutputCustomersItemKind,
  SettingsResponseDtoOutputDatevFinalInvoiceMode,
  SettingsResponseDtoOutputHolidayState,
  SettingsResponseDtoOutputMailTransport,
  SettingsResponseDtoOutputSignatureThresholdBasis,
  SettingsUpdateDtoDatevFinalInvoiceMode,
  SettingsUpdateDtoHolidayState,
  SettingsUpdateDtoMailTransport,
  SettingsUpdateDtoSignatureThresholdBasis,
  SheetResponseDtoOutputRowsItemFormula,
  SignatureObligationDtoOutputReason,
  SignatureRequestListDtoOutputRequestsItemStatus,
  SignatureRequestViewDtoOutputStatus,
  TAX_CODE_LABELS,
  TechAbsenceRequestCreateDtoType,
  TechAbsenceRequestCreateResponseDtoOutputRequestStatus,
  TechAbsenceRequestCreateResponseDtoOutputRequestType,
  TechAbsenceRequestEditResponseDtoOutputRequestStatus,
  TechAbsenceRequestEditResponseDtoOutputRequestType,
  TechAbsenceRequestsResponseDtoOutputItemsItemStatus,
  TechAbsenceRequestsResponseDtoOutputItemsItemType,
  TechAttendanceStatusDtoOutputState,
  TechAuftragFotosDtoOutputFotosItemQuelle,
  TechAuftragLeerwohnungDtoOutputAufnahmenItemPayloadNeueTuerenItemAnschlag,
  TechEventsDtoEventsItemType,
  TechEventsResponseDtoOutputResultsItemStatus,
  TechSyncResponseDtoOutputAssignmentsItemOrderCommissionType,
  TechSyncResponseDtoOutputAssignmentsItemOrderHoldInfoReason,
  TechSyncResponseDtoOutputAssignmentsItemOrderHoldInfoSource,
  TechSyncResponseDtoOutputPlanEntriesItemKind,
  TechTeamAuftragDtoOutputOrderCommissionType,
  TechTeamAuftragDtoOutputOrderHoldInfoReason,
  TechTeamAuftragDtoOutputOrderHoldInfoSource,
  TechTimeDayStampsResponseDtoOutputStampsItemKind,
  TechTimeSummaryResponseDtoOutputAccountEntriesItemType,
  TechTimeSummaryResponseDtoOutputDaysItemAbsenceKind,
  TechUploadDtoKind,
  TextTemplateCreateDtoDocType,
  TextTemplateCreateDtoDunningLevel,
  TextTemplateCreateDtoSlot,
  TextTemplateDtoOutputDocType,
  TextTemplateDtoOutputDunningLevel,
  TextTemplateDtoOutputSlot,
  TextTemplateListDtoOutputItemDocType,
  TextTemplateListDtoOutputItemDunningLevel,
  TextTemplateListDtoOutputItemSlot,
  TextTemplateUpdateDtoDocType,
  TextTemplateUpdateDtoDunningLevel,
  TextTemplatesControllerListSlot,
  TicketCreateDtoAttachmentsItemMimeType,
  TicketCreateDtoPriority,
  TicketDetailDtoOutputPriority,
  TicketDetailDtoOutputStatus,
  TicketListDtoOutputItemsItemPriority,
  TicketListDtoOutputItemsItemStatus,
  TicketReplyDtoAttachmentsItemMimeType,
  TicketUpdateDtoPriority,
  TicketUpdateDtoStatus,
  TicketsControllerListIncludeDone,
  TicketsControllerListMine,
  TicketsControllerListStatus,
  TimeAccountEntryCreateDtoType,
  TimeAccountEntryResponseDtoOutputType,
  TravelBillingUpdateDtoTravelBillingOneOfFiveMode,
  TravelBillingUpdateDtoTravelBillingOneOfMode,
  TravelBillingUpdateDtoTravelBillingOneOfNineMode,
  TravelBillingUpdateDtoTravelBillingOneOfSevenMode,
  TravelBillingUpdateDtoTravelBillingOneOfThreeMode,
  UserCreateDtoRole,
  UserResponseDtoOutputRole,
  UserUpdateDtoRole,
  VorgangResponseDtoOutputChainItemDunningLevel,
  VorgangResponseDtoOutputChainItemInvoiceKind,
  VorgangResponseDtoOutputChainItemKind,
  VorgangResponseDtoOutputChainItemPaymentStatus,
  VorgangResponseDtoOutputChainItemStatus,
  VorgangResponseDtoOutputChainItemType,
  WrFotoUploadDtoMimeType,
  absenceRequestControllerCorrect,
  absenceRequestControllerDecide,
  absenceRequestControllerList,
  activityControllerList,
  analyticsControllerEmergency,
  analyticsControllerEmergencyCsv,
  analyticsControllerHours,
  analyticsControllerHoursCsv,
  analyticsControllerOpenItems,
  analyticsControllerOpenItemsCsv,
  analyticsControllerOrderHours,
  analyticsControllerOrderHoursCsv,
  analyticsControllerQuotes,
  analyticsControllerQuotesCsv,
  analyticsControllerRevenue,
  analyticsControllerRevenueCsv,
  analyticsControllerUnbilled,
  analyticsControllerUnbilledCsv,
  appReleaseControllerVersion,
  articlesControllerCreate,
  articlesControllerList,
  articlesControllerUpdate,
  attendanceControllerClock,
  attendanceControllerCloseMonth,
  attendanceControllerCorrect,
  attendanceControllerCreateAbsences,
  attendanceControllerCreateAbsencesBulk,
  attendanceControllerCreateTimeAccountEntry,
  attendanceControllerDayDetail,
  attendanceControllerDeleteAbsence,
  attendanceControllerListAbsences,
  attendanceControllerListTimeAccountEntries,
  attendanceControllerLohnCsv,
  attendanceControllerLohnStatus,
  attendanceControllerMonth,
  attendanceControllerMonthClose,
  attendanceControllerNow,
  attendanceControllerOrderWork,
  attendanceControllerOrderWorkStatus,
  attendanceControllerReopenMonth,
  attendanceControllerSendLohnExport,
  attendanceControllerStatus,
  authControllerChangePassword,
  authControllerDisableTotp,
  authControllerEnableTotp,
  authControllerLogin,
  authControllerLogout,
  authControllerMe,
  authControllerSetupTotp,
  authControllerUpdateMe,
  bankImportControllerEinlesen,
  bankImportControllerIgnorieren,
  bankImportControllerListe,
  bankImportControllerPruefen,
  bankImportControllerRechnungen,
  bankImportControllerVorschlaege,
  bankImportControllerZuordnen,
  bankImportControllerZuruecksetzen,
  billingControllerBill,
  billingControllerSetTravelBilling,
  briefingControllerPreview,
  briefingControllerSendTest,
  businessAreasControllerCreate,
  businessAreasControllerList,
  businessAreasControllerUpdate,
  collectiveControllerBillableOrders,
  collectiveControllerCreate,
  customFetch,
  customersControllerCreate,
  customersControllerCreateContact,
  customersControllerCreateFromUnit,
  customersControllerCreatePropertiesBulk,
  customersControllerCreateProperty,
  customersControllerCreateUnit,
  customersControllerFile,
  customersControllerList,
  customersControllerUnits,
  customersControllerUpdate,
  customersControllerUpdateContact,
  customersControllerUpdateProperty,
  customersControllerUpdateUnit,
  dashboardControllerHausmeisterWarteliste,
  dashboardControllerSummary,
  datanormControllerCommit,
  datanormControllerDryRun,
  datanormControllerErrorsCsv,
  datanormControllerSuppliers,
  datevControllerAssignDebtors,
  datevControllerDownload,
  datevControllerExport,
  datevControllerPreview,
  datevControllerRuns,
  devicesControllerCleanup,
  devicesControllerCreatePairingCode,
  devicesControllerDownloadLog,
  devicesControllerList,
  devicesControllerRevoke,
  documentsControllerAddAttachment,
  documentsControllerCancel,
  documentsControllerCorrect,
  documentsControllerCreate,
  documentsControllerDatevUpload,
  documentsControllerDerive,
  documentsControllerDetail,
  documentsControllerDuplicate,
  documentsControllerEmails,
  documentsControllerFinalize,
  documentsControllerList,
  documentsControllerMailDraft,
  documentsControllerMarkSubmitted,
  documentsControllerMergeWorkReports,
  documentsControllerPdf,
  documentsControllerPreviewHtml,
  documentsControllerPreviewPages,
  documentsControllerPreviewPdf,
  documentsControllerRemove,
  documentsControllerRemoveAttachment,
  documentsControllerReopen,
  documentsControllerSend,
  documentsControllerUpdate,
  documentsControllerUpdateAttachment,
  documentsControllerWrFoto,
  documentsControllerWrFotoBild,
  documentsControllerXml,
  dunningControllerList,
  dunningControllerPropose,
  dunningControllerRun,
  followupControllerBoard,
  followupControllerFollowUp,
  followupControllerLost,
  followupControllerWon,
  getAbsenceRequestControllerCorrectMutationOptions,
  getAbsenceRequestControllerCorrectUrl,
  getAbsenceRequestControllerDecideMutationOptions,
  getAbsenceRequestControllerDecideUrl,
  getAbsenceRequestControllerListQueryKey,
  getAbsenceRequestControllerListQueryOptions,
  getAbsenceRequestControllerListUrl,
  getActivityControllerListQueryKey,
  getActivityControllerListQueryOptions,
  getActivityControllerListUrl,
  getAnalyticsControllerEmergencyCsvQueryKey,
  getAnalyticsControllerEmergencyCsvQueryOptions,
  getAnalyticsControllerEmergencyCsvUrl,
  getAnalyticsControllerEmergencyQueryKey,
  getAnalyticsControllerEmergencyQueryOptions,
  getAnalyticsControllerEmergencyUrl,
  getAnalyticsControllerHoursCsvQueryKey,
  getAnalyticsControllerHoursCsvQueryOptions,
  getAnalyticsControllerHoursCsvUrl,
  getAnalyticsControllerHoursQueryKey,
  getAnalyticsControllerHoursQueryOptions,
  getAnalyticsControllerHoursUrl,
  getAnalyticsControllerOpenItemsCsvQueryKey,
  getAnalyticsControllerOpenItemsCsvQueryOptions,
  getAnalyticsControllerOpenItemsCsvUrl,
  getAnalyticsControllerOpenItemsQueryKey,
  getAnalyticsControllerOpenItemsQueryOptions,
  getAnalyticsControllerOpenItemsUrl,
  getAnalyticsControllerOrderHoursCsvQueryKey,
  getAnalyticsControllerOrderHoursCsvQueryOptions,
  getAnalyticsControllerOrderHoursCsvUrl,
  getAnalyticsControllerOrderHoursQueryKey,
  getAnalyticsControllerOrderHoursQueryOptions,
  getAnalyticsControllerOrderHoursUrl,
  getAnalyticsControllerQuotesCsvQueryKey,
  getAnalyticsControllerQuotesCsvQueryOptions,
  getAnalyticsControllerQuotesCsvUrl,
  getAnalyticsControllerQuotesQueryKey,
  getAnalyticsControllerQuotesQueryOptions,
  getAnalyticsControllerQuotesUrl,
  getAnalyticsControllerRevenueCsvQueryKey,
  getAnalyticsControllerRevenueCsvQueryOptions,
  getAnalyticsControllerRevenueCsvUrl,
  getAnalyticsControllerRevenueQueryKey,
  getAnalyticsControllerRevenueQueryOptions,
  getAnalyticsControllerRevenueUrl,
  getAnalyticsControllerUnbilledCsvQueryKey,
  getAnalyticsControllerUnbilledCsvQueryOptions,
  getAnalyticsControllerUnbilledCsvUrl,
  getAnalyticsControllerUnbilledQueryKey,
  getAnalyticsControllerUnbilledQueryOptions,
  getAnalyticsControllerUnbilledUrl,
  getAppReleaseControllerVersionQueryKey,
  getAppReleaseControllerVersionQueryOptions,
  getAppReleaseControllerVersionUrl,
  getArticlesControllerCreateMutationOptions,
  getArticlesControllerCreateUrl,
  getArticlesControllerListQueryKey,
  getArticlesControllerListQueryOptions,
  getArticlesControllerListUrl,
  getArticlesControllerUpdateMutationOptions,
  getArticlesControllerUpdateUrl,
  getAttendanceControllerClockMutationOptions,
  getAttendanceControllerClockUrl,
  getAttendanceControllerCloseMonthMutationOptions,
  getAttendanceControllerCloseMonthUrl,
  getAttendanceControllerCorrectMutationOptions,
  getAttendanceControllerCorrectUrl,
  getAttendanceControllerCreateAbsencesBulkMutationOptions,
  getAttendanceControllerCreateAbsencesBulkUrl,
  getAttendanceControllerCreateAbsencesMutationOptions,
  getAttendanceControllerCreateAbsencesUrl,
  getAttendanceControllerCreateTimeAccountEntryMutationOptions,
  getAttendanceControllerCreateTimeAccountEntryUrl,
  getAttendanceControllerDayDetailQueryKey,
  getAttendanceControllerDayDetailQueryOptions,
  getAttendanceControllerDayDetailUrl,
  getAttendanceControllerDeleteAbsenceMutationOptions,
  getAttendanceControllerDeleteAbsenceUrl,
  getAttendanceControllerListAbsencesQueryKey,
  getAttendanceControllerListAbsencesQueryOptions,
  getAttendanceControllerListAbsencesUrl,
  getAttendanceControllerListTimeAccountEntriesQueryKey,
  getAttendanceControllerListTimeAccountEntriesQueryOptions,
  getAttendanceControllerListTimeAccountEntriesUrl,
  getAttendanceControllerLohnCsvQueryKey,
  getAttendanceControllerLohnCsvQueryOptions,
  getAttendanceControllerLohnCsvUrl,
  getAttendanceControllerLohnStatusQueryKey,
  getAttendanceControllerLohnStatusQueryOptions,
  getAttendanceControllerLohnStatusUrl,
  getAttendanceControllerMonthCloseQueryKey,
  getAttendanceControllerMonthCloseQueryOptions,
  getAttendanceControllerMonthCloseUrl,
  getAttendanceControllerMonthQueryKey,
  getAttendanceControllerMonthQueryOptions,
  getAttendanceControllerMonthUrl,
  getAttendanceControllerNowQueryKey,
  getAttendanceControllerNowQueryOptions,
  getAttendanceControllerNowUrl,
  getAttendanceControllerOrderWorkMutationOptions,
  getAttendanceControllerOrderWorkStatusQueryKey,
  getAttendanceControllerOrderWorkStatusQueryOptions,
  getAttendanceControllerOrderWorkStatusUrl,
  getAttendanceControllerOrderWorkUrl,
  getAttendanceControllerReopenMonthMutationOptions,
  getAttendanceControllerReopenMonthUrl,
  getAttendanceControllerSendLohnExportMutationOptions,
  getAttendanceControllerSendLohnExportUrl,
  getAttendanceControllerStatusQueryKey,
  getAttendanceControllerStatusQueryOptions,
  getAttendanceControllerStatusUrl,
  getAuthControllerChangePasswordMutationOptions,
  getAuthControllerChangePasswordUrl,
  getAuthControllerDisableTotpMutationOptions,
  getAuthControllerDisableTotpUrl,
  getAuthControllerEnableTotpMutationOptions,
  getAuthControllerEnableTotpUrl,
  getAuthControllerLoginMutationOptions,
  getAuthControllerLoginUrl,
  getAuthControllerLogoutMutationOptions,
  getAuthControllerLogoutUrl,
  getAuthControllerMeQueryKey,
  getAuthControllerMeQueryOptions,
  getAuthControllerMeUrl,
  getAuthControllerSetupTotpMutationOptions,
  getAuthControllerSetupTotpUrl,
  getAuthControllerUpdateMeMutationOptions,
  getAuthControllerUpdateMeUrl,
  getBankImportControllerEinlesenMutationOptions,
  getBankImportControllerEinlesenUrl,
  getBankImportControllerIgnorierenMutationOptions,
  getBankImportControllerIgnorierenUrl,
  getBankImportControllerListeQueryKey,
  getBankImportControllerListeQueryOptions,
  getBankImportControllerListeUrl,
  getBankImportControllerPruefenMutationOptions,
  getBankImportControllerPruefenUrl,
  getBankImportControllerRechnungenQueryKey,
  getBankImportControllerRechnungenQueryOptions,
  getBankImportControllerRechnungenUrl,
  getBankImportControllerVorschlaegeQueryKey,
  getBankImportControllerVorschlaegeQueryOptions,
  getBankImportControllerVorschlaegeUrl,
  getBankImportControllerZuordnenMutationOptions,
  getBankImportControllerZuordnenUrl,
  getBankImportControllerZuruecksetzenMutationOptions,
  getBankImportControllerZuruecksetzenUrl,
  getBillingControllerBillMutationOptions,
  getBillingControllerBillUrl,
  getBillingControllerSetTravelBillingMutationOptions,
  getBillingControllerSetTravelBillingUrl,
  getBriefingControllerPreviewQueryKey,
  getBriefingControllerPreviewQueryOptions,
  getBriefingControllerPreviewUrl,
  getBriefingControllerSendTestMutationOptions,
  getBriefingControllerSendTestUrl,
  getBusinessAreasControllerCreateMutationOptions,
  getBusinessAreasControllerCreateUrl,
  getBusinessAreasControllerListQueryKey,
  getBusinessAreasControllerListQueryOptions,
  getBusinessAreasControllerListUrl,
  getBusinessAreasControllerUpdateMutationOptions,
  getBusinessAreasControllerUpdateUrl,
  getCollectiveControllerBillableOrdersQueryKey,
  getCollectiveControllerBillableOrdersQueryOptions,
  getCollectiveControllerBillableOrdersUrl,
  getCollectiveControllerCreateMutationOptions,
  getCollectiveControllerCreateUrl,
  getCustomersControllerCreateContactMutationOptions,
  getCustomersControllerCreateContactUrl,
  getCustomersControllerCreateFromUnitMutationOptions,
  getCustomersControllerCreateFromUnitUrl,
  getCustomersControllerCreateMutationOptions,
  getCustomersControllerCreatePropertiesBulkMutationOptions,
  getCustomersControllerCreatePropertiesBulkUrl,
  getCustomersControllerCreatePropertyMutationOptions,
  getCustomersControllerCreatePropertyUrl,
  getCustomersControllerCreateUnitMutationOptions,
  getCustomersControllerCreateUnitUrl,
  getCustomersControllerCreateUrl,
  getCustomersControllerFileQueryKey,
  getCustomersControllerFileQueryOptions,
  getCustomersControllerFileUrl,
  getCustomersControllerListQueryKey,
  getCustomersControllerListQueryOptions,
  getCustomersControllerListUrl,
  getCustomersControllerUnitsQueryKey,
  getCustomersControllerUnitsQueryOptions,
  getCustomersControllerUnitsUrl,
  getCustomersControllerUpdateContactMutationOptions,
  getCustomersControllerUpdateContactUrl,
  getCustomersControllerUpdateMutationOptions,
  getCustomersControllerUpdatePropertyMutationOptions,
  getCustomersControllerUpdatePropertyUrl,
  getCustomersControllerUpdateUnitMutationOptions,
  getCustomersControllerUpdateUnitUrl,
  getCustomersControllerUpdateUrl,
  getDashboardControllerHausmeisterWartelisteQueryKey,
  getDashboardControllerHausmeisterWartelisteQueryOptions,
  getDashboardControllerHausmeisterWartelisteUrl,
  getDashboardControllerSummaryQueryKey,
  getDashboardControllerSummaryQueryOptions,
  getDashboardControllerSummaryUrl,
  getDatanormControllerCommitMutationOptions,
  getDatanormControllerCommitUrl,
  getDatanormControllerDryRunMutationOptions,
  getDatanormControllerDryRunUrl,
  getDatanormControllerErrorsCsvQueryKey,
  getDatanormControllerErrorsCsvQueryOptions,
  getDatanormControllerErrorsCsvUrl,
  getDatanormControllerSuppliersQueryKey,
  getDatanormControllerSuppliersQueryOptions,
  getDatanormControllerSuppliersUrl,
  getDatevControllerAssignDebtorsMutationOptions,
  getDatevControllerAssignDebtorsUrl,
  getDatevControllerDownloadQueryKey,
  getDatevControllerDownloadQueryOptions,
  getDatevControllerDownloadUrl,
  getDatevControllerExportMutationOptions,
  getDatevControllerExportUrl,
  getDatevControllerPreviewQueryKey,
  getDatevControllerPreviewQueryOptions,
  getDatevControllerPreviewUrl,
  getDatevControllerRunsQueryKey,
  getDatevControllerRunsQueryOptions,
  getDatevControllerRunsUrl,
  getDevicesControllerCleanupMutationOptions,
  getDevicesControllerCleanupUrl,
  getDevicesControllerCreatePairingCodeMutationOptions,
  getDevicesControllerCreatePairingCodeUrl,
  getDevicesControllerDownloadLogQueryKey,
  getDevicesControllerDownloadLogQueryOptions,
  getDevicesControllerDownloadLogUrl,
  getDevicesControllerListQueryKey,
  getDevicesControllerListQueryOptions,
  getDevicesControllerListUrl,
  getDevicesControllerRevokeMutationOptions,
  getDevicesControllerRevokeUrl,
  getDocumentsControllerAddAttachmentMutationOptions,
  getDocumentsControllerAddAttachmentUrl,
  getDocumentsControllerCancelMutationOptions,
  getDocumentsControllerCancelUrl,
  getDocumentsControllerCorrectMutationOptions,
  getDocumentsControllerCorrectUrl,
  getDocumentsControllerCreateMutationOptions,
  getDocumentsControllerCreateUrl,
  getDocumentsControllerDatevUploadMutationOptions,
  getDocumentsControllerDatevUploadUrl,
  getDocumentsControllerDeriveMutationOptions,
  getDocumentsControllerDeriveUrl,
  getDocumentsControllerDetailQueryKey,
  getDocumentsControllerDetailQueryOptions,
  getDocumentsControllerDetailUrl,
  getDocumentsControllerDuplicateMutationOptions,
  getDocumentsControllerDuplicateUrl,
  getDocumentsControllerEmailsQueryKey,
  getDocumentsControllerEmailsQueryOptions,
  getDocumentsControllerEmailsUrl,
  getDocumentsControllerFinalizeMutationOptions,
  getDocumentsControllerFinalizeUrl,
  getDocumentsControllerListQueryKey,
  getDocumentsControllerListQueryOptions,
  getDocumentsControllerListUrl,
  getDocumentsControllerMailDraftQueryKey,
  getDocumentsControllerMailDraftQueryOptions,
  getDocumentsControllerMailDraftUrl,
  getDocumentsControllerMarkSubmittedMutationOptions,
  getDocumentsControllerMarkSubmittedUrl,
  getDocumentsControllerMergeWorkReportsMutationOptions,
  getDocumentsControllerMergeWorkReportsUrl,
  getDocumentsControllerPdfQueryKey,
  getDocumentsControllerPdfQueryOptions,
  getDocumentsControllerPdfUrl,
  getDocumentsControllerPreviewHtmlQueryKey,
  getDocumentsControllerPreviewHtmlQueryOptions,
  getDocumentsControllerPreviewHtmlUrl,
  getDocumentsControllerPreviewPagesQueryKey,
  getDocumentsControllerPreviewPagesQueryOptions,
  getDocumentsControllerPreviewPagesUrl,
  getDocumentsControllerPreviewPdfQueryKey,
  getDocumentsControllerPreviewPdfQueryOptions,
  getDocumentsControllerPreviewPdfUrl,
  getDocumentsControllerRemoveAttachmentMutationOptions,
  getDocumentsControllerRemoveAttachmentUrl,
  getDocumentsControllerRemoveMutationOptions,
  getDocumentsControllerRemoveUrl,
  getDocumentsControllerReopenMutationOptions,
  getDocumentsControllerReopenUrl,
  getDocumentsControllerSendMutationOptions,
  getDocumentsControllerSendUrl,
  getDocumentsControllerUpdateAttachmentMutationOptions,
  getDocumentsControllerUpdateAttachmentUrl,
  getDocumentsControllerUpdateMutationOptions,
  getDocumentsControllerUpdateUrl,
  getDocumentsControllerWrFotoBildQueryKey,
  getDocumentsControllerWrFotoBildQueryOptions,
  getDocumentsControllerWrFotoBildUrl,
  getDocumentsControllerWrFotoMutationOptions,
  getDocumentsControllerWrFotoUrl,
  getDocumentsControllerXmlQueryKey,
  getDocumentsControllerXmlQueryOptions,
  getDocumentsControllerXmlUrl,
  getDunningControllerListQueryKey,
  getDunningControllerListQueryOptions,
  getDunningControllerListUrl,
  getDunningControllerProposeQueryKey,
  getDunningControllerProposeQueryOptions,
  getDunningControllerProposeUrl,
  getDunningControllerRunMutationOptions,
  getDunningControllerRunUrl,
  getFollowupControllerBoardQueryKey,
  getFollowupControllerBoardQueryOptions,
  getFollowupControllerBoardUrl,
  getFollowupControllerFollowUpMutationOptions,
  getFollowupControllerFollowUpUrl,
  getFollowupControllerLostMutationOptions,
  getFollowupControllerLostUrl,
  getFollowupControllerWonMutationOptions,
  getFollowupControllerWonUrl,
  getHealthControllerCheckQueryKey,
  getHealthControllerCheckQueryOptions,
  getHealthControllerCheckUrl,
  getKalenderControllerBeendenMutationOptions,
  getKalenderControllerBeendenUrl,
  getKalenderControllerErzeugenMutationOptions,
  getKalenderControllerErzeugenUrl,
  getKalenderControllerFeedQueryKey,
  getKalenderControllerFeedQueryOptions,
  getKalenderControllerFeedUrl,
  getKalenderControllerStatusQueryKey,
  getKalenderControllerStatusQueryOptions,
  getKalenderControllerStatusUrl,
  getLegacyArchiveControllerArchiveQueryKey,
  getLegacyArchiveControllerArchiveQueryOptions,
  getLegacyArchiveControllerArchiveUrl,
  getLegacyArchiveControllerStartwerteApplyMutationOptions,
  getLegacyArchiveControllerStartwerteApplyUrl,
  getLegacyArchiveControllerStartwerteProposalQueryKey,
  getLegacyArchiveControllerStartwerteProposalQueryOptions,
  getLegacyArchiveControllerStartwerteProposalUrl,
  getLvImportControllerCommitMutationOptions,
  getLvImportControllerCommitUrl,
  getLvImportControllerDryRunMutationOptions,
  getLvImportControllerDryRunUrl,
  getMaintenanceContractsControllerCreateMutationOptions,
  getMaintenanceContractsControllerCreateUrl,
  getMaintenanceContractsControllerGetQueryKey,
  getMaintenanceContractsControllerGetQueryOptions,
  getMaintenanceContractsControllerGetUrl,
  getMaintenanceContractsControllerListQueryKey,
  getMaintenanceContractsControllerListQueryOptions,
  getMaintenanceContractsControllerListUrl,
  getMaintenanceContractsControllerUpdateMutationOptions,
  getMaintenanceContractsControllerUpdateUrl,
  getMasterDataDeleteControllerArticleMutationOptions,
  getMasterDataDeleteControllerArticleUrl,
  getMasterDataDeleteControllerBusinessAreaMutationOptions,
  getMasterDataDeleteControllerBusinessAreaUrl,
  getMasterDataDeleteControllerContactMutationOptions,
  getMasterDataDeleteControllerContactUrl,
  getMasterDataDeleteControllerCustomerMutationOptions,
  getMasterDataDeleteControllerCustomerUrl,
  getMasterDataDeleteControllerPriceListMutationOptions,
  getMasterDataDeleteControllerPriceListUrl,
  getMasterDataDeleteControllerPropertyMutationOptions,
  getMasterDataDeleteControllerPropertyUrl,
  getMasterDataDeleteControllerUnitMutationOptions,
  getMasterDataDeleteControllerUnitUrl,
  getMaterialControllerAddAttachmentMutationOptions,
  getMaterialControllerAddAttachmentUrl,
  getMaterialControllerAppReceiptsQueryKey,
  getMaterialControllerAppReceiptsQueryOptions,
  getMaterialControllerAppReceiptsUrl,
  getMaterialControllerCreateMutationOptions,
  getMaterialControllerCreateUrl,
  getMaterialControllerDeleteAttachmentMutationOptions,
  getMaterialControllerDeleteAttachmentUrl,
  getMaterialControllerDownloadQueryKey,
  getMaterialControllerDownloadQueryOptions,
  getMaterialControllerDownloadUrl,
  getMaterialControllerMarkDeliveredMutationOptions,
  getMaterialControllerMarkDeliveredUrl,
  getMaterialControllerOverviewQueryKey,
  getMaterialControllerOverviewQueryOptions,
  getMaterialControllerOverviewUrl,
  getMaterialControllerRemoveMutationOptions,
  getMaterialControllerRemoveUrl,
  getMeasurementControllerAddItemAttachmentMutationOptions,
  getMeasurementControllerAddItemAttachmentUrl,
  getMeasurementControllerCreateItemMutationOptions,
  getMeasurementControllerCreateItemUrl,
  getMeasurementControllerCreateRowMutationOptions,
  getMeasurementControllerCreateRowUrl,
  getMeasurementControllerCreateSheetMutationOptions,
  getMeasurementControllerCreateSheetUrl,
  getMeasurementControllerDeleteItemMutationOptions,
  getMeasurementControllerDeleteItemUrl,
  getMeasurementControllerDeleteRowMutationOptions,
  getMeasurementControllerDeleteRowUrl,
  getMeasurementControllerDeleteSheetMutationOptions,
  getMeasurementControllerDeleteSheetUrl,
  getMeasurementControllerGetSheetQueryKey,
  getMeasurementControllerGetSheetQueryOptions,
  getMeasurementControllerGetSheetUrl,
  getMeasurementControllerItemAttachmentFileQueryKey,
  getMeasurementControllerItemAttachmentFileQueryOptions,
  getMeasurementControllerItemAttachmentFileUrl,
  getMeasurementControllerItemsFromWorkReportMutationOptions,
  getMeasurementControllerItemsFromWorkReportUrl,
  getMeasurementControllerListItemsQueryKey,
  getMeasurementControllerListItemsQueryOptions,
  getMeasurementControllerListItemsUrl,
  getMeasurementControllerListSheetsQueryKey,
  getMeasurementControllerListSheetsQueryOptions,
  getMeasurementControllerListSheetsUrl,
  getMeasurementControllerRemoveItemAttachmentMutationOptions,
  getMeasurementControllerRemoveItemAttachmentUrl,
  getMeasurementControllerReorderItemsMutationOptions,
  getMeasurementControllerReorderItemsUrl,
  getMeasurementControllerSheetPdfQueryKey,
  getMeasurementControllerSheetPdfQueryOptions,
  getMeasurementControllerSheetPdfUrl,
  getMeasurementControllerTransferMutationOptions,
  getMeasurementControllerTransferUrl,
  getMeasurementControllerUpdateItemAttachmentMutationOptions,
  getMeasurementControllerUpdateItemAttachmentUrl,
  getMeasurementControllerUpdateItemMutationOptions,
  getMeasurementControllerUpdateItemUrl,
  getMeasurementControllerUpdateRowMutationOptions,
  getMeasurementControllerUpdateRowUrl,
  getMeasurementControllerUpdateSheetMutationOptions,
  getMeasurementControllerUpdateSheetUrl,
  getOrdersControllerAddAttachmentMutationOptions,
  getOrdersControllerAddAttachmentUrl,
  getOrdersControllerAppDatenQueryKey,
  getOrdersControllerAppDatenQueryOptions,
  getOrdersControllerAppDatenUrl,
  getOrdersControllerAttachmentFileQueryKey,
  getOrdersControllerAttachmentFileQueryOptions,
  getOrdersControllerAttachmentFileUrl,
  getOrdersControllerCreateMutationOptions,
  getOrdersControllerCreateUrl,
  getOrdersControllerDetailQueryKey,
  getOrdersControllerDetailQueryOptions,
  getOrdersControllerDetailUrl,
  getOrdersControllerFromQuoteMutationOptions,
  getOrdersControllerFromQuoteUrl,
  getOrdersControllerLeerwohnungLoeschenMutationOptions,
  getOrdersControllerLeerwohnungLoeschenUrl,
  getOrdersControllerLeerwohnungQueryKey,
  getOrdersControllerLeerwohnungQueryOptions,
  getOrdersControllerLeerwohnungUrl,
  getOrdersControllerListQueryKey,
  getOrdersControllerListQueryOptions,
  getOrdersControllerListUrl,
  getOrdersControllerLoeschVorschauQueryKey,
  getOrdersControllerLoeschVorschauQueryOptions,
  getOrdersControllerLoeschVorschauUrl,
  getOrdersControllerLoeschenMutationOptions,
  getOrdersControllerLoeschenUrl,
  getOrdersControllerNachtragMutationOptions,
  getOrdersControllerNachtragUrl,
  getOrdersControllerNavCountersQueryKey,
  getOrdersControllerNavCountersQueryOptions,
  getOrdersControllerNavCountersUrl,
  getOrdersControllerPreviewHoldMailQueryKey,
  getOrdersControllerPreviewHoldMailQueryOptions,
  getOrdersControllerPreviewHoldMailUrl,
  getOrdersControllerRemoveAttachmentMutationOptions,
  getOrdersControllerRemoveAttachmentUrl,
  getOrdersControllerRemoveMutationOptions,
  getOrdersControllerRemoveUrl,
  getOrdersControllerResolveHoldMutationOptions,
  getOrdersControllerResolveHoldUrl,
  getOrdersControllerSendHoldMailMutationOptions,
  getOrdersControllerSendHoldMailUrl,
  getOrdersControllerSetHoldMutationOptions,
  getOrdersControllerSetHoldUrl,
  getOrdersControllerStornoMutationOptions,
  getOrdersControllerStornoUrl,
  getOrdersControllerUpdateAttachmentMutationOptions,
  getOrdersControllerUpdateAttachmentUrl,
  getOrdersControllerUpdateMutationOptions,
  getOrdersControllerUpdateUrl,
  getOrdersControllerUploadQueryKey,
  getOrdersControllerUploadQueryOptions,
  getOrdersControllerUploadUrl,
  getOrdersControllerVorgangQueryKey,
  getOrdersControllerVorgangQueryOptions,
  getOrdersControllerVorgangUrl,
  getPaymentsControllerCreateMutationOptions,
  getPaymentsControllerCreateUrl,
  getPaymentsControllerListQueryKey,
  getPaymentsControllerListQueryOptions,
  getPaymentsControllerListUrl,
  getPaymentsControllerOpenItemsCsvQueryKey,
  getPaymentsControllerOpenItemsCsvQueryOptions,
  getPaymentsControllerOpenItemsCsvUrl,
  getPaymentsControllerOpenItemsQueryKey,
  getPaymentsControllerOpenItemsQueryOptions,
  getPaymentsControllerOpenItemsUrl,
  getPaymentsControllerRemoveMutationOptions,
  getPaymentsControllerRemoveUrl,
  getPlantafelControllerCompleteMutationOptions,
  getPlantafelControllerCompleteUrl,
  getPlantafelControllerCreateEntriesMutationOptions,
  getPlantafelControllerCreateEntriesUrl,
  getPlantafelControllerCreateMutationOptions,
  getPlantafelControllerCreateUrl,
  getPlantafelControllerDeleteEntryMutationOptions,
  getPlantafelControllerDeleteEntryUrl,
  getPlantafelControllerEntryTitlesQueryKey,
  getPlantafelControllerEntryTitlesQueryOptions,
  getPlantafelControllerEntryTitlesUrl,
  getPlantafelControllerExtendMutationOptions,
  getPlantafelControllerExtendUrl,
  getPlantafelControllerMoveMutationOptions,
  getPlantafelControllerMoveUrl,
  getPlantafelControllerRevokeMutationOptions,
  getPlantafelControllerRevokeUrl,
  getPlantafelControllerUpdateEntryMutationOptions,
  getPlantafelControllerUpdateEntryUrl,
  getPlantafelControllerWeekQueryKey,
  getPlantafelControllerWeekQueryOptions,
  getPlantafelControllerWeekUrl,
  getPortalOrderControllerBauzeitenplanMutationOptions,
  getPortalOrderControllerBauzeitenplanUrl,
  getPortalOrderControllerCommitMutationOptions,
  getPortalOrderControllerCommitUrl,
  getPortalOrderControllerPreviewMutationOptions,
  getPortalOrderControllerPreviewUrl,
  getPortalPdfControllerFuellenMutationOptions,
  getPortalPdfControllerFuellenUrl,
  getPortalPdfControllerVorschlagQueryKey,
  getPortalPdfControllerVorschlagQueryOptions,
  getPortalPdfControllerVorschlagUrl,
  getPriceListsControllerCreateItemMutationOptions,
  getPriceListsControllerCreateItemUrl,
  getPriceListsControllerCreateMutationOptions,
  getPriceListsControllerCreateUrl,
  getPriceListsControllerDeleteItemMutationOptions,
  getPriceListsControllerDeleteItemUrl,
  getPriceListsControllerDetailQueryKey,
  getPriceListsControllerDetailQueryOptions,
  getPriceListsControllerDetailUrl,
  getPriceListsControllerImportCsvMutationOptions,
  getPriceListsControllerImportCsvUrl,
  getPriceListsControllerListQueryKey,
  getPriceListsControllerListQueryOptions,
  getPriceListsControllerListUrl,
  getPriceListsControllerUpdateItemMutationOptions,
  getPriceListsControllerUpdateItemUrl,
  getPriceListsControllerUpdateMutationOptions,
  getPriceListsControllerUpdateUrl,
  getPublicSignatureControllerPageQueryKey,
  getPublicSignatureControllerPageQueryOptions,
  getPublicSignatureControllerPageUrl,
  getPublicSignatureControllerPdfQueryKey,
  getPublicSignatureControllerPdfQueryOptions,
  getPublicSignatureControllerPdfUrl,
  getPublicSignatureControllerSubmitMutationOptions,
  getPublicSignatureControllerSubmitUrl,
  getRecurringControllerCreateMutationOptions,
  getRecurringControllerCreateUrl,
  getRecurringControllerListQueryKey,
  getRecurringControllerListQueryOptions,
  getRecurringControllerListUrl,
  getRecurringControllerRemoveMutationOptions,
  getRecurringControllerRemoveUrl,
  getRecurringControllerRunMutationOptions,
  getRecurringControllerRunUrl,
  getRecurringControllerUpdateMutationOptions,
  getRecurringControllerUpdateUrl,
  getRolesControllerCreateMutationOptions,
  getRolesControllerCreateUrl,
  getRolesControllerListQueryKey,
  getRolesControllerListQueryOptions,
  getRolesControllerListUrl,
  getRolesControllerRemoveMutationOptions,
  getRolesControllerRemoveUrl,
  getRolesControllerUpdateMutationOptions,
  getRolesControllerUpdateUrl,
  getRoutingControllerCustomerDistanceMutationOptions,
  getRoutingControllerCustomerDistanceUrl,
  getRoutingControllerDistanceMutationOptions,
  getRoutingControllerDistanceUrl,
  getRoutingControllerDocumentDistanceQueryKey,
  getRoutingControllerDocumentDistanceQueryOptions,
  getRoutingControllerDocumentDistanceUrl,
  getRoutingControllerEntfernungslaufStatusQueryKey,
  getRoutingControllerEntfernungslaufStatusQueryOptions,
  getRoutingControllerEntfernungslaufStatusUrl,
  getRoutingControllerPlzVorschlagQueryKey,
  getRoutingControllerPlzVorschlagQueryOptions,
  getRoutingControllerPlzVorschlagUrl,
  getRoutingControllerStartEntfernungslaufMutationOptions,
  getRoutingControllerStartEntfernungslaufUrl,
  getSearchControllerRunQueryKey,
  getSearchControllerRunQueryOptions,
  getSearchControllerRunUrl,
  getSettingsControllerGetQueryKey,
  getSettingsControllerGetQueryOptions,
  getSettingsControllerGetUrl,
  getSettingsControllerRemoveLogoMutationOptions,
  getSettingsControllerRemoveLogoUrl,
  getSettingsControllerSetNextNumbersMutationOptions,
  getSettingsControllerSetNextNumbersUrl,
  getSettingsControllerUpdateMutationOptions,
  getSettingsControllerUpdateUrl,
  getSettingsControllerUploadLogoMutationOptions,
  getSettingsControllerUploadLogoUrl,
  getSettingsLogoControllerLogoQueryKey,
  getSettingsLogoControllerLogoQueryOptions,
  getSettingsLogoControllerLogoUrl,
  getSignatureLinkControllerListQueryKey,
  getSignatureLinkControllerListQueryOptions,
  getSignatureLinkControllerListUrl,
  getSignatureLinkControllerObligationQueryKey,
  getSignatureLinkControllerObligationQueryOptions,
  getSignatureLinkControllerObligationUrl,
  getSignatureLinkControllerRequestSignatureMutationOptions,
  getSignatureLinkControllerRequestSignatureUrl,
  getSignatureLinkControllerRevokeMutationOptions,
  getSignatureLinkControllerRevokeUrl,
  getTechControllerAbsenceRequestsQueryKey,
  getTechControllerAbsenceRequestsQueryOptions,
  getTechControllerAbsenceRequestsUrl,
  getTechControllerAttendanceStatusQueryKey,
  getTechControllerAttendanceStatusQueryOptions,
  getTechControllerAttendanceStatusUrl,
  getTechControllerAuftragsAnhaengeQueryKey,
  getTechControllerAuftragsAnhaengeQueryOptions,
  getTechControllerAuftragsAnhaengeUrl,
  getTechControllerAuftragsAnhangQueryKey,
  getTechControllerAuftragsAnhangQueryOptions,
  getTechControllerAuftragsAnhangUrl,
  getTechControllerAuftragsFotoQueryKey,
  getTechControllerAuftragsFotoQueryOptions,
  getTechControllerAuftragsFotoUrl,
  getTechControllerAuftragsFotosQueryKey,
  getTechControllerAuftragsFotosQueryOptions,
  getTechControllerAuftragsFotosUrl,
  getTechControllerAuftragsLeerwohnungQueryKey,
  getTechControllerAuftragsLeerwohnungQueryOptions,
  getTechControllerAuftragsLeerwohnungUrl,
  getTechControllerCancelAbsenceRequestMutationOptions,
  getTechControllerCancelAbsenceRequestUrl,
  getTechControllerCatalogQueryKey,
  getTechControllerCatalogQueryOptions,
  getTechControllerCatalogUrl,
  getTechControllerCreateAbsenceRequestMutationOptions,
  getTechControllerCreateAbsenceRequestUrl,
  getTechControllerCreateOrderMutationOptions,
  getTechControllerCreateOrderUrl,
  getTechControllerCustomersQueryKey,
  getTechControllerCustomersQueryOptions,
  getTechControllerCustomersUrl,
  getTechControllerDayStampsQueryKey,
  getTechControllerDayStampsQueryOptions,
  getTechControllerDayStampsUrl,
  getTechControllerEditAbsenceRequestMutationOptions,
  getTechControllerEditAbsenceRequestUrl,
  getTechControllerLogsMutationOptions,
  getTechControllerLogsUrl,
  getTechControllerOffeneBestellungenQueryKey,
  getTechControllerOffeneBestellungenQueryOptions,
  getTechControllerOffeneBestellungenUrl,
  getTechControllerPushEventsMutationOptions,
  getTechControllerPushEventsUrl,
  getTechControllerSearchRunQueryKey,
  getTechControllerSearchRunQueryOptions,
  getTechControllerSearchRunUrl,
  getTechControllerSyncPullQueryKey,
  getTechControllerSyncPullQueryOptions,
  getTechControllerSyncPullUrl,
  getTechControllerTeamAuftragQueryKey,
  getTechControllerTeamAuftragQueryOptions,
  getTechControllerTeamAuftragUrl,
  getTechControllerTeamFotoQueryKey,
  getTechControllerTeamFotoQueryOptions,
  getTechControllerTeamFotoUrl,
  getTechControllerTeamKollegenQueryKey,
  getTechControllerTeamKollegenQueryOptions,
  getTechControllerTeamKollegenUrl,
  getTechControllerTeamWocheQueryKey,
  getTechControllerTeamWocheQueryOptions,
  getTechControllerTeamWocheUrl,
  getTechControllerTimeSummaryQueryKey,
  getTechControllerTimeSummaryQueryOptions,
  getTechControllerTimeSummaryUrl,
  getTechControllerUploadMutationOptions,
  getTechControllerUploadUrl,
  getTechControllerWareneingangMutationOptions,
  getTechControllerWareneingangUrl,
  getTechPairControllerPairMutationOptions,
  getTechPairControllerPairUrl,
  getTechPairControllerPairWithCodeMutationOptions,
  getTechPairControllerPairWithCodeUrl,
  getTechPairControllerReconnectInfoMutationOptions,
  getTechPairControllerReconnectInfoUrl,
  getTechPairControllerReconnectMutationOptions,
  getTechPairControllerReconnectUrl,
  getTechPlanungControllerEinplanenMutationOptions,
  getTechPlanungControllerEinplanenUrl,
  getTechPlanungControllerVorratQueryKey,
  getTechPlanungControllerVorratQueryOptions,
  getTechPlanungControllerVorratUrl,
  getTextTemplatesControllerArchiveMutationOptions,
  getTextTemplatesControllerArchiveUrl,
  getTextTemplatesControllerCreateMutationOptions,
  getTextTemplatesControllerCreateUrl,
  getTextTemplatesControllerListQueryKey,
  getTextTemplatesControllerListQueryOptions,
  getTextTemplatesControllerListUrl,
  getTextTemplatesControllerUpdateMutationOptions,
  getTextTemplatesControllerUpdateUrl,
  getTicketsControllerCreateMutationOptions,
  getTicketsControllerCreateUrl,
  getTicketsControllerDetailQueryKey,
  getTicketsControllerDetailQueryOptions,
  getTicketsControllerDetailUrl,
  getTicketsControllerFileQueryKey,
  getTicketsControllerFileQueryOptions,
  getTicketsControllerFileUrl,
  getTicketsControllerListQueryKey,
  getTicketsControllerListQueryOptions,
  getTicketsControllerListUrl,
  getTicketsControllerReplyMutationOptions,
  getTicketsControllerReplyUrl,
  getTicketsControllerUnreadQueryKey,
  getTicketsControllerUnreadQueryOptions,
  getTicketsControllerUnreadUrl,
  getTicketsControllerUpdateMutationOptions,
  getTicketsControllerUpdateUrl,
  getUsersControllerCreateMutationOptions,
  getUsersControllerCreateUrl,
  getUsersControllerListQueryKey,
  getUsersControllerListQueryOptions,
  getUsersControllerListUrl,
  getUsersControllerUpdateMutationOptions,
  getUsersControllerUpdateUrl,
  getUsersControllerUpsertWorkScheduleMutationOptions,
  getUsersControllerUpsertWorkScheduleUrl,
  getWorkReportControllerReleaseMutationOptions,
  getWorkReportControllerReleaseUrl,
  healthControllerCheck,
  invoiceTitle,
  kalenderControllerBeenden,
  kalenderControllerErzeugen,
  kalenderControllerFeed,
  kalenderControllerStatus,
  legacyArchiveControllerArchive,
  legacyArchiveControllerStartwerteApply,
  legacyArchiveControllerStartwerteProposal,
  lvImportControllerCommit,
  lvImportControllerDryRun,
  maintenanceContractsControllerCreate,
  maintenanceContractsControllerGet,
  maintenanceContractsControllerList,
  maintenanceContractsControllerUpdate,
  masterDataDeleteControllerArticle,
  masterDataDeleteControllerBusinessArea,
  masterDataDeleteControllerContact,
  masterDataDeleteControllerCustomer,
  masterDataDeleteControllerPriceList,
  masterDataDeleteControllerProperty,
  masterDataDeleteControllerUnit,
  materialControllerAddAttachment,
  materialControllerAppReceipts,
  materialControllerCreate,
  materialControllerDeleteAttachment,
  materialControllerDownload,
  materialControllerMarkDelivered,
  materialControllerOverview,
  materialControllerRemove,
  measurementControllerAddItemAttachment,
  measurementControllerCreateItem,
  measurementControllerCreateRow,
  measurementControllerCreateSheet,
  measurementControllerDeleteItem,
  measurementControllerDeleteRow,
  measurementControllerDeleteSheet,
  measurementControllerGetSheet,
  measurementControllerItemAttachmentFile,
  measurementControllerItemsFromWorkReport,
  measurementControllerListItems,
  measurementControllerListSheets,
  measurementControllerRemoveItemAttachment,
  measurementControllerReorderItems,
  measurementControllerSheetPdf,
  measurementControllerTransfer,
  measurementControllerUpdateItem,
  measurementControllerUpdateItemAttachment,
  measurementControllerUpdateRow,
  measurementControllerUpdateSheet,
  ordersControllerAddAttachment,
  ordersControllerAppDaten,
  ordersControllerAttachmentFile,
  ordersControllerCreate,
  ordersControllerDetail,
  ordersControllerFromQuote,
  ordersControllerLeerwohnung,
  ordersControllerLeerwohnungLoeschen,
  ordersControllerList,
  ordersControllerLoeschVorschau,
  ordersControllerLoeschen,
  ordersControllerNachtrag,
  ordersControllerNavCounters,
  ordersControllerPreviewHoldMail,
  ordersControllerRemove,
  ordersControllerRemoveAttachment,
  ordersControllerResolveHold,
  ordersControllerSendHoldMail,
  ordersControllerSetHold,
  ordersControllerStorno,
  ordersControllerUpdate,
  ordersControllerUpdateAttachment,
  ordersControllerUpload,
  ordersControllerVorgang,
  paymentsControllerCreate,
  paymentsControllerList,
  paymentsControllerOpenItems,
  paymentsControllerOpenItemsCsv,
  paymentsControllerRemove,
  plantafelControllerComplete,
  plantafelControllerCreate,
  plantafelControllerCreateEntries,
  plantafelControllerDeleteEntry,
  plantafelControllerEntryTitles,
  plantafelControllerExtend,
  plantafelControllerMove,
  plantafelControllerRevoke,
  plantafelControllerUpdateEntry,
  plantafelControllerWeek,
  portalOrderControllerBauzeitenplan,
  portalOrderControllerCommit,
  portalOrderControllerPreview,
  portalPdfControllerFuellen,
  portalPdfControllerVorschlag,
  priceListsControllerCreate,
  priceListsControllerCreateItem,
  priceListsControllerDeleteItem,
  priceListsControllerDetail,
  priceListsControllerImportCsv,
  priceListsControllerList,
  priceListsControllerUpdate,
  priceListsControllerUpdateItem,
  publicSignatureControllerPage,
  publicSignatureControllerPdf,
  publicSignatureControllerSubmit,
  recurringControllerCreate,
  recurringControllerList,
  recurringControllerRemove,
  recurringControllerRun,
  recurringControllerUpdate,
  resolvePlaceholders,
  resolvePlaceholdersSingleLine,
  rolesControllerCreate,
  rolesControllerList,
  rolesControllerRemove,
  rolesControllerUpdate,
  routingControllerCustomerDistance,
  routingControllerDistance,
  routingControllerDocumentDistance,
  routingControllerEntfernungslaufStatus,
  routingControllerPlzVorschlag,
  routingControllerStartEntfernungslauf,
  searchControllerRun,
  settingsControllerGet,
  settingsControllerRemoveLogo,
  settingsControllerSetNextNumbers,
  settingsControllerUpdate,
  settingsControllerUploadLogo,
  settingsLogoControllerLogo,
  signatureLinkControllerList,
  signatureLinkControllerObligation,
  signatureLinkControllerRequestSignature,
  signatureLinkControllerRevoke,
  techControllerAbsenceRequests,
  techControllerAttendanceStatus,
  techControllerAuftragsAnhaenge,
  techControllerAuftragsAnhang,
  techControllerAuftragsFoto,
  techControllerAuftragsFotos,
  techControllerAuftragsLeerwohnung,
  techControllerCancelAbsenceRequest,
  techControllerCatalog,
  techControllerCreateAbsenceRequest,
  techControllerCreateOrder,
  techControllerCustomers,
  techControllerDayStamps,
  techControllerEditAbsenceRequest,
  techControllerLogs,
  techControllerOffeneBestellungen,
  techControllerPushEvents,
  techControllerSearchRun,
  techControllerSyncPull,
  techControllerTeamAuftrag,
  techControllerTeamFoto,
  techControllerTeamKollegen,
  techControllerTeamWoche,
  techControllerTimeSummary,
  techControllerUpload,
  techControllerWareneingang,
  techPairControllerPair,
  techPairControllerPairWithCode,
  techPairControllerReconnect,
  techPairControllerReconnectInfo,
  techPlanungControllerEinplanen,
  techPlanungControllerVorrat,
  textTemplatesControllerArchive,
  textTemplatesControllerCreate,
  textTemplatesControllerList,
  textTemplatesControllerUpdate,
  ticketsControllerCreate,
  ticketsControllerDetail,
  ticketsControllerFile,
  ticketsControllerList,
  ticketsControllerReply,
  ticketsControllerUnread,
  ticketsControllerUpdate,
  useAbsenceRequestControllerCorrect,
  useAbsenceRequestControllerDecide,
  useAbsenceRequestControllerList,
  useActivityControllerList,
  useAnalyticsControllerEmergency,
  useAnalyticsControllerEmergencyCsv,
  useAnalyticsControllerHours,
  useAnalyticsControllerHoursCsv,
  useAnalyticsControllerOpenItems,
  useAnalyticsControllerOpenItemsCsv,
  useAnalyticsControllerOrderHours,
  useAnalyticsControllerOrderHoursCsv,
  useAnalyticsControllerQuotes,
  useAnalyticsControllerQuotesCsv,
  useAnalyticsControllerRevenue,
  useAnalyticsControllerRevenueCsv,
  useAnalyticsControllerUnbilled,
  useAnalyticsControllerUnbilledCsv,
  useAppReleaseControllerVersion,
  useArticlesControllerCreate,
  useArticlesControllerList,
  useArticlesControllerUpdate,
  useAttendanceControllerClock,
  useAttendanceControllerCloseMonth,
  useAttendanceControllerCorrect,
  useAttendanceControllerCreateAbsences,
  useAttendanceControllerCreateAbsencesBulk,
  useAttendanceControllerCreateTimeAccountEntry,
  useAttendanceControllerDayDetail,
  useAttendanceControllerDeleteAbsence,
  useAttendanceControllerListAbsences,
  useAttendanceControllerListTimeAccountEntries,
  useAttendanceControllerLohnCsv,
  useAttendanceControllerLohnStatus,
  useAttendanceControllerMonth,
  useAttendanceControllerMonthClose,
  useAttendanceControllerNow,
  useAttendanceControllerOrderWork,
  useAttendanceControllerOrderWorkStatus,
  useAttendanceControllerReopenMonth,
  useAttendanceControllerSendLohnExport,
  useAttendanceControllerStatus,
  useAuthControllerChangePassword,
  useAuthControllerDisableTotp,
  useAuthControllerEnableTotp,
  useAuthControllerLogin,
  useAuthControllerLogout,
  useAuthControllerMe,
  useAuthControllerSetupTotp,
  useAuthControllerUpdateMe,
  useBankImportControllerEinlesen,
  useBankImportControllerIgnorieren,
  useBankImportControllerListe,
  useBankImportControllerPruefen,
  useBankImportControllerRechnungen,
  useBankImportControllerVorschlaege,
  useBankImportControllerZuordnen,
  useBankImportControllerZuruecksetzen,
  useBillingControllerBill,
  useBillingControllerSetTravelBilling,
  useBriefingControllerPreview,
  useBriefingControllerSendTest,
  useBusinessAreasControllerCreate,
  useBusinessAreasControllerList,
  useBusinessAreasControllerUpdate,
  useCollectiveControllerBillableOrders,
  useCollectiveControllerCreate,
  useCustomersControllerCreate,
  useCustomersControllerCreateContact,
  useCustomersControllerCreateFromUnit,
  useCustomersControllerCreatePropertiesBulk,
  useCustomersControllerCreateProperty,
  useCustomersControllerCreateUnit,
  useCustomersControllerFile,
  useCustomersControllerList,
  useCustomersControllerUnits,
  useCustomersControllerUpdate,
  useCustomersControllerUpdateContact,
  useCustomersControllerUpdateProperty,
  useCustomersControllerUpdateUnit,
  useDashboardControllerHausmeisterWarteliste,
  useDashboardControllerSummary,
  useDatanormControllerCommit,
  useDatanormControllerDryRun,
  useDatanormControllerErrorsCsv,
  useDatanormControllerSuppliers,
  useDatevControllerAssignDebtors,
  useDatevControllerDownload,
  useDatevControllerExport,
  useDatevControllerPreview,
  useDatevControllerRuns,
  useDevicesControllerCleanup,
  useDevicesControllerCreatePairingCode,
  useDevicesControllerDownloadLog,
  useDevicesControllerList,
  useDevicesControllerRevoke,
  useDocumentsControllerAddAttachment,
  useDocumentsControllerCancel,
  useDocumentsControllerCorrect,
  useDocumentsControllerCreate,
  useDocumentsControllerDatevUpload,
  useDocumentsControllerDerive,
  useDocumentsControllerDetail,
  useDocumentsControllerDuplicate,
  useDocumentsControllerEmails,
  useDocumentsControllerFinalize,
  useDocumentsControllerList,
  useDocumentsControllerMailDraft,
  useDocumentsControllerMarkSubmitted,
  useDocumentsControllerMergeWorkReports,
  useDocumentsControllerPdf,
  useDocumentsControllerPreviewHtml,
  useDocumentsControllerPreviewPages,
  useDocumentsControllerPreviewPdf,
  useDocumentsControllerRemove,
  useDocumentsControllerRemoveAttachment,
  useDocumentsControllerReopen,
  useDocumentsControllerSend,
  useDocumentsControllerUpdate,
  useDocumentsControllerUpdateAttachment,
  useDocumentsControllerWrFoto,
  useDocumentsControllerWrFotoBild,
  useDocumentsControllerXml,
  useDunningControllerList,
  useDunningControllerPropose,
  useDunningControllerRun,
  useFollowupControllerBoard,
  useFollowupControllerFollowUp,
  useFollowupControllerLost,
  useFollowupControllerWon,
  useHealthControllerCheck,
  useKalenderControllerBeenden,
  useKalenderControllerErzeugen,
  useKalenderControllerFeed,
  useKalenderControllerStatus,
  useLegacyArchiveControllerArchive,
  useLegacyArchiveControllerStartwerteApply,
  useLegacyArchiveControllerStartwerteProposal,
  useLvImportControllerCommit,
  useLvImportControllerDryRun,
  useMaintenanceContractsControllerCreate,
  useMaintenanceContractsControllerGet,
  useMaintenanceContractsControllerList,
  useMaintenanceContractsControllerUpdate,
  useMasterDataDeleteControllerArticle,
  useMasterDataDeleteControllerBusinessArea,
  useMasterDataDeleteControllerContact,
  useMasterDataDeleteControllerCustomer,
  useMasterDataDeleteControllerPriceList,
  useMasterDataDeleteControllerProperty,
  useMasterDataDeleteControllerUnit,
  useMaterialControllerAddAttachment,
  useMaterialControllerAppReceipts,
  useMaterialControllerCreate,
  useMaterialControllerDeleteAttachment,
  useMaterialControllerDownload,
  useMaterialControllerMarkDelivered,
  useMaterialControllerOverview,
  useMaterialControllerRemove,
  useMeasurementControllerAddItemAttachment,
  useMeasurementControllerCreateItem,
  useMeasurementControllerCreateRow,
  useMeasurementControllerCreateSheet,
  useMeasurementControllerDeleteItem,
  useMeasurementControllerDeleteRow,
  useMeasurementControllerDeleteSheet,
  useMeasurementControllerGetSheet,
  useMeasurementControllerItemAttachmentFile,
  useMeasurementControllerItemsFromWorkReport,
  useMeasurementControllerListItems,
  useMeasurementControllerListSheets,
  useMeasurementControllerRemoveItemAttachment,
  useMeasurementControllerReorderItems,
  useMeasurementControllerSheetPdf,
  useMeasurementControllerTransfer,
  useMeasurementControllerUpdateItem,
  useMeasurementControllerUpdateItemAttachment,
  useMeasurementControllerUpdateRow,
  useMeasurementControllerUpdateSheet,
  useOrdersControllerAddAttachment,
  useOrdersControllerAppDaten,
  useOrdersControllerAttachmentFile,
  useOrdersControllerCreate,
  useOrdersControllerDetail,
  useOrdersControllerFromQuote,
  useOrdersControllerLeerwohnung,
  useOrdersControllerLeerwohnungLoeschen,
  useOrdersControllerList,
  useOrdersControllerLoeschVorschau,
  useOrdersControllerLoeschen,
  useOrdersControllerNachtrag,
  useOrdersControllerNavCounters,
  useOrdersControllerPreviewHoldMail,
  useOrdersControllerRemove,
  useOrdersControllerRemoveAttachment,
  useOrdersControllerResolveHold,
  useOrdersControllerSendHoldMail,
  useOrdersControllerSetHold,
  useOrdersControllerStorno,
  useOrdersControllerUpdate,
  useOrdersControllerUpdateAttachment,
  useOrdersControllerUpload,
  useOrdersControllerVorgang,
  usePaymentsControllerCreate,
  usePaymentsControllerList,
  usePaymentsControllerOpenItems,
  usePaymentsControllerOpenItemsCsv,
  usePaymentsControllerRemove,
  usePlantafelControllerComplete,
  usePlantafelControllerCreate,
  usePlantafelControllerCreateEntries,
  usePlantafelControllerDeleteEntry,
  usePlantafelControllerEntryTitles,
  usePlantafelControllerExtend,
  usePlantafelControllerMove,
  usePlantafelControllerRevoke,
  usePlantafelControllerUpdateEntry,
  usePlantafelControllerWeek,
  usePortalOrderControllerBauzeitenplan,
  usePortalOrderControllerCommit,
  usePortalOrderControllerPreview,
  usePortalPdfControllerFuellen,
  usePortalPdfControllerVorschlag,
  usePriceListsControllerCreate,
  usePriceListsControllerCreateItem,
  usePriceListsControllerDeleteItem,
  usePriceListsControllerDetail,
  usePriceListsControllerImportCsv,
  usePriceListsControllerList,
  usePriceListsControllerUpdate,
  usePriceListsControllerUpdateItem,
  usePublicSignatureControllerPage,
  usePublicSignatureControllerPdf,
  usePublicSignatureControllerSubmit,
  useRecurringControllerCreate,
  useRecurringControllerList,
  useRecurringControllerRemove,
  useRecurringControllerRun,
  useRecurringControllerUpdate,
  useRolesControllerCreate,
  useRolesControllerList,
  useRolesControllerRemove,
  useRolesControllerUpdate,
  useRoutingControllerCustomerDistance,
  useRoutingControllerDistance,
  useRoutingControllerDocumentDistance,
  useRoutingControllerEntfernungslaufStatus,
  useRoutingControllerPlzVorschlag,
  useRoutingControllerStartEntfernungslauf,
  useSearchControllerRun,
  useSettingsControllerGet,
  useSettingsControllerRemoveLogo,
  useSettingsControllerSetNextNumbers,
  useSettingsControllerUpdate,
  useSettingsControllerUploadLogo,
  useSettingsLogoControllerLogo,
  useSignatureLinkControllerList,
  useSignatureLinkControllerObligation,
  useSignatureLinkControllerRequestSignature,
  useSignatureLinkControllerRevoke,
  useTechControllerAbsenceRequests,
  useTechControllerAttendanceStatus,
  useTechControllerAuftragsAnhaenge,
  useTechControllerAuftragsAnhang,
  useTechControllerAuftragsFoto,
  useTechControllerAuftragsFotos,
  useTechControllerAuftragsLeerwohnung,
  useTechControllerCancelAbsenceRequest,
  useTechControllerCatalog,
  useTechControllerCreateAbsenceRequest,
  useTechControllerCreateOrder,
  useTechControllerCustomers,
  useTechControllerDayStamps,
  useTechControllerEditAbsenceRequest,
  useTechControllerLogs,
  useTechControllerOffeneBestellungen,
  useTechControllerPushEvents,
  useTechControllerSearchRun,
  useTechControllerSyncPull,
  useTechControllerTeamAuftrag,
  useTechControllerTeamFoto,
  useTechControllerTeamKollegen,
  useTechControllerTeamWoche,
  useTechControllerTimeSummary,
  useTechControllerUpload,
  useTechControllerWareneingang,
  useTechPairControllerPair,
  useTechPairControllerPairWithCode,
  useTechPairControllerReconnect,
  useTechPairControllerReconnectInfo,
  useTechPlanungControllerEinplanen,
  useTechPlanungControllerVorrat,
  useTextTemplatesControllerArchive,
  useTextTemplatesControllerCreate,
  useTextTemplatesControllerList,
  useTextTemplatesControllerUpdate,
  useTicketsControllerCreate,
  useTicketsControllerDetail,
  useTicketsControllerFile,
  useTicketsControllerList,
  useTicketsControllerReply,
  useTicketsControllerUnread,
  useTicketsControllerUpdate,
  useUsersControllerCreate,
  useUsersControllerList,
  useUsersControllerUpdate,
  useUsersControllerUpsertWorkSchedule,
  useWorkReportControllerRelease,
  usersControllerCreate,
  usersControllerList,
  usersControllerUpdate,
  usersControllerUpsertWorkSchedule,
  workReportControllerRelease
};
//# sourceMappingURL=index.js.map